import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Key, Save, RefreshCw, Loader2,
  XCircle, TrendingUp, Zap, FileText, Download,
  ChevronDown, ChevronRight, AlertTriangle, Send,
  Image, Tag, Radio, Trash2, Plus, Eye, Store, Edit2,
  Copy, Search, Monitor, Globe, ExternalLink, CheckCircle, Clock, Ban,
  ShoppingCart, Link2, Unplug, Sparkles, Settings,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { parseApiResponse, normalizeAppError } from '../../utils/apiErrors';
import { API } from '../../config/apiBase';
import { useFeatures } from '../../context/FeaturesContext';
import WhatsAppManager from './WhatsAppManager';


const GROUP_ICONS = {
  'Telegram': 'T',
  'Facebook Login': 'f',
  'Google Login': 'G',
  'Belvo / Open Finance': 'B',
  'Email / SMTP': '@',
  'Stripe / Pagamentos': '$',
};

// Error boundary to prevent white screen
class MasterErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, errorInfo: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, errorInfo) {
    console.error('[AdminMaster] ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center py-20" data-testid="master-error">
          <div className="text-center max-w-md">
            <AlertTriangle size={32} style={{ color: 'var(--status-amber)', margin: '0 auto 8px' }} />
            <p className="text-sm" style={{ color: 'var(--muted-2)' }}>Erro ao carregar Admin Master</p>
            <p className="text-xs mt-2" style={{ color: 'var(--muted)' }}>{String(this.state.error?.message || '').slice(0, 200)}</p>
            <button onClick={() => window.location.reload()} className="mt-3 px-3 py-1.5 rounded text-xs" style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }}>
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function MaskedField({ config, configKey, token, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/master/integrations/${configKey}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
      if (res.ok) {
        toast.success(`${config.label} atualizado`);
        setEditing(false);
        setValue('');
        onSaved();
      } else {
        toast.error('Erro ao salvar');
      }
    } catch(e) { toast.error('Erro de conexao'); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/admin/master/integrations/${configKey}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      setTestResult(data);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch(e) { toast.error('Erro no teste'); }
    setTesting(false);
  };

  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }} data-testid={`config-field-${configKey}`}>
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="text-xs text-gray-500 mb-0.5 truncate">{config.label}</div>
        {editing ? (
          <input
            type="text"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="Novo valor..."
            className="w-full text-sm py-1 px-2 rounded"
            style={{ background: 'var(--surface)', border: '1px solid var(--border-hover)', color: 'var(--text)' }}
            autoFocus
            data-testid={`config-input-${configKey}`}
          />
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <code className="text-sm truncate block max-w-full" style={{ color: config.has_value ? 'var(--sv-gold-primary)' : 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {config.has_value ? config.masked_value : '(vazio)'}
            </code>
            <span className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0" style={{
              background: config.source === 'database' ? 'var(--status-success-bg)' : config.source === 'env' ? 'var(--status-info-bg)' : 'var(--border)',
              color: config.source === 'database' ? '#10B981' : config.source === 'env' ? '#3B82F6' : 'var(--muted)',
            }}>
              {config.source}
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {editing ? (
          <>
            <button onClick={handleSave} disabled={saving || !value} className="p-1.5 rounded" style={{ background: 'var(--status-success-bg)', color: 'var(--status-green)' }} data-testid={`config-save-${configKey}`}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            </button>
            <button onClick={() => { setEditing(false); setValue(''); }} className="p-1.5 rounded" style={{ background: 'var(--status-error-bg)', color: 'var(--status-red)' }}>
              <XCircle size={14} />
            </button>
          </>
        ) : (
          <button onClick={() => setEditing(true)} className="p-1.5 rounded hover:bg-[rgba(128,128,128,0.08)]" style={{ color: 'var(--sv-gold-primary)' }} data-testid={`config-edit-${configKey}`}>
            <Key size={14} />
          </button>
        )}
        {config.testable && !editing && (
          <button onClick={handleTest} disabled={testing} className="p-1.5 rounded hover:bg-[rgba(128,128,128,0.08)]" style={{ color: testResult?.ok ? '#10B981' : testResult ? '#EF4444' : 'var(--muted-2)' }} data-testid={`config-test-${configKey}`}>
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}

// ============= FEATURES (toggles dinamicos do site publico) =============
const FEATURE_DEFS = [
  {
    key: 'show_plans',
    label: 'Pagina de Planos',
    description: 'Exibe a aba "Planos" no menu, o banner "7 dias gratis" e a pagina /planos no site publico.',
  },
];

function FeaturesManager({ token }) {
  const { features, refresh } = useFeatures();
  const [busyKey, setBusyKey] = useState(null);

  const toggle = async (key, current) => {
    setBusyKey(key);
    try {
      const r = await fetch(`${API}/admin/master/features`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: !current }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(`Erro: ${err.detail || r.statusText}`);
        return;
      }
      await refresh();
      toast.success(`${!current ? 'Ativada' : 'Desativada'} com sucesso`);
    } catch (e) {
      toast.error(`Erro: ${e.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="features-panel">
      <p className="text-xs" style={{ color: 'var(--muted-2)' }}>
        Ative ou desative recursos no site publico sem mexer em codigo. Mudancas valem em segundos.
      </p>
      {FEATURE_DEFS.map((f) => {
        const value = !!features[f.key];
        const busy = busyKey === f.key;
        return (
          <div
            key={f.key}
            className="rounded-lg p-4 flex items-start justify-between gap-4"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
            data-testid={`feature-row-${f.key}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{f.label}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{f.description}</p>
              <p className="text-[11px] mt-2" style={{ color: value ? 'var(--status-green, #34d399)' : 'var(--muted-2)' }}>
                Status atual: {value ? 'ATIVO' : 'INATIVO'}
              </p>
            </div>
            <button
              onClick={() => toggle(f.key, value)}
              disabled={busy}
              data-testid={`feature-toggle-${f.key}`}
              className="px-4 py-2 rounded-md text-xs font-semibold transition-all disabled:opacity-50"
              style={{
                background: value ? 'var(--status-red, #dc2626)' : 'var(--sv-gold-primary)',
                color: value ? '#fff' : '#0A0A0A',
                minWidth: 110,
              }}
            >
              {busy ? '...' : (value ? 'Desativar' : 'Ativar')}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ============= POP-UP PROMOCIONAL =============
const POPUP_FREQ_OPTIONS = [
  { value: 'once', label: 'Uma unica vez por dispositivo' },
  { value: 'session', label: 'Uma vez por sessao (recomendado)' },
  { value: 'always', label: 'Toda visita' },
];

function PromoPopupManager({ token }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    fetch(`${API}/promo-popup`).then(r => r.json()).then(setForm).catch(() => setForm({}));
  }, []);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Reusa endpoint de upload de banner (ja aceita jpg/png/webp)
      const r = await fetch(`${API}/banners/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        update('image_url', d.url);
        toast.success('Imagem enviada');
      } else {
        toast.error('Falha no upload');
      }
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/master/promo-popup`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        toast.success('Pop-up salvo');
        // limpa localStorage/sessionStorage pra o usuario ver de novo
        try {
          localStorage.removeItem('sv_promo_popup_shown');
          sessionStorage.removeItem('sv_promo_popup_session_shown');
        } catch (_) {}
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Falha ao salvar');
      }
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-sm" style={{ color: 'var(--muted)' }}>Carregando...</div>;

  const imgSrc = form.image_url?.startsWith('http') ? form.image_url : `${API.replace(/\/api\/?$/, '')}${form.image_url || ''}`;

  return (
    <div className="space-y-4" data-testid="popup-panel">
      <div className="rounded-lg p-3 space-y-1" style={{ background: 'var(--gold-bg)', border: '1px solid var(--border-hover)' }}>
        <p className="text-xs font-semibold" style={{ color: 'var(--sv-gold-primary)' }}>Como funciona</p>
        <p className="text-[11px]" style={{ color: 'var(--muted-2)' }}>
          Modal centralizado que aparece pra visitantes do site. Ativa/desativa aqui, controla frequencia
          (uma vez, por sessao, sempre) e delay em segundos. Salvar reseta o "ja vi" no seu proprio navegador.
        </p>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="text-sm flex items-center gap-2">
          <input type="checkbox" checked={!!form.enabled} onChange={(e) => update('enabled', e.target.checked)} data-testid="popup-enabled" />
          <span style={{ color: 'var(--text)' }}>Ativo no site publico</span>
        </label>
        <div className="flex gap-2">
          <button onClick={() => setPreviewOpen(true)} className="px-3 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="popup-preview">
            Ver preview
          </button>
          <button onClick={save} disabled={saving} className="px-4 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="popup-save">
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Titulo hero (esquerda, grande)</span>
          <input value={form.hero_title || ''} onChange={(e) => update('hero_title', e.target.value)} placeholder='Ex: Economize ate 3x mais no Prime Day' className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
        </label>
        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Subtitulo hero</span>
          <input value={form.hero_subtitle || ''} onChange={(e) => update('hero_subtitle', e.target.value)} placeholder='Ex: Baixe o app!' className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
        </label>

        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Titulo direita</span>
          <input value={form.title || ''} onChange={(e) => update('title', e.target.value)} placeholder='Ex: Nao perca as melhores ofertas!' className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
        </label>
        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Subtitulo direita</span>
          <input value={form.subtitle || ''} onChange={(e) => update('subtitle', e.target.value)} placeholder='Ex: No Prime Day, as melhores ofertas estao no APP!' className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
        </label>

        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Texto do botao (CTA)</span>
          <input value={form.cta_text || ''} onChange={(e) => update('cta_text', e.target.value)} placeholder='Baixar o APP' className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
        </label>
        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Link do botao</span>
          <input value={form.cta_link || ''} onChange={(e) => update('cta_link', e.target.value)} placeholder='https://...' className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace' }} />
        </label>

        <label className="text-xs space-y-1 md:col-span-2">
          <span style={{ color: 'var(--muted)' }}>Imagem (opcional, aparece na parte esquerda)</span>
          <div className="flex gap-2">
            <input value={form.image_url || ''} onChange={(e) => update('image_url', e.target.value)} placeholder="URL ou clique 'Upload'" className="flex-1 text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace' }} />
            <label className="px-3 py-2 rounded cursor-pointer text-xs font-semibold flex items-center" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
              {uploading ? 'Enviando...' : 'Upload'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} disabled={uploading} />
            </label>
          </div>
          {form.image_url && (
            <img src={imgSrc} alt="preview" className="mt-2 max-h-32 rounded" style={{ border: '1px solid var(--border)' }} />
          )}
        </label>

        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Cor fundo (painel esquerdo) - padrao azul SV</span>
          <div className="flex gap-2">
            <input type="color" value={form.bg_color || '#1756B8'} onChange={(e) => update('bg_color', e.target.value)} className="w-10 h-9 rounded cursor-pointer" style={{ border: '1px solid var(--border)' }} />
            <input value={form.bg_color || ''} onChange={(e) => update('bg_color', e.target.value)} placeholder='#1756B8 (azul SV)' className="flex-1 text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace' }} />
          </div>
          <div className="flex gap-1 mt-1">
            <button type="button" onClick={() => { update('bg_color', '#1756B8'); update('text_color', '#FFFFFF'); }} className="text-[10px] px-2 py-1 rounded" style={{ background: '#1756B8', color: '#FFFFFF' }}>Azul SV</button>
            <button type="button" onClick={() => { update('bg_color', '#7DB3FF'); update('text_color', '#031533'); }} className="text-[10px] px-2 py-1 rounded" style={{ background: '#7DB3FF', color: '#031533' }}>Azul claro</button>
            <button type="button" onClick={() => { update('bg_color', '#071A3A'); update('text_color', '#F6F8FC'); }} className="text-[10px] px-2 py-1 rounded" style={{ background: '#071A3A', color: '#F6F8FC', border: '1px solid var(--border-hover)' }}>Marinho</button>
            <button type="button" onClick={() => { update('bg_color', '#3BEA86'); update('text_color', '#03230F'); }} className="text-[10px] px-2 py-1 rounded" style={{ background: '#3BEA86', color: '#03230F' }}>Verde SV</button>
          </div>
        </label>
        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Cor do botao (CTA)</span>
          <div className="flex gap-2">
            <input type="color" value={form.cta_bg || '#3BEA86'} onChange={(e) => update('cta_bg', e.target.value)} className="w-10 h-9 rounded cursor-pointer" style={{ border: '1px solid var(--border)' }} />
            <input value={form.cta_bg || ''} onChange={(e) => update('cta_bg', e.target.value)} placeholder='#3BEA86 (verde SV)' className="flex-1 text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace' }} />
          </div>
          <div className="flex gap-1 mt-1">
            <button type="button" onClick={() => { update('cta_bg', '#3BEA86'); update('cta_color', '#03230F'); }} className="text-[10px] px-2 py-1 rounded" style={{ background: '#3BEA86', color: '#03230F' }}>Verde SV</button>
            <button type="button" onClick={() => { update('cta_bg', '#071A3A'); update('cta_color', '#7DB3FF'); }} className="text-[10px] px-2 py-1 rounded" style={{ background: '#071A3A', color: '#7DB3FF', border: '1px solid #7DB3FF' }}>Marinho/azul</button>
          </div>
        </label>

        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Frequencia de exibicao</span>
          <select value={form.show_frequency || 'session'} onChange={(e) => update('show_frequency', e.target.value)} className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
            {POPUP_FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="text-xs space-y-1">
          <span style={{ color: 'var(--muted)' }}>Delay antes de mostrar (segundos)</span>
          <input type="number" min="0" max="60" value={form.show_after_seconds || 3} onChange={(e) => update('show_after_seconds', parseInt(e.target.value, 10) || 0)} className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
        </label>
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <div onClick={() => setPreviewOpen(false)} className="fixed inset-0 z-[9998] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
          <div onClick={(e) => e.stopPropagation()} className="relative rounded-2xl overflow-hidden shadow-2xl w-full max-w-3xl" style={{ background: '#0a0f1a' }}>
            <button onClick={() => setPreviewOpen(false)} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)', color: '#000' }}>×</button>
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-8 md:p-10 flex flex-col justify-center" style={{ background: form.bg_color || '#0b1e4f', color: '#fff' }}>
                {form.hero_title && <h2 className="text-2xl md:text-3xl font-bold mb-2">{form.hero_title}</h2>}
                {form.hero_subtitle && <p className="text-sm opacity-90">{form.hero_subtitle}</p>}
                {form.image_url && <img src={imgSrc} alt="" className="mt-5 max-h-56 self-center object-contain" />}
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center" style={{ background: '#0a0f1a', color: '#fff' }}>
                {form.title && <h3 className="text-xl md:text-2xl font-bold mb-2">{form.title}</h3>}
                {form.subtitle && <p className="text-sm opacity-80 mb-6">{form.subtitle}</p>}
                {form.cta_text && (
                  <a href={form.cta_link || '#'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold" style={{ background: form.cta_bg || '#1a73e8', color: '#fff' }}>{form.cta_text}</a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============= BANNERS PROMOCIONAIS =============
const SLOT_OPTIONS = [
  { value: 'topbar', label: 'Faixa promocional do topo (barra fina)' },
  { value: 'hero', label: 'Topo da Home (carousel grande)' },
  { value: 'inline', label: 'Entre cards de oferta' },
  { value: 'sidebar', label: 'Sidebar desktop' },
];

const EMPTY_BANNER = { title: '', image_url: '', link: '', store_name: '', position: 'hero', bg_color: '', text_color: '', is_active: true, order: 0 };

function RecoveryManager({ token }) {
  const [info, setInfo] = useState(null);
  const [building, setBuilding] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadInfo = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/master/recovery-installer/info`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setInfo(await r.json());
    } catch (_) {}
  }, [token]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const build = async () => {
    setBuilding(true);
    try {
      const r = await fetch(`${API}/admin/master/recovery-installer/build`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) { toast.success(`Instalavel gerado (${d.size_mb} MB)`); loadInfo(); }
      else toast.error('Falha ao gerar o instalavel');
    } catch (_) { toast.error('Erro ao gerar'); }
    finally { setBuilding(false); }
  };

  const blobDownload = async (path, filename, okMsg) => {
    const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error('Falha ao baixar'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    if (okMsg) toast.success(okMsg);
  };

  const download = async () => {
    setDownloading(true);
    try {
      await blobDownload('/admin/master/recovery-installer', info?.filename || 'SuperVitrine-Recovery-Setup.exe', 'Download iniciado');
    } catch (_) { toast.error('Erro no download'); }
    finally { setDownloading(false); }
  };

  const downloadCert = async () => {
    try { await blobDownload('/admin/master/recovery-cert', 'SuperVitrine-Certificado.cer', 'Certificado baixado'); }
    catch (_) { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4" data-testid="recovery-panel">
      <div>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Instalavel de Recuperacao (cerebro local)</h3>
        <p className="text-xs mt-1" style={{ color: 'var(--muted-2)' }}>
          Instalador .exe (assistente Windows) pra reinstalar o cerebro numa maquina formatada:
          instala Python/Node/MongoDB, restaura o codigo + suas credenciais + config, e liga o
          autostart. Baixe, execute o .exe e siga o assistente (ele pede elevacao de admin).
        </p>
      </div>

      <div className="rounded-lg p-3 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--muted-2)' }}>
        {info?.exists
          ? <>Status: <b style={{ color: 'var(--text)' }}>gerado</b> · {info.size_mb} MB · atualizado em {new Date(info.built_at).toLocaleString('pt-BR')}</>
          : <>Status: <b style={{ color: 'var(--status-red)' }}>ainda nao gerado</b> — clique em "Gerar/Atualizar".</>}
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={build} disabled={building} className="px-3.5 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="recovery-build">
          {building ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Gerar/Atualizar instalavel
        </button>
        <button onClick={download} disabled={downloading || !info?.exists} className="px-3.5 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5"
          style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)', opacity: (downloading || !info?.exists) ? 0.6 : 1 }} data-testid="recovery-download">
          {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Baixar instalador (.exe)
        </button>
        <button onClick={downloadCert} className="px-3.5 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="recovery-cert">
          <Shield size={13} /> Baixar certificado
        </button>
      </div>

      <div className="rounded-lg p-3 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--muted-2)' }}>
        <b style={{ color: 'var(--text)' }}>Assinatura:</b> o .exe e assinado como "Super Vitrine". Numa maquina
        formatada, o Windows pode mostrar "Editor desconhecido" ate voce confiar no certificado. Duas opcoes:
        <ul style={{ margin: '6px 0 0 16px', listStyle: 'disc' }}>
          <li>Simples: ao abrir o .exe, clique em <b>Mais informacoes → Executar assim mesmo</b> (uma vez).</li>
          <li>Sem aviso: baixe o certificado e, na maquina nova, rode como admin:<br/>
            <code style={{ display: 'block', marginTop: 4, padding: '6px 8px', background: 'var(--surface)', borderRadius: 6, fontSize: 11, color: 'var(--text)' }}>
              certutil -addstore Root SuperVitrine-Certificado.cer<br/>
              certutil -addstore TrustedPublisher SuperVitrine-Certificado.cer
            </code>
          </li>
        </ul>
      </div>

      <div className="rounded-lg p-3 text-xs flex gap-2" style={{ background: 'rgba(214,40,40,0.08)', border: '1px solid rgba(214,40,40,0.3)', color: 'var(--muted-2)' }}>
        <Shield size={14} style={{ color: 'var(--status-red)', flexShrink: 0, marginTop: 1 }} />
        <span>Este arquivo contem TODAS as suas credenciais e segredos. Guarde-o em local seguro
        e nao compartilhe. Regenere apos trocar credenciais.</span>
      </div>
    </div>
  );
}

function BannersManager({ token }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | banner_id
  const [form, setForm] = useState({ ...EMPTY_BANNER });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/banners?position=all&include_inactive=true`);
      const d = await r.json();
      setList(Array.isArray(d) ? d : []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setForm({ ...EMPTY_BANNER });
    setEditing('new');
  };

  const startEdit = (b) => {
    setForm({ ...EMPTY_BANNER, ...b });
    setEditing(b.banner_id);
  };

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/banners/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        setForm(prev => ({ ...prev, image_url: d.url }));
        toast.success('Imagem enviada');
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Falha no upload');
      }
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (form.position === 'topbar') {
      if (!form.title && !form.image_url) {
        toast.error('Na faixa do topo, preencha o texto ou uma imagem');
        return;
      }
    } else if (!form.image_url || !form.link) {
      toast.error('Imagem e link sao obrigatorios');
      return;
    }
    setSaving(true);
    try {
      const isNew = editing === 'new';
      const url = isNew ? `${API}/banners` : `${API}/banners/${editing}`;
      const r = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        toast.success(isNew ? 'Banner criado' : 'Banner atualizado');
        setEditing(null);
        await load();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Falha ao salvar');
      }
    } finally { setSaving(false); }
  };

  const remove = async (banner_id) => {
    if (!window.confirm('Apagar este banner?')) return;
    const r = await fetch(`${API}/banners/${banner_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { toast.success('Banner removido'); await load(); }
    else toast.error('Falha ao remover');
  };

  const toggleActive = async (b) => {
    const r = await fetch(`${API}/banners/${b.banner_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ is_active: !b.is_active }),
    });
    if (r.ok) await load();
  };

  if (loading) return <div className="text-sm" style={{ color: 'var(--muted)' }}>Carregando...</div>;

  const refreshAuto = async () => {
    const r = await fetch(`${API}/banners/refresh-auto`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      const d = await r.json();
      toast.success(`${d.created} banners auto criados`);
      await load();
    } else {
      toast.error('Falha ao gerar auto');
    }
  };

  const manuals = list.filter(b => b.source !== 'auto');
  const autos = list.filter(b => b.source === 'auto');

  return (
    <div className="space-y-4" data-testid="banners-panel">
      <div className="rounded-lg p-3 space-y-1" style={{ background: 'var(--gold-bg)', border: '1px solid var(--border-hover)' }}>
        <p className="text-xs font-semibold" style={{ color: 'var(--sv-gold-primary)' }}>Como funciona</p>
        <p className="text-[11px]" style={{ color: 'var(--muted-2)' }}>
          <b>Manuais</b> - voce cadastra (banners de campanha, datas especiais, banners de programas externos).
          Esses ficam fixos ate voce alterar.
        </p>
        <p className="text-[11px]" style={{ color: 'var(--muted-2)' }}>
          <b>Automaticos</b> - gerados a cada rodada do scheduler (15min) a partir dos top deals ML com maior desconto.
          Renovam sozinhos com produtos novos. Aparecem no slot "inline" (entre cards de ofertas).
        </p>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs" style={{ color: 'var(--muted-2)' }}>
          Clique passa por ir.supervitrineofertas.com/b/{'<id>'} com afiliado injetado.
        </p>
        <div className="flex gap-2">
          <button onClick={refreshAuto} className="px-3.5 py-1.5 rounded-lg text-xs font-semibold" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="banner-refresh-auto">Gerar auto agora</button>
          <button onClick={startNew} className="px-3.5 py-1.5 rounded-lg text-xs font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="banner-add">+ Novo banner</button>
        </div>
      </div>

      {editing && (
        <div className="rounded-lg p-4 space-y-3" style={{ background: 'var(--surface)', border: '1px solid var(--sv-gold-primary)' }} data-testid="banner-form">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{editing === 'new' ? 'Novo banner' : 'Editar banner'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs space-y-1">
              <span style={{ color: 'var(--muted)' }}>Slot (onde aparece)</span>
              <select value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="banner-position">
                {SLOT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
            <label className="text-xs space-y-1">
              <span style={{ color: 'var(--muted)' }}>Ordem (menor = aparece primeiro)</span>
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })} className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
            </label>
            <label className="text-xs space-y-1 md:col-span-2">
              <span style={{ color: 'var(--muted)' }}>Titulo</span>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Black Friday Amazon" className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
            </label>
            <label className="text-xs space-y-1">
              <span style={{ color: 'var(--muted)' }}>Nome da loja</span>
              <input value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} placeholder="Amazon, Magalu..." className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
            </label>
            <label className="text-xs space-y-1">
              <span style={{ color: 'var(--muted)' }}>Link de destino</span>
              <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://amazon.com.br/..." className="w-full text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace' }} data-testid="banner-link" />
            </label>
            <label className="text-xs space-y-1 md:col-span-2">
              <span style={{ color: 'var(--muted)' }}>Imagem (URL ou upload)</span>
              <div className="flex gap-2">
                <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://... ou clique 'Upload'" className="flex-1 text-sm px-3 py-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace' }} data-testid="banner-image-url" />
                <label className="px-3 py-2 rounded cursor-pointer text-xs font-semibold flex items-center" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                  {uploading ? 'Enviando...' : 'Upload'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} disabled={uploading} />
                </label>
              </div>
              {form.image_url && (
                <img src={form.image_url.startsWith('http') ? form.image_url : `${API.replace(/\/api\/?$/, '')}${form.image_url}`} alt="preview" className="mt-2 max-h-32 rounded" style={{ border: '1px solid var(--border)' }} />
              )}
            </label>
            {form.position === 'topbar' && (
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg p-3" style={{ background: 'var(--surface-2)', border: '1px dashed var(--border)' }}>
                <p className="sm:col-span-2 text-[11px]" style={{ color: 'var(--muted-2)' }}>
                  Faixa fina do topo do site. Use o <b>Titulo</b> (texto) ou uma <b>Imagem</b>, e o <b>Link de destino</b> (clicavel). Deixe as cores vazias para usar o dourado padrao do tema. Desmarque "Ativo" para sumir com a faixa.
                </p>
                <label className="text-xs space-y-1">
                  <span style={{ color: 'var(--muted)' }}>Cor de fundo</span>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={form.bg_color || '#1756B8'} onChange={(e) => setForm({ ...form, bg_color: e.target.value })} className="h-9 w-12 rounded cursor-pointer" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="topbar-bg-color" />
                    <input value={form.bg_color || ''} onChange={(e) => setForm({ ...form, bg_color: e.target.value })} placeholder="#1756B8 ou vazio" className="flex-1 text-sm px-3 py-2 rounded" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace' }} />
                    {form.bg_color && <button type="button" onClick={() => setForm({ ...form, bg_color: '' })} className="text-[10px] px-2 py-1 rounded" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--muted)' }}>limpar</button>}
                  </div>
                </label>
                <label className="text-xs space-y-1">
                  <span style={{ color: 'var(--muted)' }}>Cor do texto</span>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={form.text_color || '#0A0A0A'} onChange={(e) => setForm({ ...form, text_color: e.target.value })} className="h-9 w-12 rounded cursor-pointer" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="topbar-text-color" />
                    <input value={form.text_color || ''} onChange={(e) => setForm({ ...form, text_color: e.target.value })} placeholder="#0A0A0A ou vazio" className="flex-1 text-sm px-3 py-2 rounded" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace' }} />
                    {form.text_color && <button type="button" onClick={() => setForm({ ...form, text_color: '' })} className="text-[10px] px-2 py-1 rounded" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--muted)' }}>limpar</button>}
                  </div>
                </label>
                <div className="sm:col-span-2">
                  <span className="text-[11px]" style={{ color: 'var(--muted)' }}>Previa:</span>
                  <div className="mt-1 rounded overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                    <div className="py-1.5 text-center text-xs font-semibold" style={{ background: form.bg_color || 'linear-gradient(90deg, var(--gold-3) 0%, var(--gold-2) 50%, var(--gold-3) 100%)', color: form.text_color || 'var(--sv-on-accent)' }}>
                      {form.image_url ? '🖼️ (imagem definida)' : (form.title || 'Texto da faixa aparece aqui')}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <label className="text-xs flex items-center gap-2 md:col-span-2">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span style={{ color: 'var(--text)' }}>Ativo (visivel no site)</span>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded text-xs" style={{ background: 'var(--surface-2)', color: 'var(--text)' }}>Cancelar</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded text-xs font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="banner-save">{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </div>
      )}

      {/* Manuais */}
      <div>
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text)' }}>Manuais ({manuals.length})</p>
        <div className="space-y-2">
          {manuals.length === 0 ? (
            <div className="text-center py-6 text-xs rounded-lg" style={{ color: 'var(--muted)', background: 'var(--surface)', border: '1px dashed var(--border)' }}>Nenhum banner manual cadastrado.</div>
          ) : manuals.map(b => (
            <BannerRow key={b.banner_id} b={b} onToggle={() => toggleActive(b)} onEdit={() => startEdit(b)} onRemove={() => remove(b.banner_id)} canEdit />
          ))}
        </div>
      </div>

      {/* Automaticos */}
      <div>
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text)' }}>Automaticos ({autos.length}) <span className="font-normal" style={{ color: 'var(--muted-2)' }}>- gerados pelo sistema, sao substituidos a cada rodada</span></p>
        <div className="space-y-2">
          {autos.length === 0 ? (
            <div className="text-center py-6 text-xs rounded-lg" style={{ color: 'var(--muted)', background: 'var(--surface)', border: '1px dashed var(--border)' }}>
              Nenhum banner auto ainda. Clique em "Gerar auto agora" ou aguarde a proxima rodada do scheduler.
            </div>
          ) : autos.map(b => (
            <BannerRow key={b.banner_id} b={b} onToggle={() => toggleActive(b)} onRemove={() => remove(b.banner_id)} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Miniatura do banner na lista do admin.
 *
 * Banner sem imagem é normal: a faixa do topo (position "topbar") é só texto e
 * vem com image_url vazio. Antes a tela montava a URL mesmo assim, pedia a raiz
 * do site como imagem e o navegador desenhava o ícone de imagem quebrada.
 * Agora: faixa sem imagem mostra uma prévia com o texto; outro banner sem
 * imagem, ou imagem que falha ao carregar, mostra um quadro neutro com legenda.
 */
export function urlMiniaturaBanner(imageUrl) {
  const url = typeof imageUrl === 'string' ? imageUrl.trim() : '';
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (!url.startsWith('/')) return null;
  return `${API.replace(/\/api\/?$/, '')}${url}`;
}

function MiniaturaBanner({ b }) {
  const [falhou, setFalhou] = useState(false);
  const src = urlMiniaturaBanner(b.image_url);
  const caixa = 'w-24 h-16 rounded shrink-0 overflow-hidden flex items-center justify-center text-center';
  if (!src && b.position === 'topbar') {
    return (
      <div className={caixa} data-testid={`banner-thumb-texto-${b.banner_id}`}
        style={{ background: b.bg_color || 'linear-gradient(90deg, var(--gold-3) 0%, var(--gold-2) 50%, var(--gold-3) 100%)', color: b.text_color || 'var(--sv-on-accent)' }}>
        <span className="text-[8px] font-semibold leading-tight px-1.5 line-clamp-3">{b.title || 'Faixa do topo'}</span>
      </div>
    );
  }
  if (!src || falhou) {
    return (
      <div className={`${caixa} flex-col gap-0.5`} data-testid={`banner-thumb-vazio-${b.banner_id}`}
        style={{ background: 'var(--surface-2)', border: '1px dashed var(--border-hover)', color: 'var(--muted-2)' }}>
        <Image size={16} aria-hidden="true" />
        <span className="text-[9px]">{src ? 'imagem indisponível' : 'sem imagem'}</span>
      </div>
    );
  }
  return <img src={src} alt="" className="w-24 h-16 object-cover rounded shrink-0" style={{ background: 'var(--surface-2)' }} onError={() => setFalhou(true)} />;
}

function BannerRow({ b, onToggle, onEdit, onRemove, canEdit }) {
  return (
    <div className="rounded-lg p-3 flex items-center gap-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`banner-row-${b.banner_id}`}>
      <MiniaturaBanner b={b} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{b.title || '(sem titulo)'}</p>
        <p className="text-[11px]" style={{ color: 'var(--muted-2)' }}>
          <span className="px-1.5 py-0.5 rounded mr-1" style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)' }}>{b.position}</span>
          {b.source === 'auto' && <span className="px-1.5 py-0.5 rounded mr-1" style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399' }}>AUTO</span>}
          {b.store_name && <span className="mr-1">{b.store_name}</span>}
          {b.discount_percent ? <span className="mr-1">-{b.discount_percent}%</span> : null}
          <span>{b.clicks || 0} cliques</span>
        </p>
      </div>
      <button onClick={onToggle} className="px-3 py-1 rounded text-xs font-semibold" style={{ background: b.is_active ? 'rgba(52,211,153,0.15)' : 'var(--surface-2)', color: b.is_active ? '#34d399' : 'var(--muted)' }}>{b.is_active ? 'Ativo' : 'Inativo'}</button>
      {canEdit && <button onClick={onEdit} className="px-3 py-1 rounded text-xs" style={{ background: 'var(--surface-2)', color: 'var(--text)' }}>Editar</button>}
      <button onClick={onRemove} className="px-3 py-1 rounded text-xs" style={{ background: 'rgba(220,38,38,0.15)', color: '#dc2626' }}>Apagar</button>
    </div>
  );
}

// ============= AFILIADOS (tags por marketplace) =============
const AFFILIATE_FIELDS = [
  { key: 'amazon', label: 'Amazon Associates', sub: 'tag', placeholder: 'seuid-20', help: 'Sua tag de afiliado Amazon (termina geralmente em -20).' },
  { key: 'aliexpress', label: 'AliExpress Portals', sub: 'tracking_id', placeholder: 'mm_xxxxxxxx', help: 'aff_short_key gerado no portal de afiliados AliExpress.' },
  { key: 'magalu', label: 'Magalu Voce', sub: 'store_slug', placeholder: 'magazinedouglaspaco', help: 'Slug da sua loja Magalu Voce (com prefixo "magazine").' },
  { key: 'shopee', label: 'Shopee Affiliate', sub: 'tracking_id', placeholder: 'seu_id_shopee', help: 'Site ID / Tracking ID do Shopee Affiliate.' },
];

function AffiliatesManager({ token }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/master/affiliates`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        setCfg(d);
        setForm(d);
      }
    } catch (_) {}
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const update = (key, sub, value) => {
    setForm(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [sub]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/master/affiliates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(`Erro: ${err.detail || r.statusText}`);
        return;
      }
      const d = await r.json();
      setCfg(d.affiliates);
      toast.success('Tags de afiliado salvas');
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-sm" style={{ color: 'var(--muted)' }}>Carregando...</div>;

  return (
    <div className="space-y-3" data-testid="affiliates-panel">
      <p className="text-xs" style={{ color: 'var(--muted-2)' }}>
        Cada link no Super Vitrine vai passar pelo encurtador ir.supervitrineofertas.com
        e ser convertido automaticamente pra link de afiliado da loja correspondente.
      </p>
      {AFFILIATE_FIELDS.map(f => {
        const value = (form[f.key] && form[f.key][f.sub]) || '';
        return (
          <div key={f.key} className="rounded-lg p-4 space-y-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`affiliate-row-${f.key}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{f.label}</p>
              <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: value ? 'rgba(52,211,153,0.15)' : 'var(--gold-bg)', color: value ? '#34d399' : 'var(--muted-2)' }}>
                {value ? 'configurado' : 'pendente'}
              </span>
            </div>
            <input
              type="text"
              value={value}
              onChange={(e) => update(f.key, f.sub, e.target.value)}
              placeholder={f.placeholder}
              data-testid={`affiliate-input-${f.key}`}
              className="w-full text-sm px-3 py-2 rounded"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'monospace' }}
            />
            <p className="text-[11px]" style={{ color: 'var(--muted-2)' }}>{f.help}</p>
          </div>
        );
      })}
      <button
        onClick={save}
        disabled={saving}
        data-testid="affiliates-save"
        className="px-6 py-2 rounded-md text-xs font-semibold disabled:opacity-50"
        style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
      >
        {saving ? 'Salvando...' : 'Salvar afiliados'}
      </button>
    </div>
  );
}

function AppDownloadManager({ appConfig, token, onSaved, savingApp, setSavingApp }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (appConfig) setForm({ ...appConfig });
  }, [appConfig]);

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSavingApp(true);
    try {
      const res = await fetch(`${API}/admin/master/app-download`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success('Configuracoes do app salvas');
        onSaved();
      } else { toast.error('Erro ao salvar'); }
    } catch(e) { toast.error('Erro de conexao'); }
    setSavingApp(false);
  };

  const ToggleSwitch = ({ checked, onChange, testId }) => (
    <button
      onClick={() => onChange(!checked)}
      className="relative w-10 h-5 rounded-full transition-colors"
      style={{ background: checked ? 'var(--sv-gold-primary)' : 'var(--border)', border: '1px solid var(--border-hover)' }}
      data-testid={testId}
    >
      <div className="absolute top-0.5 w-4 h-4 rounded-full transition-transform" style={{ background: checked ? 'var(--bg)' : 'var(--muted)', transform: checked ? 'translateX(20px)' : 'translateX(2px)' }} />
    </button>
  );

  const InputField = ({ label, value, onChange, placeholder, testId }) => (
    <div className="space-y-1">
      <label className="text-xs" style={{ color: 'var(--muted)' }}>{label}</label>
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm py-2 px-3 rounded-lg"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
        data-testid={testId}
      />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="app-download-panel">
      <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Gerencie a pagina de download do app e os links de distribuicao</p>

      {/* Direct Download */}
      <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Download size={14} style={{ color: 'var(--sv-gold-primary)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Download Direto (APK)</span>
          </div>
          <ToggleSwitch checked={!!form.direct_download_active} onChange={v => update('direct_download_active', v)} testId="toggle-direct" />
        </div>
        <InputField label="URL do arquivo APK" value={form.direct_download_url} onChange={v => update('direct_download_url', v)} placeholder="https://example.com/app.apk" testId="input-direct-url" />
      </div>

      {/* Google Play */}
      <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded flex items-center justify-center" style={{ background: form.play_store_active ? '#10B981' : 'var(--surface-3)', fontSize: 9, color: 'var(--text)' }}>P</span>
            <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Google Play Store</span>
          </div>
          <ToggleSwitch checked={!!form.play_store_active} onChange={v => update('play_store_active', v)} testId="toggle-play" />
        </div>
        <InputField label="URL da Play Store" value={form.play_store_url} onChange={v => update('play_store_url', v)} placeholder="https://play.google.com/store/apps/details?id=..." testId="input-play-url" />
        {!form.play_store_active && <p className="text-[10px] mt-2" style={{ color: 'var(--muted)' }}>Desativado = mostra "Em breve" na pagina</p>}
      </div>

      {/* App Store */}
      <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded flex items-center justify-center" style={{ background: form.app_store_active ? '#3B82F6' : 'var(--surface-3)', fontSize: 9, color: 'var(--text)' }}>A</span>
            <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Apple App Store</span>
          </div>
          <ToggleSwitch checked={!!form.app_store_active} onChange={v => update('app_store_active', v)} testId="toggle-appstore" />
        </div>
        <InputField label="URL da App Store" value={form.app_store_url} onChange={v => update('app_store_url', v)} placeholder="https://apps.apple.com/app/..." testId="input-appstore-url" />
        {!form.app_store_active && <p className="text-[10px] mt-2" style={{ color: 'var(--muted)' }}>Desativado = mostra "Em breve" na pagina</p>}
      </div>

      {/* Texts */}
      <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <span className="text-sm font-medium mb-3 block" style={{ color: 'var(--text)' }}>Textos da pagina</span>
        <div className="space-y-3">
          <InputField label="Titulo principal" value={form.hero_title} onChange={v => update('hero_title', v)} placeholder="SuperVitrine no seu bolso" testId="input-hero-title" />
          <InputField label="Subtitulo" value={form.hero_subtitle} onChange={v => update('hero_subtitle', v)} placeholder="Ofertas exclusivas..." testId="input-hero-sub" />
          <div className="grid grid-cols-2 gap-3">
            <InputField label="Texto do CTA" value={form.cta_text} onChange={v => update('cta_text', v)} placeholder="Baixar agora" testId="input-cta" />
            <InputField label="Texto 'Em breve'" value={form.coming_soon_text} onChange={v => update('coming_soon_text', v)} placeholder="Em breve" testId="input-coming" />
          </div>
          <InputField label="Texto de observacao" value={form.note_text} onChange={v => update('note_text', v)} placeholder="Download seguro e gratuito" testId="input-note" />
        </div>
      </div>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={savingApp}
        className="w-full py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors hover:opacity-90"
        style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
        data-testid="save-app-config"
      >
        {savingApp ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        Salvar configuracoes do app
      </button>

      {/* Preview link */}
      <a href="/baixar-app" target="_blank" rel="noopener noreferrer" className="block text-center text-xs no-underline hover:underline" style={{ color: 'var(--sv-gold-primary)' }} data-testid="preview-download-page">
        Ver pagina de download →
      </a>
    </div>
  );
}

function TelegramAdvancedManager({ token }) {
  const [config, setConfig] = useState(null);
  const [channels, setChannels] = useState([]);
  const [sendLogs, setSendLogs] = useState([]);
  const [extractedDeals, setExtractedDeals] = useState([]);
  const [health, setHealth] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [subTab, setSubTab] = useState('health');
  const [logFilter, setLogFilter] = useState('all');
  const [newChannel, setNewChannel] = useState({ channel_id: '', channel_name: '' });
  const [addingChannel, setAddingChannel] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDeals, setPreviewDeals] = useState([]);
  const [previewDealId, setPreviewDealId] = useState('');
  const [previewSearch, setPreviewSearch] = useState('');
  const [extractedFilter, setExtractedFilter] = useState('');
  const [importBusy, setImportBusy] = useState({});
  const [creds, setCreds] = useState({ bot_token_masked: '', bot_token_present: false, chat_id: '', bot_status: '' });
  const [newBotToken, setNewBotToken] = useState('');
  const [savingCreds, setSavingCreds] = useState(false);

  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/telegram-advanced`, { headers });
      if (res.ok) { const data = await res.json(); setConfig(data.config || null); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadHealth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/telegram-health`, { headers });
      if (res.ok) { const data = await res.json(); setHealth(data.health || null); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadChannels = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/telegram-channels`, { headers });
      if (res.ok) { const data = await res.json(); setChannels(Array.isArray(data.channels) ? data.channels : []); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadSendLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/telegram-send-logs?limit=50`, { headers });
      if (res.ok) { const data = await res.json(); setSendLogs(Array.isArray(data.logs) ? data.logs : []); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadExtractedDeals = useCallback(async () => {
    try {
      let url = `${API}/admin/master/telegram-extracted-deals?limit=30`;
      if (extractedFilter) url += `&status=${extractedFilter}`;
      const res = await fetch(url, { headers });
      if (res.ok) { const data = await res.json(); setExtractedDeals(Array.isArray(data.deals) ? data.deals : []); }
    } catch (e) { /* ignore */ }
  }, [token, extractedFilter]);

  const loadPreviewDeals = useCallback(async (q = '') => {
    try {
      const res = await fetch(`${API}/admin/master/telegram-preview-deals?q=${encodeURIComponent(q)}&limit=20`, { headers });
      if (res.ok) { const data = await res.json(); setPreviewDeals(data.deals || []); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadPreview = useCallback(async (dealId = '') => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API}/admin/master/telegram-preview`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ deal_id: dealId }),
      });
      if (res.ok) { const data = await res.json(); setPreview(data.preview); }
      else { toast.error('Erro ao gerar preview'); }
    } catch (e) { toast.error('Erro de conexao'); }
    setPreviewLoading(false);
  }, [token]);

  const handleExtractedStatus = async (extractedId, newStatus) => {
    setImportBusy(p => ({ ...p, [extractedId]: true }));
    try {
      const res = await fetch(`${API}/admin/master/extracted-deals/${extractedId}/status`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) { toast.success(`Status: ${newStatus}`); loadExtractedDeals(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d?.detail || 'Erro'); }
    } catch (e) { toast.error(normalizeAppError(e).message); }
    setImportBusy(p => ({ ...p, [extractedId]: false }));
  };

  const handleImportDeal = async (extractedId, override = false) => {
    if (!override && !window.confirm('Importar esta oferta para o fluxo principal?')) return;
    setImportBusy(p => ({ ...p, [extractedId]: true }));
    try {
      const res = await fetch(`${API}/admin/master/extracted-deals/${extractedId}/import`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ override }),
      });
      const d = await res.json();
      if (res.ok) { toast.success(`Importada! Deal: ${d.deal_id}`); loadExtractedDeals(); }
      else if (res.status === 422 || res.status === 409) {
        toast.error(d.detail || 'Bloqueada');
      } else { toast.error(d.detail || 'Erro'); }
    } catch (e) { toast.error(normalizeAppError(e).message); }
    setImportBusy(p => ({ ...p, [extractedId]: false }));
  };

  const loadCreds = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/telegram-credentials`, { headers });
      if (res.ok) { const d = await res.json(); setCreds(d); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const handleSaveCreds = async () => {
    const payload = {};
    if (newBotToken.trim()) payload.bot_token = newBotToken.trim();
    if (creds.chat_id !== undefined) payload.chat_id = creds.chat_id;
    if (!Object.keys(payload).length) { toast.error('Nada para salvar'); return; }
    setSavingCreds(true);
    try {
      const res = await fetch(`${API}/admin/master/telegram-credentials`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (res.ok) { toast.success(d.message || 'Credenciais salvas'); setNewBotToken(''); loadCreds(); loadHealth(); }
      else toast.error(d.detail || 'Erro ao salvar credenciais');
    } catch (e) { toast.error('Erro de conexao'); }
    setSavingCreds(false);
  };

  useEffect(() => { loadExtractedDeals(); }, [extractedFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadConfig(), loadHealth(), loadChannels(), loadSendLogs(), loadExtractedDeals(), loadCreds()])
      .finally(() => setLoading(false));
  }, []);

  const updateConfig = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/master/telegram-advanced`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (res.ok) { toast.success('Configuracoes salvas'); loadConfig(); loadHealth(); }
      else toast.error('Erro ao salvar');
    } catch (e) { toast.error('Erro de conexao'); }
    setSaving(false);
  };

  const handleQuickTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/telegram-notify-config/test`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      setTestResult({ ok: data.success !== false && res.ok, message: data.message || data.detail || 'Erro desconhecido' });
      if (data.success !== false && res.ok) {
        toast.success('Teste enviado!');
        loadHealth();
      } else {
        toast.error(data.message || data.detail || 'Falha no teste');
      }
    } catch (e) { setTestResult({ ok: false, message: 'Erro de conexao' }); toast.error('Erro de conexao'); }
    setTesting(false);
  };

  const handleAddChannel = async () => {
    if (!newChannel.channel_id.trim()) { toast.error('ID do canal obrigatorio'); return; }
    setAddingChannel(true);
    try {
      const res = await fetch(`${API}/admin/master/telegram-channels`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(newChannel),
      });
      if (res.ok) {
        toast.success('Canal adicionado');
        setNewChannel({ channel_id: '', channel_name: '' });
        loadChannels();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao adicionar');
      }
    } catch (e) { toast.error('Erro de conexao'); }
    setAddingChannel(false);
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Remover canal monitorado?')) return;
    try {
      const res = await fetch(`${API}/admin/master/telegram-channels/${encodeURIComponent(channelId)}`, {
        method: 'DELETE', headers,
      });
      if (res.ok) { toast.success('Canal removido'); loadChannels(); }
      else toast.error('Erro ao remover');
    } catch (e) { toast.error('Erro de conexao'); }
  };

  const handleToggleChannel = async (ch) => {
    try {
      const res = await fetch(`${API}/admin/master/telegram-channels/${encodeURIComponent(ch.channel_id)}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !ch.active }),
      });
      if (res.ok) loadChannels();
    } catch (e) { /* ignore */ }
  };

  const ToggleSwitch = ({ checked, onChange, testId }) => (
    <button
      onClick={() => onChange(!checked)}
      className="relative w-10 h-5 rounded-full transition-colors"
      style={{ background: checked ? 'var(--sv-gold-primary)' : 'var(--border)', border: '1px solid var(--border-hover)' }}
      data-testid={testId}
    >
      <div className="absolute top-0.5 w-4 h-4 rounded-full transition-transform" style={{ background: checked ? 'var(--bg)' : 'var(--muted)', transform: checked ? 'translateX(20px)' : 'translateX(2px)' }} />
    </button>
  );

  const HealthDot = ({ ok }) => (
    <span className="inline-block w-2 h-2 rounded-full" style={{ background: ok ? '#10B981' : '#EF4444' }} />
  );

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

  const SUB_TABS = [
    { key: 'health', label: 'Saude', icon: Shield },
    { key: 'preview', label: 'Preview', icon: Monitor },
    { key: 'config', label: 'Config', icon: Image },
    { key: 'channels', label: 'Canais', icon: Radio },
    { key: 'logs', label: 'Logs', icon: FileText },
    { key: 'extracted', label: 'Extraidas', icon: Tag },
  ];

  const filteredLogs = logFilter === 'all' ? sendLogs
    : logFilter === 'ok' ? sendLogs.filter(l => l.send_status === 'ok')
    : logFilter === 'error' ? sendLogs.filter(l => l.send_status !== 'ok' && l.send_status !== 'skipped')
    : logFilter === 'skipped' ? sendLogs.filter(l => l.send_status === 'skipped')
    : sendLogs;

  return (
    <div className="space-y-4" data-testid="telegram-advanced-panel">
      {/* Sub-tabs */}
      <div className="flex gap-1 p-1 rounded-lg overflow-x-auto" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors whitespace-nowrap"
            style={{ background: subTab === t.key ? 'var(--gold-bg)' : 'transparent', color: subTab === t.key ? 'var(--sv-gold-primary)' : 'var(--muted)', border: subTab === t.key ? '1px solid var(--sv-gold-primary)' : '1px solid transparent' }}
            data-testid={`subtab-${t.key}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* HEALTH Sub-tab */}
      {subTab === 'health' && (
        <div className="space-y-3" data-testid="telegram-health-panel">
          {/* Health Cards */}
          {health ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Bot Token', ok: health.bot_token_present, detail: health.bot_status || 'N/A' },
                  { label: 'Chat ID', ok: health.chat_id_present, detail: health.chat_id_preview || 'N/A' },
                  { label: 'Notificacoes', ok: health.notifications_enabled, detail: health.notifications_enabled ? 'Ativas' : 'Desativadas' },
                  { label: 'Imagem Obrigatoria', ok: health.require_image, detail: health.require_image ? 'Ativo' : 'Desativado' },
                  { label: 'Cupom Automatico', ok: health.auto_coupon, detail: health.auto_coupon ? 'Ativo' : 'Desativado' },
                  { label: 'Formato Profissional', ok: health.professional_format, detail: health.professional_format ? 'Ativo' : 'Desativado' },
                ].map((item, i) => (
                  <div key={i} className="rounded-lg p-2.5 flex items-center gap-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`health-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <HealthDot ok={item.ok} />
                    <div className="min-w-0">
                      <div className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{item.label}</div>
                      <div className="text-xs font-medium truncate" style={{ color: item.ok ? 'var(--text)' : '#EF4444' }}>{item.detail}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Counters */}
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg p-3 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                  <div className="text-lg font-bold" style={{ color: 'var(--status-green)' }}>{health.total_sent}</div>
                  <div className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Enviados</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                  <div className="text-lg font-bold" style={{ color: 'var(--status-red)' }}>{health.total_errors}</div>
                  <div className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Erros</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                  <div className="text-lg font-bold" style={{ color: 'var(--sv-gold-primary)' }}>{health.custom_buttons || 0}/{health.monitored_channels || 0}</div>
                  <div className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Botoes/Canais</div>
                </div>
              </div>

              {/* Last send info */}
              {health.last_ok && (
                <div className="rounded-lg p-3" style={{ background: 'var(--status-success-bg)', border: '1px solid var(--status-success-border)' }}>
                  <div className="text-[10px] font-medium" style={{ color: 'var(--status-green)' }}>Ultimo envio OK</div>
                  <div className="text-xs mt-1" style={{ color: 'var(--text)' }}>{health.last_ok.title}</div>
                  <div className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{health.last_ok.timestamp ? new Date(health.last_ok.timestamp).toLocaleString('pt-BR') : ''} | {health.last_ok.send_method}</div>
                </div>
              )}
              {health.last_error && (
                <div className="rounded-lg p-3" style={{ background: 'var(--status-error-bg)', border: '1px solid var(--status-error-border)' }}>
                  <div className="text-[10px] font-medium" style={{ color: 'var(--status-red)' }}>Ultimo erro</div>
                  <div className="text-xs mt-1" style={{ color: 'var(--text)' }}>{health.last_error.title}</div>
                  <div className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{health.last_error.details}</div>
                  <div className="text-[10px]" style={{ color: 'var(--muted)' }}>{health.last_error.timestamp ? new Date(health.last_error.timestamp).toLocaleString('pt-BR') : ''}</div>
                </div>
              )}
            </>
          ) : (
            <p className="text-xs" style={{ color: 'var(--muted)' }}>Erro ao carregar saude</p>
          )}

          {/* Quick Test */}
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-medium" style={{ color: 'var(--text)' }}>Teste Rapido</div>
                <div className="text-[10px]" style={{ color: 'var(--muted)' }}>Envia mensagem de teste para o grupo configurado</div>
              </div>
              <button onClick={handleQuickTest} disabled={testing}
                className="px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 hover:opacity-90 transition-opacity"
                style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }} data-testid="quick-test-btn">
                {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {testing ? 'Enviando...' : 'Enviar Teste'}
              </button>
            </div>
            {testResult && (
              <div className="rounded p-2 mt-2 text-xs" style={{ background: testResult.ok ? '#0a1a0a' : '#1a0a0a', color: testResult.ok ? '#10B981' : '#EF4444', border: `1px solid ${testResult.ok ? '#1a2a1a' : '#2a1a1a'}` }}
                data-testid="test-result">
                {testResult.message}
              </div>
            )}
            <div className="rounded p-2 mt-2 flex items-center gap-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <AlertTriangle size={12} style={{ color: 'var(--sv-gold-primary)', flexShrink: 0 }} />
              <span className="text-[10px]" style={{ color: 'var(--muted-2)' }}>
                O teste interno NAO grava nos logs de envio real. Aparece apenas como "ok (teste)" no status geral.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW Sub-tab */}
      {subTab === 'preview' && (
        <div className="space-y-3" data-testid="telegram-preview-panel">
          {/* Deal selector */}
          <div className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Search size={13} style={{ color: 'var(--sv-gold-primary)' }} />
              <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>Selecionar Oferta</span>
            </div>
            <div className="flex gap-2">
              <input
                type="text" placeholder="Buscar oferta por titulo..."
                value={previewSearch} onChange={e => setPreviewSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadPreviewDeals(previewSearch)}
                className="flex-1 px-3 py-1.5 rounded text-xs" data-testid="preview-search-input"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
              />
              <button onClick={() => loadPreviewDeals(previewSearch)}
                className="px-3 py-1.5 rounded text-xs font-medium" data-testid="preview-search-btn"
                style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }}>
                <Search size={12} />
              </button>
            </div>
            {previewDeals.length > 0 && (
              <div className="mt-2 max-h-36 overflow-y-auto space-y-1 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                {previewDeals.map(d => (
                  <button key={d.deal_id} onClick={() => { setPreviewDealId(d.deal_id); loadPreview(d.deal_id); }}
                    className="w-full text-left px-2.5 py-1.5 hover:opacity-80 transition-opacity flex justify-between items-center"
                    style={{ background: previewDealId === d.deal_id ? 'var(--gold-bg)' : 'transparent', borderBottom: '1px solid var(--border)' }}
                    data-testid={`preview-deal-${d.deal_id}`}>
                    <span className="text-[11px] truncate flex-1" style={{ color: 'var(--text)' }}>{d.title}</span>
                    <span className="text-[10px] ml-2 shrink-0" style={{ color: 'var(--sv-gold-primary)' }}>
                      {d.discount_percent > 0 ? `-${d.discount_percent}%` : `R$${(d.current_price || 0).toFixed(0)}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <button onClick={() => { setPreviewDealId(''); loadPreview(''); }}
                className="px-3 py-1.5 rounded text-[11px] font-medium" data-testid="preview-mock-btn"
                style={{ background: 'var(--surface-2)', color: 'var(--muted-2)', border: '1px solid var(--border)' }}>
                Usar Oferta Mock
              </button>
              <button onClick={() => loadPreview(previewDealId)} disabled={previewLoading}
                className="px-3 py-1.5 rounded text-[11px] font-bold flex items-center gap-1.5" data-testid="preview-refresh-btn"
                style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }}>
                {previewLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Atualizar Preview
              </button>
            </div>
          </div>

          {/* Telegram Preview Card */}
          {preview ? (
            <div className="grid gap-3" style={{ gridTemplateColumns: '1fr' }}>
              <div className="rounded-xl overflow-hidden" style={{ background: '#0e1621', border: '1px solid #1a2a3a', maxWidth: 420 }} data-testid="preview-telegram-card">
                {/* Header */}
                <div className="px-3 py-2 flex items-center gap-2" style={{ background: '#17212b', borderBottom: '1px solid #1a2a3a' }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: 'var(--sv-brand-icon-bg)', color: 'var(--sv-brand-icon-fg)' }}>SV</div>
                  <div>
                    <div className="text-xs font-medium" style={{ color: 'var(--text)' }}>SuperVitrine Ofertas</div>
                    <div className="text-[9px]" style={{ color: '#6c7883' }}>bot</div>
                  </div>
                  <div className="ml-auto text-[9px]" style={{ color: preview.deal_source === 'database' ? '#10B981' : 'var(--sv-gold-primary)' }}>
                    {preview.deal_source === 'database' ? 'Oferta Real' : 'Oferta Mock'}
                  </div>
                </div>

                {/* Image */}
                {preview.image_url && (
                  <div className="relative" style={{ maxHeight: 220, overflow: 'hidden' }}>
                    <img src={preview.image_url} alt="preview" className="w-full object-cover" style={{ maxHeight: 220 }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}

                {/* Caption */}
                <div className="px-3 py-2.5">
                  <pre className="text-[11px] whitespace-pre-wrap font-sans leading-relaxed" style={{ color: '#e4e6eb' }} data-testid="preview-caption">
                    {preview.caption}
                  </pre>
                </div>

                {/* Inline Buttons */}
                {preview.buttons && preview.buttons.length > 0 && (
                  <div className="px-3 pb-3 space-y-1" data-testid="preview-buttons">
                    {(() => {
                      const rows = [];
                      if (preview.buttons.length >= 2) {
                        rows.push(preview.buttons.slice(0, 2));
                        preview.buttons.slice(2, 5).forEach(b => rows.push([b]));
                      } else {
                        rows.push(preview.buttons);
                      }
                      return rows.map((row, ri) => (
                        <div key={ri} className="flex gap-1">
                          {row.map((btn, bi) => (
                            <div key={bi} className="flex-1 text-center py-1.5 rounded text-[11px] font-medium truncate"
                              style={{ background: '#2b5278', color: '#7eb8f7' }}>
                              {btn.text}
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <button onClick={() => { navigator.clipboard.writeText(preview.caption); toast.success('Caption copiada!'); }}
                  className="px-3 py-1.5 rounded text-[11px] font-medium flex items-center gap-1.5" data-testid="copy-caption-btn"
                  style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }}>
                  <Copy size={12} /> Copiar Caption
                </button>
                <button onClick={() => { navigator.clipboard.writeText(JSON.stringify(preview.reply_markup, null, 2)); toast.success('Payload copiado!'); }}
                  className="px-3 py-1.5 rounded text-[11px] font-medium flex items-center gap-1.5" data-testid="copy-payload-btn"
                  style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }}>
                  <Copy size={12} /> Copiar Payload
                </button>
              </div>

              {/* Technical Debug Panel */}
              <div className="rounded-lg p-3 space-y-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }} data-testid="preview-debug-panel">
                <div className="flex items-center gap-1.5 mb-1">
                  <Eye size={12} style={{ color: 'var(--sv-gold-primary)' }} />
                  <span className="text-[11px] font-medium" style={{ color: 'var(--sv-gold-primary)' }}>Debug Tecnico</span>
                </div>
                {[
                  { label: 'Image Source', value: preview.image_source, color: preview.image_url ? '#10B981' : '#EF4444' },
                  { label: 'Coupon Detected', value: preview.coupon_code || '(nenhum)', color: preview.coupon_code ? '#10B981' : 'var(--muted-2)' },
                  { label: 'Coupon Source', value: preview.coupon_source, color: 'var(--muted-2)' },
                  { label: 'Buy URL', value: preview.buy_url || '(vazio)', color: 'var(--status-blue)', truncate: true },
                  { label: 'Details URL', value: preview.details_url || '(vazio)', color: 'var(--status-blue)', truncate: true },
                  { label: 'Buttons Count', value: String(preview.buttons_count), color: 'var(--sv-gold-primary)' },
                  { label: 'Professional Format', value: preview.professional_format ? 'ON' : 'OFF', color: preview.professional_format ? '#10B981' : '#EF4444' },
                  { label: 'Deal Source', value: preview.deal_source, color: preview.deal_source === 'database' ? '#10B981' : 'var(--sv-gold-primary)' },
                ].map((row, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[10px] shrink-0 w-28" style={{ color: 'var(--muted)' }}>{row.label}</span>
                    <span className={`text-[10px] ${row.truncate ? 'truncate' : ''}`} style={{ color: row.color }}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-10" style={{ color: 'var(--muted)' }}>
              <Monitor size={28} className="mx-auto mb-3" style={{ color: 'var(--surface-3)' }} />
              <p className="text-sm">Nenhum preview gerado</p>
              <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Selecione uma oferta ou use o mock para visualizar</p>
              <button onClick={() => loadPreview('')}
                className="mt-3 px-4 py-2 rounded-lg text-xs font-bold" data-testid="preview-generate-btn"
                style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }}>
                Gerar Preview Mock
              </button>
            </div>
          )}
        </div>
      )}


      {/* CONFIG Sub-tab */}
      {subTab === 'config' && config && (
        <div className="space-y-3">
          {/* Credenciais do Bot */}
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Key size={14} style={{ color: 'var(--sv-gold-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Credenciais do Bot</span>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs block mb-1" style={{ color: 'var(--muted-2)' }}>Bot Token</label>
                {creds.bot_token_present && !newBotToken && (
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-[10px] px-2 py-1 rounded" style={{ background: 'var(--bg)', color: 'var(--status-green)', border: '1px solid var(--border)' }}>{creds.bot_token_masked}</code>
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: creds.bot_status === 'connected' ? '#0a1a0a' : '#1a0a0a', color: creds.bot_status === 'connected' ? '#10B981' : '#EF4444' }}>
                      {creds.bot_status === 'connected' ? 'Conectado' : creds.bot_status}
                    </span>
                  </div>
                )}
                <input type="text" value={newBotToken} onChange={e => setNewBotToken(e.target.value)}
                  placeholder={creds.bot_token_present ? 'Novo token (deixe vazio para manter atual)' : 'Cole o Bot Token do @BotFather'}
                  className="w-full text-sm py-1.5 px-2 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                  data-testid="input-bot-token" />
              </div>
              <div>
                <label className="text-xs block mb-1" style={{ color: 'var(--muted-2)' }}>Chat ID</label>
                <input type="text" value={creds.chat_id} onChange={e => setCreds(p => ({ ...p, chat_id: e.target.value }))}
                  placeholder="Ex: -1001234567890 ou 964787263"
                  className="w-full text-sm py-1.5 px-2 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                  data-testid="input-chat-id" />
                <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Use @userinfobot no Telegram para descobrir seu Chat ID</p>
              </div>
              <button onClick={handleSaveCreds} disabled={savingCreds}
                className="px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 hover:opacity-90"
                style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="save-telegram-creds">
                {savingCreds ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar credenciais
              </button>
            </div>
          </div>
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Image size={14} style={{ color: 'var(--sv-gold-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Imagem</span>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs" style={{ color: 'var(--text)' }}>Obrigar imagem no envio</div>
                  <div className="text-[10px]" style={{ color: 'var(--muted)' }}>Redimensiona e envia como upload</div>
                </div>
                <ToggleSwitch checked={!!config.require_image} onChange={v => updateConfig('require_image', v)} testId="toggle-require-image" />
              </div>
              <div>
                <label className="text-xs block mb-1" style={{ color: 'var(--muted)' }}>Tamanho maximo (px)</label>
                <input type="number" value={config.max_image_size || 500} onChange={e => updateConfig('max_image_size', parseInt(e.target.value) || 500)}
                  className="w-24 text-sm py-1.5 px-2 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-max-image-size" />
              </div>
            </div>
          </div>
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Tag size={14} style={{ color: 'var(--sv-gold-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Cupom</span>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs" style={{ color: 'var(--text)' }}>Deteccao automatica de cupons</div>
                  <div className="text-[10px]" style={{ color: 'var(--muted)' }}>Busca cupons no titulo e descricao</div>
                </div>
                <ToggleSwitch checked={!!config.auto_coupon_detection} onChange={v => updateConfig('auto_coupon_detection', v)} testId="toggle-auto-coupon" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs" style={{ color: 'var(--text)' }}>Destacar cupom na mensagem</div>
                </div>
                <ToggleSwitch checked={!!config.coupon_highlight} onChange={v => updateConfig('coupon_highlight', v)} testId="toggle-coupon-highlight" />
              </div>
            </div>
          </div>
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Send size={14} style={{ color: 'var(--sv-gold-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Formato</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs" style={{ color: 'var(--text)' }}>Formato profissional</div>
                <div className="text-[10px]" style={{ color: 'var(--muted)' }}>Layout visual otimizado para conversao</div>
              </div>
              <ToggleSwitch checked={!!config.professional_format} onChange={v => updateConfig('professional_format', v)} testId="toggle-professional-format" />
            </div>
          </div>
          <button onClick={handleSaveConfig} disabled={saving}
            className="w-full py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:opacity-90"
            style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="save-telegram-advanced">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar configuracoes
          </button>
        </div>
      )}

      {/* CHANNELS Sub-tab */}
      {subTab === 'channels' && (
        <div className="space-y-3">
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Plus size={14} style={{ color: 'var(--sv-gold-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Adicionar canal</span>
            </div>
            <div className="flex gap-2">
              <input type="text" value={newChannel.channel_id} onChange={e => setNewChannel(p => ({ ...p, channel_id: e.target.value }))}
                placeholder="@canal ou -100123456..." className="flex-1 text-sm py-2 px-3 rounded-lg"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-channel-id" />
              <input type="text" value={newChannel.channel_name} onChange={e => setNewChannel(p => ({ ...p, channel_name: e.target.value }))}
                placeholder="Nome (opcional)" className="w-32 text-sm py-2 px-3 rounded-lg"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-channel-name" />
              <button onClick={handleAddChannel} disabled={addingChannel} className="px-3 py-2 rounded-lg text-xs font-bold"
                style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="add-channel-btn">
                {addingChannel ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              </button>
            </div>
            <p className="text-[10px] mt-2" style={{ color: 'var(--muted)' }}>O bot precisa ser membro/admin do canal</p>
          </div>
          {channels.length === 0 ? (
            <div className="text-center py-6" style={{ color: 'var(--muted)' }}>
              <Radio size={24} className="mx-auto mb-2" /><p className="text-sm">Nenhum canal monitorado</p>
            </div>
          ) : (
            <div className="space-y-2">
              {channels.map(ch => (
                <div key={ch.channel_id} className="rounded-lg p-3 flex items-center gap-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`channel-${ch.channel_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{ch.channel_name}</div>
                    <div className="text-[10px]" style={{ color: 'var(--muted)' }}>{ch.channel_id}</div>
                    <div className="flex gap-3 mt-1">
                      <span className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Extraidas: {ch.total_extracted || 0}</span>
                      {ch.last_checked_at && <span className="text-[10px]" style={{ color: 'var(--muted)' }}>Ultima: {new Date(ch.last_checked_at).toLocaleString('pt-BR')}</span>}
                    </div>
                  </div>
                  <ToggleSwitch checked={!!ch.active} onChange={() => handleToggleChannel(ch)} testId={`toggle-channel-${ch.channel_id}`} />
                  <button onClick={() => handleDeleteChannel(ch.channel_id)} className="p-1.5 rounded hover:bg-[rgba(128,128,128,0.08)]" style={{ color: 'var(--status-red)' }} data-testid={`delete-channel-${ch.channel_id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* LOGS Sub-tab */}
      {subTab === 'logs' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              {[{ key: 'all', label: 'Todos' }, { key: 'ok', label: 'Sucesso' }, { key: 'error', label: 'Erros' }, { key: 'skipped', label: 'Pulados' }].map(f => (
                <button key={f.key} onClick={() => setLogFilter(f.key)}
                  className="text-[10px] px-2 py-1 rounded transition-colors"
                  style={{ background: logFilter === f.key ? 'var(--gold-bg)' : 'var(--bg)', color: logFilter === f.key ? 'var(--sv-gold-primary)' : 'var(--muted)', border: `1px solid ${logFilter === f.key ? 'var(--sv-gold-primary)' : 'var(--border)'}` }}
                  data-testid={`log-filter-${f.key}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <button onClick={loadSendLogs} className="text-xs flex items-center gap-1 px-2 py-1 rounded" style={{ color: 'var(--sv-gold-primary)', background: 'var(--gold-bg)' }}>
              <RefreshCw size={12} /> Atualizar
            </button>
          </div>
          <div className="rounded p-2 flex items-center gap-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <AlertTriangle size={11} style={{ color: 'var(--sv-gold-primary)', flexShrink: 0 }} />
            <span className="text-[9px]" style={{ color: 'var(--muted)' }}>Estes logs registram apenas envios REAIS. O teste interno nao aparece aqui.</span>
          </div>
          {filteredLogs.length === 0 ? (
            <div className="text-center py-6" style={{ color: 'var(--muted)' }}>
              <FileText size={24} className="mx-auto mb-2" /><p className="text-sm">Nenhum log {logFilter !== 'all' ? `com filtro "${logFilter}"` : ''}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredLogs.map((log, i) => (
                <div key={i} className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`send-log-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium truncate pr-2" style={{ color: 'var(--text)', maxWidth: '60%' }}>{log.title}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: log.send_status === 'ok' ? '#0a1a0a' : log.send_status === 'skipped' ? 'var(--border)' : '#1a0a0a', color: log.send_status === 'ok' ? '#10B981' : log.send_status === 'skipped' ? 'var(--muted-2)' : '#EF4444' }}>
                      {log.send_status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                    <span>Img: <span style={{ color: log.image_status?.includes('resize') || log.image_status?.includes('ok') || log.image_status?.includes('passthrough') ? '#10B981' : 'var(--muted)' }}>{log.image_status}</span></span>
                    <span>Cupom: <span style={{ color: log.coupon_status?.includes('detected') || log.coupon_status?.includes('manual') ? 'var(--sv-gold-primary)' : 'var(--muted)' }}>{log.coupon_status}</span></span>
                    <span>Via: {log.send_method}</span>
                  </div>
                  {log.details && <div className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>{log.details}</div>}
                  <div className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>{log.timestamp ? new Date(log.timestamp).toLocaleString('pt-BR') : ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* EXTRACTED Sub-tab */}
      {subTab === 'extracted' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Ofertas extraidas ({extractedDeals.length})</p>
            <button onClick={loadExtractedDeals} className="text-xs flex items-center gap-1 px-2 py-1 rounded" style={{ color: 'var(--sv-gold-primary)', background: 'var(--gold-bg)' }}>
              <RefreshCw size={12} /> Atualizar
            </button>
          </div>

          {/* Status filters */}
          <div className="flex gap-1.5 flex-wrap">
            {[
              { v: '', l: 'Todas' },
              { v: 'extracted', l: 'Extraidas', c: 'var(--muted-2)' },
              { v: 'reviewed', l: 'Revisadas', c: '#F59E0B' },
              { v: 'approved', l: 'Aprovadas', c: '#10B981' },
              { v: 'imported', l: 'Importadas', c: '#3B82F6' },
              { v: 'blocked_quality', l: 'Bloqueadas', c: '#EF4444' },
              { v: 'duplicate', l: 'Duplicadas', c: '#A855F7' },
              { v: 'discarded', l: 'Descartadas', c: 'var(--muted)' },
            ].map(f => (
              <button key={f.v} onClick={() => setExtractedFilter(f.v)}
                className="text-[10px] px-2 py-1 rounded transition-colors" data-testid={`ext-filter-${f.v || 'all'}`}
                style={{ background: extractedFilter === f.v ? 'var(--gold-bg)' : 'var(--bg)', color: extractedFilter === f.v ? 'var(--sv-gold-primary)' : (f.c || 'var(--muted)'), border: `1px solid ${extractedFilter === f.v ? 'var(--sv-gold-primary)' : 'var(--border)'}` }}>
                {f.l}
              </button>
            ))}
          </div>

          {extractedDeals.length === 0 ? (
            <div className="text-center py-6" style={{ color: 'var(--muted)' }}>
              <Tag size={24} className="mx-auto mb-2" /><p className="text-sm">Nenhuma oferta extraida{extractedFilter ? ` com status "${extractedFilter}"` : ''}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {extractedDeals.map((deal, i) => {
                const status = deal.import_status || 'extracted';
                const statusColors = {
                  extracted: { bg: 'var(--border)', c: 'var(--muted-2)', l: 'Extraida' },
                  reviewed: { bg: 'var(--gold-bg)', c: '#F59E0B', l: 'Revisada' },
                  approved: { bg: '#0a1a0a', c: '#10B981', l: 'Aprovada' },
                  imported: { bg: '#0a0a1a', c: '#3B82F6', l: 'Importada' },
                  blocked_quality: { bg: '#1a0a0a', c: '#EF4444', l: 'Bloqueada' },
                  duplicate: { bg: '#0f0a1a', c: '#A855F7', l: 'Duplicada' },
                  discarded: { bg: 'var(--surface)', c: 'var(--muted)', l: 'Descartada' },
                };
                const sc = statusColors[status] || statusColors.extracted;
                const qs = deal.quality_score;
                const qsColor = qs >= 70 ? '#10B981' : qs >= 50 ? '#F59E0B' : qs != null ? '#EF4444' : 'var(--muted)';
                const busy = importBusy[deal.extracted_id];

                return (
                  <div key={deal.extracted_id || i} className="rounded-lg p-3" style={{ background: 'var(--surface)', border: `1px solid ${status === 'imported' ? '#3B82F630' : status === 'blocked_quality' ? '#EF444430' : 'var(--border)'}` }} data-testid={`extracted-deal-${deal.extracted_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{deal.product || 'Sem titulo'}</div>
                        <div className="flex flex-wrap gap-2 mt-1 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                          {deal.price > 0 && <span>R$ {deal.price.toFixed(2)}</span>}
                          {deal.original_price > 0 && deal.original_price > deal.price && <span className="line-through">R$ {deal.original_price.toFixed(2)}</span>}
                          {deal.discount_percent > 0 && <span style={{ color: 'var(--status-green)' }}>-{deal.discount_percent}%</span>}
                          {deal.coupon && <span style={{ color: 'var(--sv-gold-primary)' }}>Cupom: {deal.coupon}</span>}
                          {deal.channel_id && <span>Canal: {deal.channel_id}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {qs != null && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${qsColor}15`, color: qsColor, border: `1px solid ${qsColor}30` }}>
                            {qs}/100
                          </span>
                        )}
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: sc.bg, color: sc.c }}>
                          {sc.l}
                        </span>
                      </div>
                    </div>

                    {deal.link && <a href={deal.link} target="_blank" rel="noopener noreferrer" className="text-[10px] mt-1 block truncate" style={{ color: 'var(--status-blue)' }}>{deal.link}</a>}

                    {status === 'blocked_quality' && deal.quality_missing?.length > 0 && (
                      <div className="text-[10px] mt-1 px-2 py-1 rounded" style={{ background: 'var(--status-error-bg)', color: 'var(--status-red)', border: '1px solid var(--status-error-border)' }}>
                        Faltam: {deal.quality_missing.join(', ')}
                      </div>
                    )}
                    {status === 'duplicate' && deal.duplicate_of && (
                      <div className="text-[10px] mt-1" style={{ color: 'var(--status-purple)' }}>Duplicada de: {deal.duplicate_of}</div>
                    )}
                    {status === 'imported' && deal.imported_deal_id && (
                      <div className="text-[10px] mt-1" style={{ color: 'var(--status-blue)' }}>Deal: {deal.imported_deal_id} | Por: {deal.imported_by}</div>
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {status === 'extracted' && (
                        <button onClick={() => handleExtractedStatus(deal.extracted_id, 'reviewed')} disabled={busy}
                          className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 disabled:opacity-40"
                          style={{ background: 'var(--gold-bg)', color: 'var(--status-amber)', border: '1px solid var(--border-hover)' }} data-testid={`review-${deal.extracted_id}`}>
                          {busy ? <Loader2 size={10} className="animate-spin" /> : <Eye size={10} />} Revisar
                        </button>
                      )}
                      {(status === 'extracted' || status === 'reviewed') && (
                        <button onClick={() => handleExtractedStatus(deal.extracted_id, 'approved')} disabled={busy}
                          className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 disabled:opacity-40"
                          style={{ background: 'var(--status-success-bg)', color: 'var(--status-green)', border: '1px solid var(--border-hover)' }} data-testid={`approve-${deal.extracted_id}`}>
                          {busy ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle size={10} />} Aprovar
                        </button>
                      )}
                      {(status === 'approved' || status === 'reviewed') && (
                        <button onClick={() => handleImportDeal(deal.extracted_id)} disabled={busy}
                          className="px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 disabled:opacity-40"
                          style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid={`import-${deal.extracted_id}`}>
                          {busy ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />} Importar
                        </button>
                      )}
                      {(status === 'blocked_quality' || status === 'duplicate') && (
                        <button onClick={() => handleImportDeal(deal.extracted_id, true)} disabled={busy}
                          className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 disabled:opacity-40"
                          style={{ background: 'var(--gold-bg)', color: 'var(--status-amber)', border: '1px solid var(--status-warning-border)' }} data-testid={`override-import-${deal.extracted_id}`}>
                          {busy ? <Loader2 size={10} className="animate-spin" /> : <AlertTriangle size={10} />} Importar mesmo assim
                        </button>
                      )}
                      {status !== 'imported' && status !== 'discarded' && (
                        <button onClick={() => handleExtractedStatus(deal.extracted_id, 'discarded')} disabled={busy}
                          className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 disabled:opacity-40"
                          style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }} data-testid={`discard-${deal.extracted_id}`}>
                          <Ban size={10} /> Descartar
                        </button>
                      )}
                    </div>

                    <div className="text-[9px] mt-1.5" style={{ color: 'var(--muted)' }}>
                      {deal.extracted_at ? new Date(deal.extracted_at).toLocaleString('pt-BR') : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StoreIntegrationsManager({ token }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ store_name: '', store_type: 'custom', notes: '', credentials: {} });
  const [newFieldKey, setNewFieldKey] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  const loadStores = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/store-integrations`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setStores(Array.isArray(data.stores) ? data.stores : []); }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadStores(); }, []);

  const resetForm = () => {
    setForm({ store_name: '', store_type: 'custom', notes: '', credentials: {} });
    setAdding(false);
    setEditingId(null);
    setNewFieldKey('');
  };

  const handleSave = async () => {
    if (!form.store_name.trim()) { toast.error('Nome da loja obrigatorio'); return; }
    setSaving(true);
    try {
      const url = editingId
        ? `${API}/admin/master/store-integrations/${editingId}`
        : `${API}/admin/master/store-integrations`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editingId ? 'Loja atualizada' : 'Loja adicionada');
        resetForm();
        loadStores();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao salvar');
      }
    } catch (e) { toast.error('Erro de conexao'); }
    setSaving(false);
  };

  const handleDelete = async (storeId, name) => {
    if (!window.confirm(`Remover "${name}"? Esta acao e irreversivel.`)) return;
    try {
      const res = await fetch(`${API}/admin/master/store-integrations/${storeId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) { toast.success('Loja removida'); loadStores(); }
      else toast.error('Erro ao remover');
    } catch (e) { toast.error('Erro de conexao'); }
  };

  const handleToggle = async (store) => {
    try {
      await fetch(`${API}/admin/master/store-integrations/${store.store_id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ active: !store.active }),
      });
      loadStores();
    } catch (e) { /* ignore */ }
  };

  const startEdit = (store) => {
    setEditingId(store.store_id);
    setForm({
      store_name: store.store_name,
      store_type: store.store_type || 'custom',
      notes: store.notes || '',
      credentials: {},
    });
    setAdding(true);
  };

  const updateCredField = (key, val) => setForm(p => ({ ...p, credentials: { ...p.credentials, [key]: val } }));
  const removeCredField = (key) => setForm(p => {
    const c = { ...p.credentials };
    delete c[key];
    return { ...p, credentials: c };
  });
  const addField = () => {
    if (!newFieldKey.trim()) return;
    const key = newFieldKey.trim().toLowerCase().replace(/\s+/g, '_');
    setForm(p => ({ ...p, credentials: { ...p.credentials, [key]: '' } }));
    setNewFieldKey('');
  };

  const ToggleSwitch = ({ checked, onChange, testId }) => (
    <button onClick={() => onChange(!checked)} className="relative w-10 h-5 rounded-full transition-colors"
      style={{ background: checked ? 'var(--sv-gold-primary)' : 'var(--border)', border: '1px solid var(--border-hover)' }} data-testid={testId}>
      <div className="absolute top-0.5 w-4 h-4 rounded-full transition-transform"
        style={{ background: checked ? 'var(--bg)' : 'var(--muted)', transform: checked ? 'translateX(20px)' : 'translateX(2px)' }} />
    </button>
  );

  const FIELD_SUGGESTIONS = ['api_key', 'api_secret', 'token', 'client_id', 'client_secret', 'affiliate_id', 'base_url', 'webhook_url'];

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

  return (
    <div className="space-y-4" data-testid="store-integrations-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Gerencie credenciais e dados de conexao de lojas e parceiros</p>
        {!adding && (
          <button onClick={() => { resetForm(); setAdding(true); }} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg font-bold"
            style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="add-store-btn">
            <Plus size={12} /> Nova loja
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {adding && (
        <div className="rounded-lg p-4 space-y-3" style={{ background: 'var(--surface)', border: '1px solid var(--sv-gold-primary)' }} data-testid="store-form">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium" style={{ color: 'var(--sv-gold-primary)' }}>{editingId ? 'Editar loja' : 'Nova loja'}</span>
            <button onClick={resetForm} className="p-1 rounded" style={{ color: 'var(--status-red)' }}><XCircle size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>Nome da loja *</label>
              <input type="text" value={form.store_name} onChange={e => setForm(p => ({ ...p, store_name: e.target.value }))}
                placeholder="Ex: Amazon, Magalu, KaBuM..." className="w-full text-sm py-2 px-3 rounded-lg"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="store-name-input" />
            </div>
            <div>
              <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>Tipo</label>
              <select value={form.store_type} onChange={e => setForm(p => ({ ...p, store_type: e.target.value }))}
                className="w-full text-sm py-2 px-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                data-testid="store-type-select">
                <option value="custom">Custom</option>
                <option value="marketplace">Marketplace</option>
                <option value="affiliate">Afiliado</option>
                <option value="crawler_source">Fonte Crawler</option>
                <option value="payment">Pagamento</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>Notas</label>
            <input type="text" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Observacoes opcionais..." className="w-full text-sm py-2 px-3 rounded-lg"
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="store-notes-input" />
          </div>

          {/* Credential fields */}
          <div>
            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>Credenciais {editingId ? '(preencha apenas os campos a atualizar)' : ''}</label>
            <div className="space-y-2">
              {Object.entries(form.credentials).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="text-[10px] w-28 text-right truncate" style={{ color: 'var(--muted-2)' }}>{k}</span>
                  <input type="text" value={v} onChange={e => updateCredField(k, e.target.value)}
                    placeholder={`Valor de ${k}...`} className="flex-1 text-sm py-1.5 px-2 rounded"
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                    data-testid={`cred-input-${k}`} />
                  <button onClick={() => removeCredField(k)} className="p-1 rounded" style={{ color: 'var(--status-red)' }}><XCircle size={12} /></button>
                </div>
              ))}
            </div>
            {/* Add field */}
            <div className="flex items-center gap-2 mt-2">
              <input type="text" value={newFieldKey} onChange={e => setNewFieldKey(e.target.value)}
                placeholder="Nome do campo..." className="flex-1 text-xs py-1.5 px-2 rounded"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                onKeyDown={e => e.key === 'Enter' && addField()} data-testid="new-field-input" />
              <button onClick={addField} className="px-2 py-1.5 rounded text-[10px] font-bold" style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }}
                data-testid="add-field-btn">+ Campo</button>
            </div>
            {/* Quick add suggestions */}
            <div className="flex flex-wrap gap-1 mt-2">
              {FIELD_SUGGESTIONS.filter(f => !(f in form.credentials)).map(f => (
                <button key={f} onClick={() => updateCredField(f, '')} className="text-[9px] px-1.5 py-0.5 rounded"
                  style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }}>{f}</button>
              ))}
            </div>
          </div>

          <button onClick={handleSave} disabled={saving}
            className="w-full py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:opacity-90"
            style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="save-store-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {editingId ? 'Atualizar loja' : 'Criar loja'}
          </button>
        </div>
      )}

      {/* Store List */}
      {stores.length === 0 && !adding ? (
        <div className="text-center py-8" style={{ color: 'var(--muted)' }}>
          <Store size={28} className="mx-auto mb-2" />
          <p className="text-sm">Nenhuma loja cadastrada</p>
          <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Adicione lojas para gerenciar suas credenciais de integracao</p>
        </div>
      ) : (
        <div className="space-y-2">
          {stores.map(store => (
            <div key={store.store_id} className="rounded-lg p-3" style={{ background: 'var(--surface)', border: `1px solid ${store.active ? 'var(--border)' : '#2a1a1a'}` }}
              data-testid={`store-card-${store.store_id}`}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ background: store.active ? 'var(--gold-bg)' : 'var(--border)', color: store.active ? 'var(--sv-gold-primary)' : 'var(--muted)', border: '1px solid var(--border-hover)' }}>
                  {(store.store_name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium" style={{ color: store.active ? 'var(--text)' : 'var(--muted)' }}>{store.store_name}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'var(--bg)', color: 'var(--muted)' }}>{store.store_type}</span>
                  </div>
                  {/* Masked credentials */}
                  <div className="flex flex-wrap gap-2 mt-1">
                    {Object.entries(store.credentials_masked || {}).map(([k, v]) => (
                      <span key={k} className="text-[9px]" style={{ color: v ? 'var(--muted-2)' : 'var(--surface-3)' }}>
                        {k}: <code style={{ color: v ? 'var(--sv-gold-primary)' : 'var(--surface-3)' }}>{v || '(vazio)'}</code>
                      </span>
                    ))}
                  </div>
                  {store.notes && <div className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>{store.notes}</div>}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <ToggleSwitch checked={!!store.active} onChange={() => handleToggle(store)} testId={`toggle-store-${store.store_id}`} />
                  <button onClick={() => startEdit(store)} className="p-1.5 rounded hover:bg-[rgba(128,128,128,0.08)]" style={{ color: 'var(--sv-gold-primary)' }} data-testid={`edit-store-${store.store_id}`}>
                    <Edit2 size={13} />
                  </button>
                  <button onClick={() => handleDelete(store.store_id, store.store_name)} className="p-1.5 rounded hover:bg-[rgba(128,128,128,0.08)]" style={{ color: 'var(--status-red)' }}
                    data-testid={`delete-store-${store.store_id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// =====================================================================
// EXTERNAL PUBLISH MANAGER
// =====================================================================

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', color: 'var(--muted-2)', bg: 'var(--border)' },
  review: { label: 'Revisao', color: 'var(--status-amber)', bg: 'var(--gold-bg)' },
  approved: { label: 'Aprovado', color: 'var(--status-green)', bg: '#0a1a0a' },
  published: { label: 'Publicado', color: 'var(--status-blue)', bg: '#0a0a1a' },
  error: { label: 'Erro', color: 'var(--status-red)', bg: '#1a0a0a' },
  discarded: { label: 'Descartado', color: 'var(--muted)', bg: 'var(--surface)' },
};

const NEXT_STATUS = { draft: 'review', review: 'approved', approved: 'published' };

function ExternalPublishManager({ token }) {
  const [subTab, setSubTab] = useState('queue');
  const [queue, setQueue] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [dealSearch, setDealSearch] = useState('');
  const [addingPlatform, setAddingPlatform] = useState('');
  const [addingDealId, setAddingDealId] = useState('');
  const [queueFilter, setQueueFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [previewItem, setPreviewItem] = useState(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({ platform: 'promobit', account_name: '', account_url: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [qualityConfig, setQualityConfig] = useState({ quality_score_min: 70, block_below_min: true, allow_override: true });
  const [overrideLogs, setOverrideLogs] = useState([]);

  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
  const jsonHeaders = token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  const loadQueue = useCallback(async () => {
    try {
      let url = `${API}/admin/master/external-queue?limit=50`;
      if (queueFilter) url += `&status=${queueFilter}`;
      if (platformFilter) url += `&platform=${platformFilter}`;
      const res = await fetch(url, { headers });
      if (res.ok) { const d = await res.json(); setQueue(d.items || []); }
    } catch (e) { /* ignore */ }
  }, [token, queueFilter, platformFilter]);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/external-accounts`, { headers });
      if (res.ok) { const d = await res.json(); setAccounts(d.accounts || []); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/external-publish-log?limit=50`, { headers });
      if (res.ok) { const d = await res.json(); setLogs(d.logs || []); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadQualityConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/quality-config`, { headers });
      if (res.ok) { const d = await res.json(); setQualityConfig(d.config || { quality_score_min: 70, block_below_min: true, allow_override: true }); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadOverrideLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/quality-override-log?limit=30`, { headers });
      if (res.ok) { const d = await res.json(); setOverrideLogs(d.logs || []); }
    } catch (e) { /* ignore */ }
  }, [token]);

  const saveQualityConfig = async (cfg) => {
    try {
      const res = await fetch(`${API}/admin/master/quality-config`, {
        method: 'PUT', headers: jsonHeaders, body: JSON.stringify(cfg),
      });
      if (res.ok) { toast.success('Config de qualidade salva'); setQualityConfig(cfg); }
      else { const d = await res.json().catch(() => ({})); toast.error(d?.detail || 'Erro'); }
    } catch (e) { toast.error(normalizeAppError(e).message); }
  };

  const handleOverride = async (queueId, targetStatus = 'approved') => {
    const reason = window.prompt('Motivo do override (opcional):') || '';
    try {
      const res = await fetch(`${API}/admin/master/external-queue/${queueId}/override`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ target_status: targetStatus, reason }),
      });
      if (res.ok) { toast.success('Override aplicado'); loadQueue(); loadOverrideLogs(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d?.detail || 'Erro no override'); }
    } catch (e) { toast.error(normalizeAppError(e).message); }
  };

  const searchDeals = async (q) => {
    try {
      const res = await fetch(`${API}/admin/master/telegram-preview-deals?q=${encodeURIComponent(q)}&limit=15`, { headers });
      if (res.ok) { const d = await res.json(); setDeals(d.deals || []); }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadQueue(), loadAccounts(), loadLogs(), loadQualityConfig(), loadOverrideLogs()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadQueue(); }, [queueFilter, platformFilter]);

  const handleAddToQueue = async () => {
    if (!addingDealId || !addingPlatform) { toast.error('Selecione oferta e plataforma'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/master/external-queue`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ deal_id: addingDealId, platform: addingPlatform }),
      });
      const data = await res.json();
      if (res.ok) { toast.success('Adicionado a fila'); setAddingDealId(''); loadQueue(); }
      else toast.error(data.detail || 'Erro');
    } catch (e) { toast.error('Erro de conexao'); }
    setBusy(false);
  };

  const handleStatusChange = async (queueId, newStatus, errorDetail = '') => {
    try {
      const res = await fetch(`${API}/admin/master/external-queue/${queueId}/status`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ status: newStatus, error_detail: errorDetail }),
      });
      if (res.ok) { toast.success(`Status: ${STATUS_CONFIG[newStatus]?.label}`); loadQueue(); loadLogs(); }
      else {
        const d = await res.json();
        if (res.status === 422) {
          toast.error(d.detail || 'Bloqueado por qualidade');
        } else {
          toast.error(d.detail || 'Erro');
        }
      }
    } catch (e) { toast.error(normalizeAppError(e).message); }
  };

  const handleAddAccount = async () => {
    if (!accountForm.account_name.trim()) { toast.error('Nome/identificador da conta obrigatorio'); return; }
    if (!accountForm.platform) { toast.error('Selecione uma plataforma'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/master/external-accounts`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify(accountForm),
      });
      if (res.ok) { toast.success('Conta adicionada com sucesso'); setShowAddAccount(false); setAccountForm({ platform: 'promobit', account_name: '', account_url: '', notes: '' }); loadAccounts(); }
      else {
        let msg = 'Erro ao salvar conta';
        try {
          const d = await res.json();
          if (res.status === 409) msg = d.detail || 'Conta duplicada — ja existe com esse identificador';
          else if (res.status === 403) msg = 'Sem permissao. Apenas Admin Master pode salvar contas.';
          else if (res.status === 400) msg = d.detail || 'Dados invalidos. Verifique os campos.';
          else msg = d.detail || msg;
        } catch(_) {}
        toast.error(msg);
      }
    } catch (e) { toast.error('Erro de conexao ao salvar conta'); }
    setBusy(false);
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Remover conta?')) return;
    try {
      const res = await fetch(`${API}/admin/master/external-accounts/${accountId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Conta removida'); loadAccounts(); }
    } catch (e) { toast.error(normalizeAppError(e).message); }
  };

  const loadPreview = async (item) => {
    try {
      const res = await fetch(`${API}/admin/master/external-queue/${item.queue_id}/format`, {
        method: 'POST', headers: jsonHeaders,
      });
      if (res.ok) { const d = await res.json(); setPreviewItem({ ...item, formatted: d.formatted }); }
    } catch (e) { toast.error('Erro ao gerar preview'); }
  };

  const copyText = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copiado!`); };

  const SUB_TABS = [
    { key: 'queue', label: 'Fila', icon: Clock },
    { key: 'quality', label: 'Qualidade', icon: Shield },
    { key: 'accounts', label: 'Contas', icon: Key },
    { key: 'history', label: 'Historico', icon: FileText },
  ];

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

  return (
    <div className="space-y-4" data-testid="external-publish-panel">
      {/* Sub-tabs */}
      <div className="flex gap-1 p-1 rounded-lg overflow-x-auto" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors whitespace-nowrap"
            style={{ background: subTab === t.key ? 'var(--gold-bg)' : 'transparent', color: subTab === t.key ? 'var(--sv-gold-primary)' : 'var(--muted)', border: subTab === t.key ? '1px solid var(--sv-gold-primary)' : '1px solid transparent' }}
            data-testid={`ext-subtab-${t.key}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* QUEUE Sub-tab */}
      {subTab === 'queue' && (
        <div className="space-y-3" data-testid="ext-queue-panel">
          {/* Add to queue */}
          <div className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Plus size={13} style={{ color: 'var(--sv-gold-primary)' }} />
              <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>Adicionar oferta a fila</span>
            </div>
            <div className="flex gap-2 mb-2">
              <input type="text" placeholder="Buscar oferta..." value={dealSearch}
                onChange={e => setDealSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchDeals(dealSearch)}
                className="flex-1 px-3 py-1.5 rounded text-xs" data-testid="ext-deal-search"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} />
              <button onClick={() => searchDeals(dealSearch)} className="px-3 py-1.5 rounded text-xs"
                style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }}>
                <Search size={12} />
              </button>
            </div>
            {deals.length > 0 && (
              <div className="max-h-28 overflow-y-auto space-y-0.5 rounded mb-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                {deals.map(d => (
                  <button key={d.deal_id} onClick={() => setAddingDealId(d.deal_id)}
                    className="w-full text-left px-2.5 py-1.5 flex justify-between items-center hover:opacity-80"
                    style={{ background: addingDealId === d.deal_id ? 'var(--gold-bg)' : 'transparent', borderBottom: '1px solid var(--border)' }}
                    data-testid={`ext-select-deal-${d.deal_id}`}>
                    <span className="text-[11px] truncate flex-1" style={{ color: 'var(--text)' }}>{d.title}</span>
                    <span className="text-[10px] ml-2 shrink-0" style={{ color: 'var(--sv-gold-primary)' }}>
                      {d.discount_percent > 0 ? `-${d.discount_percent}%` : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-center">
              <select value={addingPlatform} onChange={e => setAddingPlatform(e.target.value)}
                className="px-3 py-1.5 rounded text-xs" data-testid="ext-platform-select"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                <option value="">Plataforma...</option>
                <option value="promobit">Promobit</option>
                <option value="pelando">Pelando</option>
              </select>
              <button onClick={handleAddToQueue} disabled={busy || !addingDealId || !addingPlatform}
                className="px-4 py-1.5 rounded text-xs font-bold flex items-center gap-1.5 disabled:opacity-40"
                style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="ext-add-queue-btn">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Adicionar
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {['', 'draft', 'review', 'approved', 'published', 'error'].map(s => (
              <button key={s} onClick={() => setQueueFilter(s)}
                className="text-[10px] px-2 py-1 rounded transition-colors" data-testid={`ext-filter-${s || 'all'}`}
                style={{ background: queueFilter === s ? 'var(--gold-bg)' : 'var(--bg)', color: queueFilter === s ? 'var(--sv-gold-primary)' : 'var(--muted)', border: `1px solid ${queueFilter === s ? 'var(--sv-gold-primary)' : 'var(--border)'}` }}>
                {s ? STATUS_CONFIG[s]?.label : 'Todos'}
              </button>
            ))}
            <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)}
              className="text-[10px] px-2 py-1 rounded ml-auto"
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted-2)' }}>
              <option value="">Todas plataformas</option>
              <option value="promobit">Promobit</option>
              <option value="pelando">Pelando</option>
            </select>
          </div>

          {/* Queue items */}
          {queue.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--muted)' }}>
              <Clock size={28} className="mx-auto mb-2" />
              <p className="text-sm">Fila vazia</p>
              <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Adicione ofertas para publicacao assistida</p>
            </div>
          ) : (
            <div className="space-y-2">
              {queue.map(item => {
                const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;
                return (
                  <div key={item.queue_id} className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`queue-item-${item.queue_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{item.title}</div>
                        <div className="flex flex-wrap gap-2 mt-1 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                          <span style={{ color: 'var(--sv-gold-primary)' }}>{item.platform_name}</span>
                          {item.price > 0 && <span>R$ {item.price.toFixed(2)}</span>}
                          {item.discount_percent > 0 && <span style={{ color: 'var(--status-green)' }}>-{item.discount_percent}%</span>}
                          {item.coupon_code && <span>Cupom: {item.coupon_code}</span>}
                          {item.store_name && <span>Loja: {item.store_name}</span>}
                        </div>
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded shrink-0 font-medium" style={{ background: sc.bg, color: sc.color }}>
                        {sc.label}
                      </span>
                    </div>
                    {/* Quality + missing + blocked */}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {(() => {
                        const s = item.quality_score || 0;
                        const blocked = qualityConfig.block_below_min && s < qualityConfig.quality_score_min && !item.quality_override;
                        const scoreColor = s >= 70 ? '#10B981' : s >= 50 ? '#F59E0B' : '#EF4444';
                        const scoreBg = s >= 70 ? 'var(--status-success-bg)' : s >= 50 ? 'var(--gold-bg)' : 'var(--status-error-bg)';
                        return (
                          <>
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: scoreBg, color: scoreColor, border: `1px solid ${scoreColor}30` }}
                              data-testid={`score-badge-${item.queue_id}`}>
                              {s}/100
                            </span>
                            {blocked && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: 'var(--status-error-bg)', color: 'var(--status-red)', border: '1px solid var(--status-error-border)' }}
                                data-testid={`blocked-badge-${item.queue_id}`}>
                                Bloqueado (min: {qualityConfig.quality_score_min})
                              </span>
                            )}
                            {item.quality_override && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: 'var(--gold-bg)', color: 'var(--status-amber)', border: '1px solid var(--status-warning-border)' }}
                                data-testid={`override-badge-${item.queue_id}`}>
                                Override por {item.quality_override_by}
                              </span>
                            )}
                          </>
                        );
                      })()}
                      {item.missing_fields?.length > 0 && (
                        <span className="text-[10px]" style={{ color: 'var(--status-red)' }}>Faltam: {item.missing_fields.join(', ')}</span>
                      )}
                    </div>
                    {/* Actions */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <button onClick={() => loadPreview(item)}
                        className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1"
                        style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid={`ext-preview-${item.queue_id}`}>
                        <Eye size={10} /> Preview
                      </button>
                      {NEXT_STATUS[item.status] && (
                        <button onClick={() => handleStatusChange(item.queue_id, NEXT_STATUS[item.status])}
                          className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1"
                          style={{ background: STATUS_CONFIG[NEXT_STATUS[item.status]]?.bg, color: STATUS_CONFIG[NEXT_STATUS[item.status]]?.color, border: '1px solid var(--border-hover)' }}
                          data-testid={`ext-advance-${item.queue_id}`}>
                          <CheckCircle size={10} /> {STATUS_CONFIG[NEXT_STATUS[item.status]]?.label}
                        </button>
                      )}
                      {/* Override button: shows when blocked */}
                      {qualityConfig.block_below_min && (item.quality_score || 0) < qualityConfig.quality_score_min && !item.quality_override && qualityConfig.allow_override && item.status !== 'published' && item.status !== 'discarded' && (
                        <button onClick={() => handleOverride(item.queue_id, NEXT_STATUS[item.status] || 'approved')}
                          className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1"
                          style={{ background: 'var(--gold-bg)', color: 'var(--status-amber)', border: '1px solid var(--status-warning-border)' }} data-testid={`ext-override-${item.queue_id}`}>
                          <AlertTriangle size={10} /> Aprovar mesmo assim
                        </button>
                      )}
                      {item.status !== 'published' && item.status !== 'discarded' && (
                        <button onClick={() => handleStatusChange(item.queue_id, 'discarded')}
                          className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1"
                          style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }} data-testid={`ext-discard-${item.queue_id}`}>
                          <Ban size={10} /> Descartar
                        </button>
                      )}
                      {item.status === 'error' && (
                        <button onClick={() => handleStatusChange(item.queue_id, 'draft')}
                          className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1"
                          style={{ background: 'var(--surface-2)', color: 'var(--muted-2)', border: '1px solid var(--border)' }}>
                          <RefreshCw size={10} /> Retentar
                        </button>
                      )}
                    </div>
                    <div className="text-[9px] mt-1.5" style={{ color: 'var(--muted)' }}>
                      {item.created_at ? new Date(item.created_at).toLocaleString('pt-BR') : ''}
                      {item.created_by ? ` | ${item.created_by}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Preview Modal */}
          {previewItem && previewItem.formatted && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--bg)', border: '2px solid var(--sv-accent)' }} data-testid="ext-preview-modal">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Globe size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--sv-gold-primary)' }}>Preview — {previewItem.formatted.platform_name}</span>
                </div>
                <button onClick={() => setPreviewItem(null)} style={{ color: 'var(--muted)' }}><XCircle size={16} /></button>
              </div>

              {/* Platform preview card */}
              <div className="rounded-lg overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="px-3 py-2 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: previewItem.platform === 'promobit' ? '#FF6B35' : '#00B894', color: 'var(--text)' }}>
                    {previewItem.formatted.platform_name}
                  </span>
                  <span className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Preview da oferta</span>
                </div>
                {previewItem.formatted.image_url && (
                  <div style={{ maxHeight: 160, overflow: 'hidden' }}>
                    <img src={previewItem.formatted.image_url} alt="" className="w-full object-cover" style={{ maxHeight: 160 }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}
                <div className="p-3 space-y-2">
                  <div className="text-sm font-bold" style={{ color: 'var(--text)' }}>{previewItem.formatted.title}</div>
                  <div className="flex items-center gap-2">
                    {previewItem.formatted.original_price > previewItem.formatted.price && (
                      <span className="text-xs line-through" style={{ color: 'var(--muted-2)' }}>R$ {previewItem.formatted.original_price.toFixed(2)}</span>
                    )}
                    <span className="text-base font-bold" style={{ color: 'var(--status-green)' }}>R$ {previewItem.formatted.price.toFixed(2)}</span>
                    {previewItem.formatted.discount_percent > 0 && (
                      <span className="text-[10px] px-1 py-0.5 rounded font-bold" style={{ background: '#10B981', color: 'var(--text)' }}>-{previewItem.formatted.discount_percent}%</span>
                    )}
                  </div>
                  {previewItem.formatted.coupon_code && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] px-2 py-0.5 rounded border border-dashed" style={{ color: 'var(--sv-gold-primary)', borderColor: 'var(--sv-gold-primary)' }}>
                        {previewItem.formatted.coupon_code}
                      </span>
                    </div>
                  )}
                  <div className="text-[10px]" style={{ color: 'var(--muted-2)' }}>
                    Loja: {previewItem.formatted.store_name || 'N/A'} | Cat: {previewItem.formatted.category || 'Outros'}
                  </div>
                  {previewItem.formatted.affiliate_disclosure && (
                    <div className="text-[9px] italic" style={{ color: 'var(--muted)' }}>{previewItem.formatted.affiliate_disclosure}</div>
                  )}
                </div>
              </div>

              {/* Copy buttons */}
              <div className="flex flex-wrap gap-2">
                <button onClick={() => copyText(previewItem.formatted.title, 'Titulo')}
                  className="px-2.5 py-1.5 rounded text-[10px] font-medium flex items-center gap-1"
                  style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid="ext-copy-title">
                  <Copy size={10} /> Titulo
                </button>
                <button onClick={() => copyText(previewItem.formatted.description, 'Descricao')}
                  className="px-2.5 py-1.5 rounded text-[10px] font-medium flex items-center gap-1"
                  style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid="ext-copy-desc">
                  <Copy size={10} /> Descricao
                </button>
                <button onClick={() => copyText(previewItem.formatted.link, 'Link')}
                  className="px-2.5 py-1.5 rounded text-[10px] font-medium flex items-center gap-1"
                  style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid="ext-copy-link">
                  <Copy size={10} /> Link
                </button>
                <button onClick={() => copyText(JSON.stringify({ title: previewItem.formatted.title, description: previewItem.formatted.description, price: previewItem.formatted.price, original_price: previewItem.formatted.original_price, coupon: previewItem.formatted.coupon_code, store: previewItem.formatted.store_name, link: previewItem.formatted.link, category: previewItem.formatted.category, image: previewItem.formatted.image_url }, null, 2), 'Payload')}
                  className="px-2.5 py-1.5 rounded text-[10px] font-medium flex items-center gap-1"
                  style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid="ext-copy-payload">
                  <Copy size={10} /> Payload Completo
                </button>
                {previewItem.formatted.url_create && (
                  <a href={previewItem.formatted.url_create} target="_blank" rel="noopener noreferrer"
                    className="px-2.5 py-1.5 rounded text-[10px] font-bold flex items-center gap-1"
                    style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }} data-testid="ext-open-platform">
                    <ExternalLink size={10} /> Abrir {previewItem.formatted.platform_name}
                  </a>
                )}
              </div>

              {/* Debug info */}
              <div className="rounded p-2 space-y-1" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <div className="text-[10px] font-medium mb-1" style={{ color: 'var(--sv-gold-primary)' }}>Debug</div>
                {[
                  { l: 'Score', v: String(previewItem.formatted.quality_score), c: previewItem.formatted.quality_score >= 70 ? '#10B981' : '#F59E0B' },
                  { l: 'Campos faltando', v: previewItem.formatted.missing_fields?.join(', ') || 'Nenhum', c: previewItem.formatted.missing_fields?.length ? '#EF4444' : '#10B981' },
                  { l: 'Limite titulo', v: `${previewItem.formatted.title?.length || 0}/${previewItem.formatted.title_max}`, c: 'var(--muted-2)' },
                  { l: 'Limite descricao', v: `${previewItem.formatted.description?.length || 0}/${previewItem.formatted.description_max}`, c: 'var(--muted-2)' },
                ].map((r, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <span className="text-[9px] w-28 shrink-0" style={{ color: 'var(--muted)' }}>{r.l}</span>
                    <span className="text-[9px]" style={{ color: r.c }}>{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* QUALITY Sub-tab */}
      {subTab === 'quality' && (
        <div className="space-y-4" data-testid="ext-quality-panel">
          {/* Config */}
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Shield size={14} style={{ color: 'var(--sv-gold-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--sv-gold-primary)' }}>Configuracao de Qualidade</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Score minimo</label>
                <input type="number" min="0" max="100" value={qualityConfig.quality_score_min}
                  onChange={e => setQualityConfig(p => ({ ...p, quality_score_min: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-1.5 rounded text-sm" data-testid="quality-min-input"
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={qualityConfig.block_below_min}
                    onChange={e => setQualityConfig(p => ({ ...p, block_below_min: e.target.checked }))}
                    className="accent-[var(--sv-accent)]" data-testid="quality-block-checkbox" />
                  <span className="text-xs" style={{ color: 'var(--text)' }}>Bloquear abaixo do minimo</span>
                </label>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={qualityConfig.allow_override}
                    onChange={e => setQualityConfig(p => ({ ...p, allow_override: e.target.checked }))}
                    className="accent-[var(--sv-accent)]" data-testid="quality-override-checkbox" />
                  <span className="text-xs" style={{ color: 'var(--text)' }}>Permitir override manual</span>
                </label>
              </div>
            </div>
            <button onClick={() => saveQualityConfig(qualityConfig)}
              className="mt-3 px-4 py-1.5 rounded text-xs font-bold" data-testid="quality-save-btn"
              style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}>
              <Save size={12} className="inline mr-1" /> Salvar
            </button>
          </div>

          {/* Score scale visual */}
          <div className="rounded-lg p-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="text-[10px] font-medium mb-2" style={{ color: 'var(--muted-2)' }}>Escala de pontuacao (total: 100)</div>
            <div className="space-y-1">
              {[
                { f: 'Titulo', p: 15 }, { f: 'Imagem', p: 15 }, { f: 'Preco atual', p: 15 },
                { f: 'Preco original', p: 10 }, { f: 'Desconto', p: 10 }, { f: 'Link principal', p: 15 },
                { f: 'Details URL', p: 10 }, { f: 'Loja', p: 5 }, { f: 'Cupom', p: 5 }, { f: 'Consistencia', p: 10 },
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[10px] w-24 shrink-0" style={{ color: 'var(--muted-2)' }}>{r.f}</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                    <div className="h-full rounded-full" style={{ width: `${r.p}%`, background: r.p >= 15 ? 'var(--sv-gold-primary)' : 'var(--muted)' }} />
                  </div>
                  <span className="text-[10px] w-5 text-right" style={{ color: 'var(--sv-gold-primary)' }}>+{r.p}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3">
              <span className="text-[10px] flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#EF4444' }} /> 0-49 Baixo</span>
              <span className="text-[10px] flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#F59E0B' }} /> 50-69 Medio</span>
              <span className="text-[10px] flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} /> 70-100 Bom</span>
            </div>
          </div>

          {/* Override Logs */}
          <div className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium" style={{ color: 'var(--status-amber)' }}>Overrides recentes ({overrideLogs.length})</span>
              <button onClick={loadOverrideLogs} className="text-[10px] px-2 py-0.5 rounded" style={{ color: 'var(--sv-gold-primary)', background: 'var(--gold-bg)' }}>
                <RefreshCw size={10} className="inline mr-1" /> Atualizar
              </button>
            </div>
            {overrideLogs.length === 0 ? (
              <p className="text-[10px] py-4 text-center" style={{ color: 'var(--muted)' }}>Nenhum override registrado</p>
            ) : (
              <div className="space-y-1.5">
                {overrideLogs.map((log, i) => (
                  <div key={log.log_id || i} className="rounded p-2 flex items-start justify-between" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }} data-testid={`override-log-${i}`}>
                    <div>
                      <div className="text-[11px] font-medium" style={{ color: 'var(--text)' }}>{log.title}</div>
                      <div className="text-[9px]" style={{ color: 'var(--muted-2)' }}>
                        Score: <span style={{ color: 'var(--status-red)' }}>{log.score}</span> / Min: {log.threshold} | Por: {log.operator}
                        {log.reason && <span> | Motivo: {log.reason}</span>}
                      </div>
                    </div>
                    <span className="text-[9px] shrink-0" style={{ color: 'var(--muted)' }}>{log.timestamp ? new Date(log.timestamp).toLocaleString('pt-BR') : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* ACCOUNTS Sub-tab */}
      {subTab === 'accounts' && (
        <div className="space-y-3" data-testid="ext-accounts-panel">
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Contas autorizadas ({accounts.length})</p>
            <button onClick={() => setShowAddAccount(true)}
              className="text-xs flex items-center gap-1 px-3 py-1.5 rounded font-medium"
              style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="ext-add-account-btn">
              <Plus size={12} /> Nova conta
            </button>
          </div>

          {showAddAccount && (
            <div className="rounded-lg p-3 space-y-2" style={{ background: 'var(--surface)', border: '1px solid var(--sv-gold-primary)' }}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>Nova conta</span>
                <button onClick={() => setShowAddAccount(false)} style={{ color: 'var(--muted)' }}><XCircle size={14} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={accountForm.platform} onChange={e => setAccountForm(p => ({ ...p, platform: e.target.value }))}
                  className="text-xs py-1.5 px-2 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ext-acc-platform">
                  <option value="promobit">Promobit</option>
                  <option value="pelando">Pelando</option>
                </select>
                <input type="text" value={accountForm.account_name} onChange={e => setAccountForm(p => ({ ...p, account_name: e.target.value }))}
                  placeholder="Nome da conta" className="text-xs py-1.5 px-2 rounded"
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ext-acc-name" />
              </div>
              <input type="text" value={accountForm.account_url} onChange={e => setAccountForm(p => ({ ...p, account_url: e.target.value }))}
                placeholder="URL do perfil (opcional)" className="w-full text-xs py-1.5 px-2 rounded"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ext-acc-url" />
              <input type="text" value={accountForm.notes} onChange={e => setAccountForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Notas (opcional)" className="w-full text-xs py-1.5 px-2 rounded"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ext-acc-notes" />
              <button onClick={handleAddAccount} disabled={busy}
                className="px-4 py-1.5 rounded text-xs font-bold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="ext-acc-save">
                {busy ? <Loader2 size={12} className="animate-spin mx-auto" /> : 'Salvar'}
              </button>
            </div>
          )}

          {accounts.length === 0 && !showAddAccount ? (
            <div className="text-center py-8" style={{ color: 'var(--muted)' }}>
              <Key size={28} className="mx-auto mb-2" />
              <p className="text-sm">Nenhuma conta cadastrada</p>
              <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>Cadastre contas autorizadas para publicacao</p>
            </div>
          ) : (
            <div className="space-y-2">
              {accounts.map(acc => (
                <div key={acc.account_id} className="rounded-lg p-3 flex items-center gap-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`ext-account-${acc.account_id}`}>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0"
                    style={{ background: acc.platform === 'promobit' ? '#FF6B35' : '#00B894', color: 'var(--text)' }}>
                    {acc.platform_name}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium" style={{ color: 'var(--text)' }}>{acc.account_name}</div>
                    <div className="flex gap-3 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                      <span>Publicados: {acc.total_published || 0}</span>
                      {acc.last_published_at && <span>Ultima: {new Date(acc.last_published_at).toLocaleString('pt-BR')}</span>}
                    </div>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: acc.status === 'active' ? '#0a1a0a' : '#1a0a0a', color: acc.status === 'active' ? '#10B981' : '#EF4444' }}>
                    {acc.status === 'active' ? 'Ativa' : 'Inativa'}
                  </span>
                  <button onClick={() => handleDeleteAccount(acc.account_id)} className="p-1 rounded hover:bg-[rgba(128,128,128,0.08)]" style={{ color: 'var(--status-red)' }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HISTORY Sub-tab */}
      {subTab === 'history' && (
        <div className="space-y-3" data-testid="ext-history-panel">
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Historico de publicacoes externas ({logs.length})</p>
            <button onClick={loadLogs} className="text-xs flex items-center gap-1 px-2 py-1 rounded" style={{ color: 'var(--sv-gold-primary)', background: 'var(--gold-bg)' }}>
              <RefreshCw size={12} /> Atualizar
            </button>
          </div>
          {logs.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--muted)' }}>
              <FileText size={28} className="mx-auto mb-2" />
              <p className="text-sm">Nenhuma publicacao registrada</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, i) => {
                const isErr = log.status === 'error';
                return (
                  <div key={log.log_id || i} className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`ext-log-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium truncate pr-2" style={{ color: 'var(--text)' }}>{log.title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                        style={{ background: isErr ? '#1a0a0a' : '#0a0a1a', color: isErr ? '#EF4444' : '#3B82F6' }}>
                        {isErr ? 'Erro' : 'Publicado'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                      <span style={{ color: 'var(--sv-gold-primary)' }}>{log.platform_name}</span>
                      <span>Por: {log.operator}</span>
                      {log.timestamp && <span>{new Date(log.timestamp).toLocaleString('pt-BR')}</span>}
                    </div>
                    {isErr && log.error_detail && <div className="text-[10px] mt-1" style={{ color: 'var(--status-red)' }}>{log.error_detail}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function WeeklyReportManager({ token }) {
  const [subTab, setSubTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState({ enabled: false, day_of_week: 5, hour: 9, channels: ['email'], recipients: [], chat_id: '' });
  const [access, setAccess] = useState(false);
  const [plan, setPlan] = useState('free');
  const [history, setHistory] = useState([]);
  const [sendResult, setSendResult] = useState(null);
  const [newRecipient, setNewRecipient] = useState('');
  const [deliveryModal, setDeliveryModal] = useState(false);
  const [deliverySetting, setDeliverySetting] = useState(null);
  const [savingDelivery, setSavingDelivery] = useState(false);

  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
  const jsonHeaders = token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  const DAY_NAMES = ['Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado', 'Domingo'];

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ponto/weekly-report/config`, { headers });
      if (res.ok) {
        const d = await res.json();
        setConfig(d.config || { enabled: false, day_of_week: 5, hour: 9, channels: ['email'], recipients: [], chat_id: '' });
        setAccess(d.access);
        setPlan(d.plan);
      }
    } catch (e) { /* ignore */ }
  }, [token]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ponto/weekly-report/history?limit=20`, { headers });
      if (res.ok) {
        const d = await res.json();
        setHistory(Array.isArray(d) ? d : []);
      }
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadConfig(), loadHistory()]);
      setLoading(false);
    };
    init();
  }, [loadConfig, loadHistory]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/ponto/weekly-report/config`, {
        method: 'PUT', headers: jsonHeaders, body: JSON.stringify(config),
      });
      if (res.ok) { const d = await res.json(); setConfig(d.config); toast.success('Configuracao salva'); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Erro ao salvar'); }
    } catch (e) { toast.error('Erro de rede'); }
    setSaving(false);
  };

  const sendNow = async () => {
    setSending(true); setSendResult(null);
    try {
      const res = await fetch(`${API}/ponto/weekly-report/send`, { method: 'POST', headers });
      if (res.ok) { const d = await res.json(); setSendResult(d); toast.success('Relatorio enviado'); loadHistory(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Erro ao enviar'); }
    } catch (e) { toast.error('Erro de rede'); }
    setSending(false);
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/ponto/weekly-report/download`, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'relatorio_ops_semanal.pdf';
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
        toast.success('PDF baixado');
      } else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Erro ao gerar PDF'); }
    } catch (e) { toast.error('Erro de rede'); }
  };

  const toggleChannel = (ch) => {
    setConfig(prev => ({ ...prev, channels: prev.channels.includes(ch) ? prev.channels.filter(c => c !== ch) : [...prev.channels, ch] }));
  };
  const addRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (config.recipients.includes(email)) { toast.error('Ja adicionado'); return; }
    setConfig(prev => ({ ...prev, recipients: [...prev.recipients, email] }));
    setNewRecipient('');
  };
  const removeRecipient = (email) => setConfig(prev => ({ ...prev, recipients: prev.recipients.filter(r => r !== email) }));

  const openDeliveryModal = async () => {
    setDeliveryModal(true);
    try {
      const res = await fetch(`${API}/admin/master/report-delivery-settings/emp_23384031`, { headers });
      if (res.ok) { const d = await res.json(); setDeliverySetting(d.setting); }
    } catch (e) { /* ignore */ }
  };
  const saveDelivery = async () => {
    if (!deliverySetting) return;
    setSavingDelivery(true);
    try {
      const res = await fetch(`${API}/admin/master/report-delivery-settings/${deliverySetting.empresa_id}`, {
        method: 'PUT', headers: jsonHeaders, body: JSON.stringify(deliverySetting),
      });
      if (res.ok) { toast.success('Config de recebimento salva'); setDeliveryModal(false); }
      else { toast.error('Erro ao salvar'); }
    } catch (e) { toast.error('Erro de rede'); }
    setSavingDelivery(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12" data-testid="reports-loading">
      <Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} />
    </div>
  );

  if (!access) return (
    <div className="text-center py-12" data-testid="reports-blocked">
      <Ban size={28} className="mx-auto mb-2" style={{ color: 'var(--status-red)' }} />
      <p className="text-sm" style={{ color: 'var(--muted-2)' }}>Relatorios nao disponiveis no plano <strong style={{ color: 'var(--sv-gold-primary)' }}>{plan}</strong></p>
      <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Disponivel nos planos Gestao, Pro e Enterprise</p>
    </div>
  );

  const SUB_TABS = [
    { key: 'config', label: 'Configuracao' },
    { key: 'send', label: 'Enviar Agora' },
    { key: 'history', label: 'Historico' },
    { key: 'download', label: 'Download PDF' },
    { key: 'delivery', label: 'Recebimento' },
  ];

  return (
    <div className="space-y-4" data-testid="reports-panel">
      <div className="flex gap-1 flex-wrap">
        {SUB_TABS.map(st => (
          <button key={st.key} onClick={() => st.key === 'delivery' ? openDeliveryModal() : setSubTab(st.key)}
            className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
            style={{
              background: subTab === st.key && st.key !== 'delivery' ? 'var(--gold-bg)' : 'var(--bg)',
              color: subTab === st.key && st.key !== 'delivery' ? 'var(--sv-gold-primary)' : 'var(--muted-2)',
              border: subTab === st.key && st.key !== 'delivery' ? '1px solid var(--sv-gold-primary)' : '1px solid var(--border)',
            }}
            data-testid={`report-subtab-${st.key}`}
          >
            {st.label}
          </button>
        ))}
      </div>

      {subTab === 'config' && (
        <div className="space-y-4" data-testid="report-config-panel">
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Envio Automatico Semanal</span>
              <button onClick={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
                className="w-10 h-5 rounded-full transition-colors relative"
                style={{ background: config.enabled ? 'var(--sv-gold-primary)' : 'var(--surface-3)' }}
                data-testid="report-toggle-enabled"
              >
                <div className="w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all" style={{ left: config.enabled ? '22px' : '2px' }} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--muted-2)' }}>Dia da Semana</label>
                <select value={config.day_of_week} onChange={e => setConfig(prev => ({ ...prev, day_of_week: parseInt(e.target.value) }))}
                  className="w-full rounded px-2 py-1.5 text-xs" style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-hover)' }} data-testid="report-day-select">
                  {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--muted-2)' }}>Hora (UTC)</label>
                <select value={config.hour} onChange={e => setConfig(prev => ({ ...prev, hour: parseInt(e.target.value) }))}
                  className="w-full rounded px-2 py-1.5 text-xs" style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-hover)' }} data-testid="report-hour-select">
                  {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs mb-2" style={{ color: 'var(--muted-2)' }}>Canais de Envio</label>
              <div className="flex gap-2">
                {['email', 'telegram'].map(ch => (
                  <button key={ch} onClick={() => toggleChannel(ch)} className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
                    style={{ background: config.channels.includes(ch) ? 'var(--gold-bg)' : 'var(--bg)', color: config.channels.includes(ch) ? 'var(--sv-gold-primary)' : 'var(--muted)', border: config.channels.includes(ch) ? '1px solid var(--sv-gold-primary)' : '1px solid #333' }}
                    data-testid={`report-channel-${ch}`}>{ch === 'email' ? 'Email' : 'Telegram'}</button>
                ))}
                <span className="px-3 py-1.5 rounded text-xs" style={{ background: 'var(--bg)', color: 'var(--surface-3)', border: '1px solid var(--border)' }}>WhatsApp (em breve)</span>
              </div>
            </div>
            {config.channels.includes('email') && (
              <div className="mt-4">
                <label className="block text-xs mb-2" style={{ color: 'var(--muted-2)' }}>Destinatarios de Email</label>
                <div className="flex gap-2 mb-2">
                  <input type="email" value={newRecipient} onChange={e => setNewRecipient(e.target.value)} onKeyDown={e => e.key === 'Enter' && addRecipient()}
                    placeholder="email@exemplo.com" className="flex-1 rounded px-2 py-1.5 text-xs" style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-hover)' }} data-testid="report-email-input" />
                  <button onClick={addRecipient} className="px-3 py-1.5 rounded text-xs font-medium" style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid="report-add-email-btn"><Plus size={12} /></button>
                </div>
                {config.recipients.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {config.recipients.map((email, i) => (
                      <span key={i} className="flex items-center gap-1 px-2 py-1 rounded text-xs" style={{ background: 'var(--bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }}>
                        {email}
                        <button onClick={() => removeRecipient(email)} data-testid={`report-remove-email-${i}`}><XCircle size={10} style={{ color: 'var(--status-red)' }} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            {config.channels.includes('telegram') && (
              <div className="mt-4">
                <label className="block text-xs mb-1" style={{ color: 'var(--muted-2)' }}>Chat ID do Telegram (opcional, usa padrao se vazio)</label>
                <input type="text" value={config.chat_id} onChange={e => setConfig(prev => ({ ...prev, chat_id: e.target.value }))}
                  placeholder="Ex: 964787263" className="w-full rounded px-2 py-1.5 text-xs" style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-hover)' }} data-testid="report-chatid-input" />
              </div>
            )}
            <button onClick={saveConfig} disabled={saving} className="mt-4 flex items-center gap-2 px-4 py-2 rounded text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="report-save-config-btn">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar Configuracao
            </button>
          </div>
        </div>
      )}

      {subTab === 'send' && (
        <div className="space-y-4" data-testid="report-send-panel">
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>Enviar Relatorio Operacional Agora</p>
            <p className="text-xs mb-4" style={{ color: 'var(--muted-2)' }}>Gera PDF com dados de ofertas (extraidas, importadas, quality score, top canais/lojas/cupons) e envia pelos canais configurados + chat global (964787263).</p>
            <button onClick={sendNow} disabled={sending} className="flex items-center gap-2 px-4 py-2 rounded text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="report-send-now-btn">
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {sending ? 'Enviando...' : 'Enviar Relatorio'}
            </button>
          </div>
          {sendResult && (
            <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="report-send-result">
              <p className="text-xs font-medium mb-3" style={{ color: 'var(--sv-gold-primary)' }}>Resultado do Envio</p>
              <div className="space-y-1 mb-3">
                {Object.entries(sendResult.results || {}).map(([ch, ok]) => (
                  <div key={ch} className="flex items-center gap-2 text-xs">
                    {ok === true ? <CheckCircle size={12} style={{ color: 'var(--status-green)' }} /> : ok === 'not_implemented' ? <Clock size={12} style={{ color: 'var(--muted)' }} /> : <XCircle size={12} style={{ color: 'var(--status-red)' }} />}
                    <span style={{ color: ok === true ? '#10B981' : ok === 'not_implemented' ? 'var(--muted)' : '#EF4444' }}>
                      {ch === 'telegram_global' ? 'Telegram Global (964787263)' : ch}: {ok === true ? 'Enviado' : ok === 'not_implemented' ? 'Em breve' : 'Falhou'}
                    </span>
                  </div>
                ))}
              </div>
              {sendResult.summary && (
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: 'Extraidas', value: sendResult.summary.total_extracted, color: 'var(--sv-gold-primary)' },
                    { label: 'Importadas', value: sendResult.summary.total_imported, color: 'var(--status-green)' },
                    { label: 'Aprovadas', value: sendResult.summary.total_approved, color: 'var(--status-blue)' },
                    { label: 'Descartadas', value: sendResult.summary.total_discarded, color: 'var(--status-red)' },
                    { label: 'Bloqueadas', value: sendResult.summary.total_blocked, color: 'var(--status-amber)' },
                    { label: 'Score Medio', value: sendResult.summary.avg_score, color: 'var(--sv-gold-primary)' },
                    { label: 'Taxa Aprov.', value: `${sendResult.summary.approval_rate}%`, color: 'var(--status-green)' },
                    { label: 'Deals Ativas', value: sendResult.summary.total_deals_active, color: 'var(--status-blue)' },
                  ].map((kpi, i) => (
                    <div key={i} className="rounded p-2 text-center" style={{ background: 'var(--bg)' }}>
                      <div className="text-sm font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
                      <div className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{kpi.label}</div>
                    </div>
                  ))}
                </div>
              )}
              {sendResult.summary?.top_channels?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-medium mb-1" style={{ color: 'var(--muted-2)' }}>Top Canais</p>
                  <div className="flex flex-wrap gap-1">
                    {sendResult.summary.top_channels.map(([ch, cnt], i) => (
                      <span key={i} className="px-2 py-0.5 rounded text-[10px]" style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }}>{ch}: {cnt}</span>
                    ))}
                  </div>
                </div>
              )}
              {sendResult.summary?.top_stores?.length > 0 && (
                <div className="mt-2">
                  <p className="text-[10px] font-medium mb-1" style={{ color: 'var(--muted-2)' }}>Top Lojas</p>
                  <div className="flex flex-wrap gap-1">
                    {sendResult.summary.top_stores.map(([st, cnt], i) => (
                      <span key={i} className="px-2 py-0.5 rounded text-[10px]" style={{ background: 'var(--bg)', color: 'var(--status-green)', border: '1px solid var(--border-hover)' }}>{st}: {cnt}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {subTab === 'history' && (
        <div className="space-y-3" data-testid="report-history-panel">
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Historico de envios ({history.length})</p>
            <button onClick={loadHistory} className="text-xs flex items-center gap-1 px-2 py-1 rounded" style={{ color: 'var(--sv-gold-primary)', background: 'var(--gold-bg)' }} data-testid="report-refresh-history">
              <RefreshCw size={12} /> Atualizar
            </button>
          </div>
          {history.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--muted)' }}><FileText size={28} className="mx-auto mb-2" /><p className="text-sm">Nenhum relatorio enviado</p></div>
          ) : (
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={h.report_id || i} className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`report-history-row-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>{h.date || 'N/A'} {h.report_type === 'operational' ? '(Operacional)' : ''}</span>
                    <span className="text-[10px]" style={{ color: 'var(--muted)' }}>{h.sent_at ? new Date(h.sent_at).toLocaleString('pt-BR') : ''}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px]">
                    {Object.entries(h.results || {}).map(([ch, ok]) => (
                      <span key={ch} className="flex items-center gap-1">
                        {ok === true ? <CheckCircle size={10} style={{ color: 'var(--status-green)' }} /> : <XCircle size={10} style={{ color: 'var(--status-red)' }} />}
                        <span style={{ color: ok === true ? '#10B981' : '#EF4444' }}>{ch === 'telegram_global' ? 'Global' : ch}</span>
                      </span>
                    ))}
                  </div>
                  {h.summary && (
                    <div className="flex gap-4 mt-1 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                      {h.summary.total_extracted !== undefined ? (
                        <>
                          <span>Ext: {h.summary.total_extracted}</span>
                          <span>Imp: {h.summary.total_imported}</span>
                          <span>Score: {h.summary.avg_score}</span>
                        </>
                      ) : (
                        <>
                          <span>{h.summary.total_colabs} colabs</span>
                          <span>{h.summary.total_regs} regs</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === 'download' && (
        <div className="space-y-4" data-testid="report-download-panel">
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>Download PDF Operacional</p>
            <p className="text-xs mb-4" style={{ color: 'var(--muted-2)' }}>Gera PDF com KPIs de ofertas, top canais, top lojas, top cupons e status operacional dos ultimos 7 dias.</p>
            <button onClick={downloadPdf} className="flex items-center gap-2 px-4 py-2 rounded text-xs font-medium" style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }} data-testid="report-download-consolidado">
              <Download size={12} /> Baixar Relatorio Operacional
            </button>
          </div>
        </div>
      )}

      {/* Modal Delivery Settings */}
      {deliveryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setDeliveryModal(false)}>
          <div className="rounded-lg p-5 w-full max-w-lg mx-4" style={{ background: 'var(--surface)', border: '1px solid var(--sv-gold-primary)' }} onClick={e => e.stopPropagation()} data-testid="delivery-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold" style={{ color: 'var(--sv-gold-primary)' }}>Configurar Recebimento de Relatorios</h3>
              <button onClick={() => setDeliveryModal(false)}><XCircle size={16} style={{ color: 'var(--muted)' }} /></button>
            </div>
            {deliverySetting ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--text)' }}>Ativar Recebimento Semanal</span>
                  <button onClick={() => setDeliverySetting(prev => ({ ...prev, weekly_report_enabled: !prev.weekly_report_enabled }))}
                    className="w-10 h-5 rounded-full transition-colors relative" style={{ background: deliverySetting.weekly_report_enabled ? 'var(--sv-gold-primary)' : 'var(--surface-3)' }} data-testid="delivery-toggle">
                    <div className="w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all" style={{ left: deliverySetting.weekly_report_enabled ? '22px' : '2px' }} />
                  </button>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--muted-2)' }}>Canais Preferidos</label>
                  <div className="flex gap-2">
                    {['telegram', 'email'].map(ch => (
                      <button key={ch} onClick={() => {
                        const chs = deliverySetting.preferred_channels || [];
                        setDeliverySetting(prev => ({ ...prev, preferred_channels: chs.includes(ch) ? chs.filter(c => c !== ch) : [...chs, ch] }));
                      }} className="px-3 py-1.5 rounded text-xs font-medium"
                        style={{ background: (deliverySetting.preferred_channels || []).includes(ch) ? 'var(--gold-bg)' : 'var(--bg)', color: (deliverySetting.preferred_channels || []).includes(ch) ? 'var(--sv-gold-primary)' : 'var(--muted)', border: (deliverySetting.preferred_channels || []).includes(ch) ? '1px solid var(--sv-gold-primary)' : '1px solid #333' }}
                        data-testid={`delivery-ch-${ch}`}>{ch === 'email' ? 'Email' : 'Telegram'}</button>
                    ))}
                    <span className="px-3 py-1.5 rounded text-xs" style={{ background: 'var(--bg)', color: 'var(--surface-3)', border: '1px solid var(--border)' }}>WhatsApp (futuro)</span>
                  </div>
                </div>
                <div className="rounded-lg p-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="text-xs font-medium mb-2" style={{ color: 'var(--sv-gold-primary)' }}>Telegram</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] mb-1" style={{ color: 'var(--muted-2)' }}>Chat ID</label>
                      <input type="text" value={deliverySetting.telegram?.chat_id || ''} onChange={e => setDeliverySetting(prev => ({ ...prev, telegram: { ...prev.telegram, chat_id: e.target.value } }))}
                        className="w-full rounded px-2 py-1.5 text-xs" style={{ background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border-hover)' }} data-testid="delivery-tg-chatid" placeholder="964787263" />
                    </div>
                    <div>
                      <label className="block text-[10px] mb-1" style={{ color: 'var(--muted-2)' }}>Username (opcional)</label>
                      <input type="text" value={deliverySetting.telegram?.username || ''} onChange={e => setDeliverySetting(prev => ({ ...prev, telegram: { ...prev.telegram, username: e.target.value } }))}
                        className="w-full rounded px-2 py-1.5 text-xs" style={{ background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border-hover)' }} data-testid="delivery-tg-username" placeholder="@douglasPaco" />
                    </div>
                  </div>
                </div>
                <div className="rounded-lg p-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)', opacity: 0.5 }}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium" style={{ color: 'var(--muted-2)' }}>WhatsApp (estrutura futura)</p>
                    <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>Em breve</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] mb-1" style={{ color: 'var(--muted)' }}>Telefone</label>
                      <input type="text" value={deliverySetting.whatsapp?.phone || ''} onChange={e => setDeliverySetting(prev => ({ ...prev, whatsapp: { ...prev.whatsapp, phone: e.target.value } }))}
                        className="w-full rounded px-2 py-1.5 text-xs" style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }} data-testid="delivery-wa-phone" placeholder="+55 11 99999-9999" />
                    </div>
                    <div className="flex items-end">
                      <span className="text-[10px] pb-2" style={{ color: 'var(--muted)' }}>Ativacao futura sem refazer config</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: 'var(--muted-2)' }}>Dia do Envio</label>
                    <select value={deliverySetting.schedule?.day_of_week ?? 5} onChange={e => setDeliverySetting(prev => ({ ...prev, schedule: { ...prev.schedule, day_of_week: parseInt(e.target.value) } }))}
                      className="w-full rounded px-2 py-1.5 text-xs" style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-hover)' }} data-testid="delivery-day">
                      {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: 'var(--muted-2)' }}>Hora</label>
                    <input type="text" value={deliverySetting.schedule?.time || '09:00'} onChange={e => setDeliverySetting(prev => ({ ...prev, schedule: { ...prev.schedule, time: e.target.value } }))}
                      className="w-full rounded px-2 py-1.5 text-xs" style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-hover)' }} data-testid="delivery-time" placeholder="09:00" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--muted-2)' }}>Observacoes Internas</label>
                  <textarea value={deliverySetting.notes || ''} onChange={e => setDeliverySetting(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full rounded px-2 py-1.5 text-xs h-16 resize-none" style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-hover)' }} data-testid="delivery-notes" />
                </div>
                <button onClick={saveDelivery} disabled={savingDelivery} className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="delivery-save-btn">
                  {savingDelivery ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar Config de Recebimento
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8"><Loader2 size={16} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}




function BelvoTestButton({ token }) {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const test = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/openfinance/belvo-status`, { headers });
      if (res.ok) {
        const d = await res.json();
        setStatus(d);
        if (d.credentials_valid) toast.success('Belvo conectado com sucesso!');
        else if (d.configured) toast.error('Credenciais configuradas mas invalidas');
        else toast.error('Belvo nao configurado. Preencha Secret ID e Password.');
      } else {
        toast.error('Erro ao testar Belvo');
      }
    } catch (e) { toast.error('Erro de conexao'); }
    setTesting(false);
  };

  return (
    <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
      <div className="flex items-center gap-3">
        <button onClick={test} disabled={testing}
          className="px-3 py-1.5 rounded text-xs font-bold flex items-center gap-2 hover:opacity-90"
          style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }} data-testid="belvo-test-btn">
          {testing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
          Testar Conexao Belvo
        </button>
        {status && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{
              background: status.credentials_valid ? 'var(--status-success-bg)' : 'var(--status-error-bg)',
              color: status.credentials_valid ? '#10B981' : '#EF4444',
            }}>
              {status.credentials_valid ? 'Conectado' : status.configured ? 'Invalido' : 'Nao configurado'}
            </span>
            <span className="text-[10px]" style={{ color: 'var(--muted)' }}>
              {status.env} | {status.demo_mode ? 'Demo' : 'Producao'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

const TOUR_STEPS = {
  integrations: [
    { title: 'Chaves de Integracao', text: 'Gerencie tokens e credenciais das integracoes externas (Telegram, Stripe, Facebook, etc). Valores sao mascarados por seguranca.' },
    { title: 'Editar valores', text: 'Clique em qualquer chave para editar. As alteracoes sao registradas na aba Auditoria.' },
  ],
  telegram: [
    { title: 'Telegram+ Avancado', text: 'Configure o bot, canais, formato das mensagens e monitore a saude do envio automatico.' },
    { title: 'Sub-abas', text: 'Saude = status geral, Preview = simule envios, Config = credenciais + formato, Canais = lista de canais, Logs = historico, Extraidas = ofertas captadas.' },
  ],
  external: [
    { title: 'Publicacao Externa', text: 'Gerencie a fila de publicacao assistida para Promobit e Pelando.' },
    { title: 'Fluxo', text: 'Fila = ofertas pendentes, Qualidade = score minimo, Contas = credenciais, Historico = publicacoes feitas.' },
  ],
  social: [
    { title: 'Login Social', text: 'Ative ou desative provedores de login (Google, Facebook, Apple) que aparecem na tela de login.' },
    { title: 'Configuracao', text: 'Google ja vem configurado. Para Facebook, informe o App ID e Secret. Toggle ativa/desativa instantaneamente.' },
  ],
  reports: [
    { title: 'Relatorios Semanais', text: 'Configure e envie relatorios operacionais automaticos via Email e Telegram.' },
    { title: 'Sub-abas', text: 'Config = parametros, Enviar Agora = disparo manual, Historico = envios anteriores, Recebimento = quem recebe.' },
  ],
  stores: [
    { title: 'Lojas', text: 'Integracoes com lojas parceiras e configuracoes de scraping.' },
  ],
  funnel: [
    { title: 'Funil de Conversao', text: 'Acompanhe views, cliques e redirects das ofertas. Top 20 dos ultimos 30 dias.' },
  ],
  keywords: [
    { title: 'Keywords', text: 'Keywords automaticas detectadas + manuais. Use "Recalcular" para atualizar a lista.' },
  ],
  app: [
    { title: 'Download App', text: 'Configure links e disponibilidade do app mobile para Android e iOS.' },
  ],
  audit: [
    { title: 'Auditoria', text: 'Historico de todas as alteracoes em configuracoes do sistema, com autor e data.' },
  ],
};

function MiniTour({ tabKey }) {
  const steps = TOUR_STEPS[tabKey];
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const storageKey = `tour_seen_${tabKey}`;

  useEffect(() => {
    if (!localStorage.getItem(storageKey)) setVisible(true);
    setStep(0);
  }, [tabKey]);

  if (!visible || !steps || !steps.length) return null;

  const dismiss = () => { localStorage.setItem(storageKey, '1'); setVisible(false); };
  const current = steps[step];

  return (
    <div className="rounded-lg p-3 mb-3 flex items-start gap-3" style={{ background: 'var(--gold-bg)', border: '1px solid var(--sv-gold-soft)' }} data-testid={`tour-${tabKey}`}>
      <div className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: 'var(--sv-accent)', color: 'var(--sv-on-accent)' }}>
        {step + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold mb-0.5" style={{ color: 'var(--sv-gold-primary)' }}>{current.title}</div>
        <div className="text-[11px]" style={{ color: 'var(--text)' }}>{current.text}</div>
        <div className="flex items-center gap-2 mt-2">
          {step < steps.length - 1 ? (
            <button onClick={() => setStep(s => s + 1)} className="text-[10px] px-2 py-1 rounded font-medium" style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }} data-testid="tour-next">
              Proximo
            </button>
          ) : (
            <button onClick={dismiss} className="text-[10px] px-2 py-1 rounded font-medium" style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }} data-testid="tour-finish">
              Entendi
            </button>
          )}
          <button onClick={dismiss} className="text-[10px] px-2 py-1 rounded" style={{ color: 'var(--muted)' }} data-testid="tour-skip">
            Pular
          </button>
          {steps.length > 1 && (
            <span className="text-[9px] ml-auto" style={{ color: 'var(--muted)' }}>{step + 1}/{steps.length}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ====================================================================
// MARKETPLACES HUB
// ====================================================================

const PROVIDER_META = {
  mercadolivre: { color: '#FFE600', initial: 'ML', tagline: 'OAuth2 — busca e importacao de ofertas' },
  shopee: { color: '#EE4D2D', initial: 'SH', tagline: 'Shopee Afiliados Open API — AppID e Secret' },
  amazon: { color: '#FF9900', initial: 'AZ', tagline: 'Affiliate API — integracao planejada' },
  aliexpress: { color: '#E43225', initial: 'AE', tagline: 'Marketplace global — integracao planejada' },
  magalu: { color: '#0086FF', initial: 'MG', tagline: 'Marketplace nacional — integracao planejada' },
  terabyte: { color: '#00B4D8', initial: 'TB', tagline: 'Loja tech — crawler planejado' },
  pichau: { color: '#7B2D8E', initial: 'PC', tagline: 'Loja tech — crawler planejado' },
  kabum: { color: '#FF6600', initial: 'KB', tagline: 'Loja tech — crawler planejado' },
  dafiti: { color: '#1A1A2E', initial: 'DF', tagline: 'Moda — integracao planejada' },
  shein: { color: '#222', initial: 'SN', tagline: 'Fast fashion global — integracao planejada' },
  netshoes: { color: '#E91E63', initial: 'NS', tagline: 'Esportes — integracao planejada' },
  samsung: { color: '#1428A0', initial: 'SS', tagline: 'Eletronicos — integracao planejada' },
  decathlon: { color: '#0082C3', initial: 'DC', tagline: 'Esportes — crawler planejado' },
  nike: { color: '#111', initial: 'NK', tagline: 'Esportes — integracao planejada' },
  fastshop: { color: '#E3001B', initial: 'FS', tagline: 'Eletronicos — crawler planejado' },
  sephora: { color: '#000', initial: 'SP', tagline: 'Beleza — integracao planejada' },
  adidas: { color: '#000', initial: 'AD', tagline: 'Esportes — integracao planejada' },
};

const STATUS_LABELS = {
  connected: { label: 'Conectado', color: '#10B981', bg: 'rgba(16,185,129,0.1)' },
  configured: { label: 'Configurado', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
  not_configured: { label: 'Nao configurado', color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
  planned: { label: 'Em breve', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
};

function MarketplacesHub({ token }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [authFields, setAuthFields] = useState({});
  // Inline credentials state per-provider (replaces modal for non-ML/Shopee providers)
  const [inlineCreds, setInlineCreds] = useState({});       // { [providerId]: { ...values } }
  const [inlineCredsLoading, setInlineCredsLoading] = useState({}); // { [providerId]: bool }
  const [inlineCredsSaving, setInlineCredsSaving] = useState({});   // { [providerId]: bool }
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const loadProviders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/marketplaces/providers`, { headers });
      if (res.ok) {
        const data = await res.json();
        setProviders(Array.isArray(data.providers) ? data.providers : []);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadProviders();
    fetch(`${API}/marketplaces/auth-fields`, { headers }).then(r => r.ok ? r.json() : {}).then(d => setAuthFields(d || {})).catch(() => {});
  }, [loadProviders]);

  const loadInlineCreds = async (providerId) => {
    if (inlineCreds[providerId] !== undefined) return;
    setInlineCredsLoading(prev => ({ ...prev, [providerId]: true }));
    try {
      const r = await fetch(`${API}/marketplaces/${providerId}/credentials`, { headers });
      if (r.ok) {
        const d = await r.json();
        setInlineCreds(prev => ({ ...prev, [providerId]: d.credentials || {} }));
      } else {
        setInlineCreds(prev => ({ ...prev, [providerId]: {} }));
      }
    } catch {
      setInlineCreds(prev => ({ ...prev, [providerId]: {} }));
    }
    setInlineCredsLoading(prev => ({ ...prev, [providerId]: false }));
  };

  const toggleExpand = (providerId) => {
    const willOpen = expandedProvider !== providerId;
    setExpandedProvider(willOpen ? providerId : null);
    if (willOpen && providerId !== 'mercadolivre' && providerId !== 'shopee') {
      loadInlineCreds(providerId);
    }
  };

  const saveInlineCredentials = async (providerId) => {
    setInlineCredsSaving(prev => ({ ...prev, [providerId]: true }));
    try {
      const r = await fetch(`${API}/marketplaces/${providerId}/credentials`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(inlineCreds[providerId] || {}),
      });
      if (r.ok) {
        await loadProviders();
        if (typeof toast !== 'undefined' && toast.success) toast.success('Credenciais salvas');
      } else {
        if (typeof toast !== 'undefined' && toast.error) toast.error('Falha ao salvar credenciais');
      }
    } catch {
      if (typeof toast !== 'undefined' && toast.error) toast.error('Erro de rede ao salvar');
    }
    setInlineCredsSaving(prev => ({ ...prev, [providerId]: false }));
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

  return (
    <div className="space-y-4" data-testid="marketplaces-hub">
      {/* Header with yellow button */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs" style={{ color: 'var(--muted-2)' }}>
            {providers.filter(p => p.status === 'connected' || p.status === 'configured').length} configurado(s) de {providers.length} lojas
          </p>
        </div>
        <button
          data-testid="add-store-btn"
          onClick={() => {
            const next = providers.find(p => p.status !== 'configured' && p.status !== 'connected');
            if (next) toggleExpand(next.id);
          }}
          className="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-105"
          style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
        >
          + Adicionar Loja
        </button>
      </div>

      {/* Unified Providers List — single accordion pattern for ALL providers */}
      <div className="space-y-2" data-testid="providers-list">
        {providers.map(p => {
          const meta = PROVIDER_META[p.id] || { color: '#888', initial: '?', tagline: '' };
          const st = STATUS_LABELS[p.status] || STATUS_LABELS.planned;
          const isExpanded = expandedProvider === p.id;
          const isConnected = p.status === 'connected';
          const hasManager = p.id === 'mercadolivre' || p.id === 'shopee';
          const fields = authFields[p.auth_type] || [];
          const curValues = inlineCreds[p.id] || {};
          const isLoadingCreds = !!inlineCredsLoading[p.id];
          const isSavingCreds = !!inlineCredsSaving[p.id];
          return (
            <div
              key={p.id}
              className="rounded-xl overflow-hidden transition-colors"
              style={{
                background: 'var(--surface)',
                border: `1px solid ${isConnected ? '#10B98140' : isExpanded ? meta.color + '40' : 'var(--border)'}`,
              }}
              data-testid={`provider-card-${p.id}`}
            >
              {/* Compact header — always visible */}
              <div
                className="flex items-center gap-3 p-3.5 cursor-pointer hover:bg-[var(--bg)]/30 transition-colors"
                onClick={() => toggleExpand(p.id)}
                data-testid={`provider-header-${p.id}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 relative"
                  style={{
                    background: isConnected ? 'rgba(16,185,129,0.12)' : `${meta.color}15`,
                    color: isConnected ? '#10B981' : meta.color,
                    border: `1px solid ${isConnected ? '#10B98130' : meta.color + '30'}`,
                  }}
                >
                  {meta.initial}
                  {isConnected && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ background: '#10B981' }}>
                      <CheckCircle size={8} style={{ color: '#fff' }} />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{p.name}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full whitespace-nowrap" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap" style={{ background: 'var(--bg)', color: 'var(--muted)' }}>
                      {p.auth_type === 'crawler' ? 'Crawler' : p.auth_type}
                    </span>
                  </div>
                  <p className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--muted)' }}>{meta.tagline}</p>
                  {p.connected_account && (
                    <p className="text-[10px] mt-0.5 truncate" style={{ color: '#10B981' }}>Conta: {p.connected_account}</p>
                  )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-1.5">
                  {isConnected && (
                    <div className="p-1 rounded" style={{ background: 'var(--bg)' }} title="Editar configuracao" data-testid={`edit-btn-${p.id}`}>
                      <Edit2 size={13} style={{ color: 'var(--muted)' }} />
                    </div>
                  )}
                  {isExpanded
                    ? <ChevronDown size={16} style={{ color: 'var(--muted)' }} />
                    : <ChevronRight size={16} style={{ color: 'var(--muted)' }} />}
                </div>
              </div>

              {/* Expandable body — hidden by default */}
              {isExpanded && (
                <div className="border-t" style={{ borderColor: 'var(--border)' }} data-testid={`provider-body-${p.id}`}>
                  {p.id === 'mercadolivre' && (
                    <div className="p-1">
                      <MercadoLivreManager token={token} onStatusChange={loadProviders} />
                    </div>
                  )}
                  {p.id === 'shopee' && (
                    <div className="p-1">
                      <ShopeeManager token={token} onStatusChange={loadProviders} />
                    </div>
                  )}
                  {!hasManager && (
                    <div className="p-4 space-y-3">
                      {isLoadingCreds ? (
                        <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--muted)' }}>
                          <Loader2 size={12} className="animate-spin" /> Carregando configuracao...
                        </div>
                      ) : (
                        <>
                          {fields.length === 0 ? (
                            <p className="text-[11px]" style={{ color: 'var(--muted)' }}>
                              {p.status === 'planned'
                                ? 'Integracao planejada. Em breve sera possivel conectar esta loja.'
                                : 'Nenhuma credencial necessaria para este tipo de integracao.'}
                            </p>
                          ) : (
                            <div className="space-y-3">
                              {fields.map(f => (
                                <div key={f.key}>
                                  <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--muted)' }}>
                                    {f.label} {f.required && <span style={{ color: '#EF4444' }}>*</span>}
                                  </label>
                                  <input
                                    data-testid={`cred-input-${p.id}-${f.key}`}
                                    type={f.type === 'password' ? 'password' : 'text'}
                                    className="w-full px-3 py-2 rounded-lg text-xs"
                                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                                    placeholder={f.label}
                                    value={curValues[f.key] || ''}
                                    onChange={e => setInlineCreds(prev => ({
                                      ...prev,
                                      [p.id]: { ...(prev[p.id] || {}), [f.key]: e.target.value },
                                    }))}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          {fields.length > 0 && (
                            <div className="flex items-center justify-end gap-2 pt-1">
                              <button
                                onClick={() => setExpandedProvider(null)}
                                className="px-3 py-1.5 rounded-lg text-[11px]"
                                style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }}
                              >
                                Cancelar
                              </button>
                              <button
                                data-testid={`save-credentials-btn-${p.id}`}
                                onClick={() => saveInlineCredentials(p.id)}
                                disabled={isSavingCreds}
                                className="px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:scale-105"
                                style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)', opacity: isSavingCreds ? 0.6 : 1 }}
                              >
                                {isSavingCreds ? 'Salvando...' : 'Salvar credenciais'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info */}
      <div className="rounded-lg p-3" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
        <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
          <strong style={{ color: 'var(--text)' }}>Marketplaces:</strong> Clique em qualquer loja para expandir e configurar credenciais. Providers com OAuth exigem autorizacao. Crawlers coletam dados publicos.
        </p>
      </div>
    </div>
  );
}

// ====================================================================
// MERCADO LIVRE MANAGER
// ====================================================================
function MercadoLivreManager({ token, onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [mlTab, setMlTab] = useState('deals');
  const [dealsQuery, setDealsQuery] = useState('');
  const [dealsCat, setDealsCat] = useState('');
  const [dealsResults, setDealsResults] = useState([]);
  const [dealsSearching, setDealsSearching] = useState(false);
  const [dealsSelected, setDealsSelected] = useState({});
  const [dealsImporting, setDealsImporting] = useState(false);
  const [dealsSource, setDealsSource] = useState('');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/mercadolivre/status`, { headers });
      if (res.ok) setStatus(await res.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const connect = async () => {
    try {
      const res = await fetch(`${API}/mercadolivre/auth-url`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (!data.auth_url) { toast.error('URL de auth vazia'); return; }
        const popup = window.open(data.auth_url, 'ml_auth', 'width=600,height=700');
        if (!popup || popup.closed) {
          window.location.href = data.auth_url;
          return;
        }
        const check = setInterval(() => {
          if (popup.closed) { clearInterval(check); setTimeout(() => { loadStatus(); onStatusChange?.(); }, 1000); }
        }, 500);
      } else {
        const err = await res.json().catch(() => ({}));
        console.error('[ML auth-url]', res.status, err);
        toast.error(err.detail || `Erro ${res.status} ao gerar URL de auth`);
      }
    } catch (e) { console.error('[ML connect]', e); toast.error('Erro de conexao com o servidor'); }
  };

  const disconnect = async () => {
    if (!window.confirm('Desconectar Mercado Livre?')) return;
    await fetch(`${API}/mercadolivre/disconnect`, { method: 'POST', headers });
    setStatus(null);
    loadStatus();
    onStatusChange?.();
    toast.success('Desconectado');
  };

  const refreshToken = async () => {
    try {
      const res = await fetch(`${API}/mercadolivre/refresh-token`, { method: 'POST', headers });
      if (res.ok) { toast.success('Token renovado'); loadStatus(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d?.detail || 'Erro'); }
    } catch (e) { toast.error('Erro ao renovar'); }
  };

  const loadCategories = async () => {
    try {
      const res = await fetch(`${API}/mercadolivre/categories`, { headers });
      if (res.ok) { const d = await res.json(); setCategories(Array.isArray(d.categories) ? d.categories : []); }
    } catch (e) { /* ignore */ }
  };

  const searchProducts = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      if (searchCategory) params.set('category', searchCategory);
      params.set('limit', '20');
      const res = await fetch(`${API}/mercadolivre/search?${params}`, { headers });
      if (res.ok) {
        const d = await res.json();
        if (d.error) {
          toast.error(d.error);
          setResults([]);
        } else {
          setResults(Array.isArray(d.items) ? d.items : []);
        }
      }
    } catch (e) { toast.error('Erro na busca'); }
    setSearching(false);
  };

  const toggleSelect = (mlId) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[mlId]) delete next[mlId]; else next[mlId] = true;
      return next;
    });
  };

  const importSelected = async () => {
    const items = results.filter(r => selected[r.ml_id]);
    if (!items.length) { toast.error('Selecione itens'); return; }
    setImporting(true);
    try {
      const res = await fetch(`${API}/mercadolivre/import-to-system`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${d.imported} importadas, ${d.updated || 0} atualizadas`);
        setSelected({});
      } else { toast.error('Erro ao importar'); }
    } catch (e) { toast.error(normalizeAppError(e).message); }
    setImporting(false);
  };

  const searchDeals = async () => {
    setDealsSearching(true);
    try {
      const params = new URLSearchParams();
      if (dealsQuery) params.set('q', dealsQuery);
      if (dealsCat) params.set('category', dealsCat);
      params.set('limit', '20');
      const res = await fetch(`${API}/mercadolivre/deals?${params}`, { headers });
      if (res.ok) {
        const d = await res.json();
        if (d.error) { toast.error(d.error); setDealsResults([]); }
        else { setDealsResults(Array.isArray(d.items) ? d.items : []); setDealsSource(d.source || ''); }
      }
    } catch (e) { toast.error('Erro na busca de ofertas'); }
    setDealsSearching(false);
  };

  const toggleDealsSelect = (mlId) => {
    setDealsSelected(prev => { const n = { ...prev }; if (n[mlId]) delete n[mlId]; else n[mlId] = true; return n; });
  };

  const importDeals = async () => {
    const items = dealsResults.filter(r => dealsSelected[r.ml_id]);
    if (!items.length) { toast.error('Selecione ofertas'); return; }
    setDealsImporting(true);
    try {
      const res = await fetch(`${API}/mercadolivre/import-to-system`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${d.imported} publicadas no site + Telegram, ${d.updated || 0} atualizadas`);
        setDealsSelected({});
      } else { toast.error('Erro ao importar'); }
    } catch (e) { toast.error(normalizeAppError(e).message); }
    setDealsImporting(false);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

  return (
    <div className="space-y-4" data-testid="ml-manager">
      {/* Connection Status */}
      <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShoppingCart size={16} style={{ color: 'var(--sv-gold-primary)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Mercado Livre</span>
          </div>
          <div className="flex items-center gap-2">
            {status?.connected ? (
              <>
                <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'var(--status-success-bg)', color: '#10B981' }}>Conectado</span>
                <button onClick={refreshToken} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid="ml-refresh-token">
                  <RefreshCw size={12} />
                </button>
                <button onClick={disconnect} className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }} data-testid="ml-disconnect">
                  <Unplug size={12} />
                </button>
              </>
            ) : (
              <button onClick={connect} className="text-xs px-3 py-1.5 rounded font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="ml-connect">
                <Link2 size={12} className="inline mr-1" /> Conectar
              </button>
            )}
          </div>
        </div>
        {status?.connected && (
          <div className="flex items-center gap-4 text-xs flex-wrap" style={{ color: 'var(--muted)' }}>
            <span>Conta: <strong style={{ color: 'var(--text)' }}>{status.ml_nickname}</strong></span>
            <span>Token: {status.token_valid ? <span style={{ color: '#10B981' }}>Valido</span> : <span style={{ color: '#EF4444' }}>Expirado</span>}</span>
            {status.ml_blocked && (
              <span className="px-2 py-0.5 rounded" style={{ background: 'rgba(234,179,8,0.1)', color: '#EAB308', border: '1px solid rgba(234,179,8,0.2)' }} data-testid="ml-blocked-badge">
                API bloqueada neste servidor
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabs: Capturar Ofertas | Meus Anuncios */}
      {status?.connected && (
        <>
          <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'var(--bg)' }}>
            <button onClick={() => setMlTab('deals')} className="flex-1 text-xs py-1.5 rounded-md font-medium transition-colors" style={{ background: mlTab === 'deals' ? 'var(--sv-gold-primary)' : 'transparent', color: mlTab === 'deals' ? '#0A0A0A' : 'var(--muted)' }} data-testid="ml-tab-deals">
              Capturar Ofertas ML
            </button>
            <button onClick={() => setMlTab('my')} className="flex-1 text-xs py-1.5 rounded-md font-medium transition-colors" style={{ background: mlTab === 'my' ? 'var(--sv-gold-primary)' : 'transparent', color: mlTab === 'my' ? '#0A0A0A' : 'var(--muted)' }} data-testid="ml-tab-my">
              Meus Anuncios
            </button>
          </div>

          {/* Capturar Ofertas ML */}
          {mlTab === 'deals' && (
            <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color: 'var(--muted-2)' }}>CAPTURAR OFERTAS DO MERCADO LIVRE</div>
              <div className="flex gap-2 mb-3">
                <input value={dealsQuery} onChange={e => setDealsQuery(e.target.value)} placeholder="Ex: iPhone, notebook, fone..." className="flex-1 text-sm px-3 py-2 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} onKeyDown={e => e.key === 'Enter' && searchDeals()} data-testid="ml-deals-input" />
                <select value={dealsCat} onChange={e => setDealsCat(e.target.value)} className="text-xs px-2 py-2 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted)' }} onFocus={() => !categories.length && loadCategories()} data-testid="ml-deals-cat">
                  <option value="">Categoria</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button onClick={searchDeals} disabled={dealsSearching} className="text-xs px-4 py-2 rounded-lg font-medium" style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }} data-testid="ml-deals-search-btn">
                  {dealsSearching ? <Loader2 size={14} className="animate-spin" /> : <><Search size={14} className="inline mr-1" />Buscar</>}
                </button>
              </div>
              {dealsSource && <div className="text-[10px] mb-2" style={{ color: 'var(--muted)' }}>Fonte: {dealsSource}</div>}
              {dealsResults.length > 0 && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>{dealsResults.length} ofertas — {Object.keys(dealsSelected).length} selecionadas</span>
                  <button onClick={importDeals} disabled={dealsImporting || !Object.keys(dealsSelected).length} className="text-xs px-3 py-1.5 rounded font-medium disabled:opacity-50" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="ml-deals-import-btn">
                    {dealsImporting ? <Loader2 size={12} className="animate-spin" /> : `Publicar (${Object.keys(dealsSelected).length})`}
                  </button>
                </div>
              )}
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {dealsResults.map(item => (
                  <div key={item.ml_id} className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors" style={{ background: dealsSelected[item.ml_id] ? 'var(--gold-bg)' : 'var(--bg)', border: `1px solid ${dealsSelected[item.ml_id] ? 'var(--border-hover)' : 'var(--border)'}` }} onClick={() => toggleDealsSelect(item.ml_id)} data-testid={`ml-deal-${item.ml_id}`}>
                    <input type="checkbox" checked={!!dealsSelected[item.ml_id]} onChange={() => {}} className="flex-shrink-0" />
                    {item.image && <img src={item.image} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{item.title}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-bold" style={{ color: 'var(--sv-gold-primary)' }}>R$ {item.price?.toFixed(2)}</span>
                        {item.discount > 0 && <span className="text-[10px] px-1 py-0.5 rounded" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>-{item.discount}%</span>}
                        {item.free_shipping && <span className="text-[10px] px-1 py-0.5 rounded" style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}>Frete gratis</span>}
                        {item.seller && <span className="text-[10px]" style={{ color: 'var(--muted)' }}>{item.seller}</span>}
                      </div>
                    </div>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <ExternalLink size={14} style={{ color: 'var(--muted)' }} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Meus Anuncios */}
          {mlTab === 'my' && (
            <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color: 'var(--muted-2)' }}>MEUS ANUNCIOS</div>
              <div className="flex gap-2 mb-3">
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Filtrar seus anuncios..." className="flex-1 text-sm px-3 py-2 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} onKeyDown={e => e.key === 'Enter' && searchProducts()} data-testid="ml-search-input" />
                <select value={searchCategory} onChange={e => setSearchCategory(e.target.value)} className="text-xs px-2 py-2 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted)' }} onFocus={() => !categories.length && loadCategories()} data-testid="ml-category-select">
                  <option value="">Categoria</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button onClick={searchProducts} disabled={searching} className="text-xs px-4 py-2 rounded-lg font-medium" style={{ background: 'var(--sv-secondary)', color: 'var(--sv-on-secondary)' }} data-testid="ml-search-btn">
                  {searching ? <Loader2 size={14} className="animate-spin" /> : <><Search size={14} className="inline mr-1" />Buscar</>}
                </button>
                <button onClick={() => { setSearchQuery(''); setSearchCategory(''); searchProducts(); }} disabled={searching} className="text-xs px-3 py-2 rounded-lg font-medium" style={{ background: 'var(--bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid="ml-list-all-btn">
                  Listar Todos
                </button>
              </div>
              {results.length > 0 && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>{results.length} resultados — {Object.keys(selected).length} selecionados</span>
                  <button onClick={importSelected} disabled={importing || !Object.keys(selected).length} className="text-xs px-3 py-1.5 rounded font-medium disabled:opacity-50" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="ml-import-btn">
                    {importing ? <Loader2 size={12} className="animate-spin" /> : `Importar (${Object.keys(selected).length})`}
                  </button>
                </div>
              )}
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {results.map(item => (
                  <div key={item.ml_id} className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors" style={{ background: selected[item.ml_id] ? 'var(--gold-bg)' : 'var(--bg)', border: `1px solid ${selected[item.ml_id] ? 'var(--border-hover)' : 'var(--border)'}` }} onClick={() => toggleSelect(item.ml_id)} data-testid={`ml-item-${item.ml_id}`}>
                    <input type="checkbox" checked={!!selected[item.ml_id]} onChange={() => {}} className="flex-shrink-0" />
                    {item.image && <img src={item.image} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{item.title}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs font-bold" style={{ color: 'var(--sv-gold-primary)' }}>R$ {item.price?.toFixed(2)}</span>
                        {item.discount > 0 && <span className="text-[10px] px-1 py-0.5 rounded" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>-{item.discount}%</span>}
                        {item.free_shipping && <span className="text-[10px] px-1 py-0.5 rounded" style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}>Frete gratis</span>}
                      </div>
                    </div>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <ExternalLink size={14} style={{ color: 'var(--muted)' }} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ====================================================================
// SHOPEE MANAGER (minimal — connect, status, refresh, disconnect)
// ====================================================================

function ShopeeManager({ token, onStatusChange }) {
  // Shopee Afiliados Open API mode (affiliate.shopee.com.br/open_api).
  // DOES NOT use Seller OAuth / partner.shopeemobile.com / shop/auth_partner.
  // Credentials are AppID + Secret — saved via POST /api/shopee/config.
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [collectResult, setCollectResult] = useState(null);
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const loadAll = useCallback(async () => {
    try {
      const cRes = await fetch(`${API}/shopee/config`, { headers });
      if (cRes.ok) {
        const c = await cRes.json();
        setCfg(c);
        setAppId(c.partner_id || '');
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveConfig = async () => {
    if (!appId.trim()) { toast.error('Informe o AppID'); return; }
    if (!cfg?.partner_key_set && !appSecret.trim()) { toast.error('Informe o Secret'); return; }
    setSaving(true);
    try {
      const body = { partner_id: appId.trim() };
      if (appSecret.trim()) body.partner_key = appSecret.trim();
      const res = await fetch(`${API}/shopee/config`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Configuracao Shopee Afiliados salva');
        setAppSecret('');
        loadAll();
        onStatusChange?.();
      } else {
        toast.error('Erro ao salvar configuracao');
      }
    } catch (e) { toast.error('Erro de conexao'); }
    setSaving(false);
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/shopee/affiliate/test`, { method: 'POST', headers });
      const data = await res.json();
      setTestResult(data);
      if (data.ok) toast.success(`Conexao OK — ${data.returned} oferta(s) retornada(s)`);
      else toast.error(data.error || 'Falha no teste');
    } catch (e) { setTestResult({ ok: false, error: 'Erro de rede' }); toast.error('Erro de rede'); }
    setTesting(false);
  };

  const runCollect = async () => {
    setCollecting(true);
    setCollectResult(null);
    try {
      const res = await fetch(`${API}/shopee/affiliate/collect`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 20, pages: 1, list_type: 0 }),
      });
      const data = await res.json();
      setCollectResult(data);
      if (data.ok) toast.success(`${data.collected} oferta(s) coletada(s) (${data.inserted} novas, ${data.updated} atualizadas)`);
      else toast.error((data.errors && data.errors[0]) || 'Falha na coleta');
      onStatusChange?.();
    } catch (e) { setCollectResult({ ok: false, errors: ['Erro de rede'] }); toast.error('Erro de rede'); }
    setCollecting(false);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: '#EE4D2D' }} /></div>;

  const credsOk = cfg?.partner_id && cfg?.partner_key_set;
  const testOk = testResult?.ok || cfg?.last_test_ok;
  const collectOk = (collectResult?.collected > 0) || cfg?.last_collect_ok;

  return (
    <div className="space-y-3" data-testid="shopee-manager">
      <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <ShoppingCart size={16} style={{ color: '#EE4D2D' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Shopee Afiliados Open API</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {credsOk ? (
              <>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold tracking-wider" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }} data-testid="shopee-creds-ok">
                  CREDENCIAIS SALVAS
                </span>
                {collectOk ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold tracking-wider flex items-center gap-1" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }} data-testid="shopee-collect-active">
                    <CheckCircle size={9} /> COLETA ATIVA
                  </span>
                ) : testOk ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1" style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6' }} data-testid="shopee-test-ok">
                    <CheckCircle size={9} /> ULTIMO TESTE OK
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }} data-testid="shopee-collection-inactive">
                    <Clock size={9} /> Aguardando teste
                  </span>
                )}
              </>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold tracking-wider" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }} data-testid="shopee-creds-missing">
                NAO CONFIGURADA
              </span>
            )}
          </div>
        </div>

        <p className="text-xs mb-3" style={{ color: 'var(--muted)' }} data-testid="shopee-help-text">
          Use o <strong style={{ color: 'var(--text)' }}>AppID</strong> e <strong style={{ color: 'var(--text)' }}>Secret</strong> obtidos em <span style={{ color: '#EE4D2D' }}>affiliate.shopee.com.br/open_api</span>.
        </p>

        {credsOk && (
          <div className="rounded-md px-3 py-2 mb-3 flex flex-wrap items-center gap-2" style={{ background: 'rgba(238,77,45,0.06)', border: '1px solid rgba(238,77,45,0.22)' }} data-testid="shopee-actions">
            <button onClick={runTest} disabled={testing} className="text-[11px] px-3 py-1.5 rounded-md font-medium flex items-center gap-1.5" style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }} data-testid="shopee-btn-test">
              {testing ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
              {testing ? 'Testando...' : 'Testar coleta'}
            </button>
            <button onClick={runCollect} disabled={collecting} className="text-[11px] px-3 py-1.5 rounded-md font-semibold flex items-center gap-1.5" style={{ background: '#EE4D2D', color: '#FFF' }} data-testid="shopee-btn-collect">
              {collecting ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
              {collecting ? 'Coletando...' : 'Coletar 20 ofertas agora'}
            </button>
            {testResult && (
              <span className="text-[10px] ml-auto" style={{ color: testResult.ok ? '#10B981' : '#EF4444' }} data-testid="shopee-test-result">
                {testResult.ok
                  ? `OK — "${(testResult.sample_title || '').slice(0, 40)}${(testResult.sample_title || '').length > 40 ? '...' : ''}"`
                  : `Erro: ${(testResult.error || '').slice(0, 80)}`}
              </span>
            )}
            {collectResult && (
              <span className="text-[10px] basis-full" style={{ color: collectResult.collected > 0 ? '#10B981' : '#EF4444' }} data-testid="shopee-collect-result">
                {collectResult.collected > 0
                  ? `Coletadas: ${collectResult.collected} (${collectResult.inserted} novas, ${collectResult.updated} atualizadas)`
                  : `Nada coletado${collectResult.errors?.[0] ? ` — ${collectResult.errors[0].slice(0, 90)}` : ''}`}
              </span>
            )}
          </div>
        )}

        <div className="space-y-2">
          <div>
            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>AppID</label>
            <input value={appId} onChange={e => setAppId(e.target.value)} placeholder="Ex: 18184730009" className="w-full text-sm px-3 py-2 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="shopee-app-id" />
          </div>
          <div>
            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>Secret</label>
            <input value={appSecret} onChange={e => setAppSecret(e.target.value)} type="password" placeholder={cfg?.partner_key_set ? '(ja configurado — preencha para alterar)' : 'Cole o Secret aqui'} className="w-full text-sm px-3 py-2 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="shopee-app-secret" />
          </div>
          <button onClick={saveConfig} disabled={saving} className="text-xs px-4 py-2 rounded-lg font-medium mt-2" style={{ background: '#EE4D2D', color: '#FFF' }} data-testid="shopee-save-config">
            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Salvar configuracao'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ====================================================================
// OFFER AUTOMATION MANAGER — "Cerebro de Ofertas"
// Base central de controle da automacao de ofertas para TODAS as lojas
// conectadas em Marketplaces. Nesta etapa: apenas leitura/escrita da
// configuracao. Sem worker/cron/distribuicao real.
// ====================================================================
const DAYS_PT = [
  { n: 1, label: 'Seg' },
  { n: 2, label: 'Ter' },
  { n: 3, label: 'Qua' },
  { n: 4, label: 'Qui' },
  { n: 5, label: 'Sex' },
  { n: 6, label: 'Sab' },
  { n: 7, label: 'Dom' },
];

function DaysPicker({ value, onChange, testid }) {
  const set = new Set(Array.isArray(value) ? value : []);
  const toggle = (n) => {
    const next = new Set(set);
    if (next.has(n)) next.delete(n); else next.add(n);
    onChange(Array.from(next).sort((a, b) => a - b));
  };
  return (
    <div className="flex gap-1 flex-wrap" data-testid={testid}>
      {DAYS_PT.map(d => {
        const on = set.has(d.n);
        return (
          <button key={d.n} type="button" onClick={() => toggle(d.n)}
            className="text-[10px] px-2 py-1 rounded-md font-medium transition-colors"
            style={{
              background: on ? 'var(--sv-gold-primary)' : 'var(--bg)',
              color: on ? '#0A0A0A' : 'var(--muted)',
              border: '1px solid var(--border)',
            }}
            data-testid={`${testid}-${d.n}`}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

function WindowEditor({ windows, onChange, testid }) {
  const list = Array.isArray(windows) && windows.length ? windows : [{ start: '08:00', end: '22:00' }];
  const update = (i, field, val) => {
    const next = list.map((w, idx) => idx === i ? { ...w, [field]: val } : w);
    onChange(next);
  };
  const add = () => onChange([...list, { start: '08:00', end: '22:00' }]);
  const remove = (i) => onChange(list.length > 1 ? list.filter((_, idx) => idx !== i) : list);
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {list.map((w, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input type="time" value={w.start || '08:00'} onChange={e => update(i, 'start', e.target.value)}
            className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
            data-testid={`${testid}-start-${i}`} />
          <span className="text-[10px]" style={{ color: 'var(--muted)' }}>ate</span>
          <input type="time" value={w.end || '22:00'} onChange={e => update(i, 'end', e.target.value)}
            className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
            data-testid={`${testid}-end-${i}`} />
          {list.length > 1 && (
            <button type="button" onClick={() => remove(i)} className="text-[10px] px-1.5 py-1 rounded" style={{ color: '#EF4444', background: 'rgba(239,68,68,0.1)' }} data-testid={`${testid}-remove-${i}`}>
              <Trash2 size={10} />
            </button>
          )}
        </div>
      ))}
      <button type="button" onClick={add} className="text-[10px] px-2 py-1 rounded" style={{ color: 'var(--sv-gold-primary)', background: 'var(--gold-bg)' }} data-testid={`${testid}-add`}>
        + janela
      </button>
    </div>
  );
}

function Toggle({ value, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className="relative w-9 h-5 rounded-full transition-colors flex-shrink-0"
      style={{ background: value ? 'var(--sv-gold-primary)' : 'var(--surface-3, #333)' }}
      data-testid={testid}>
      <span className="absolute top-0.5 rounded-full w-4 h-4 bg-white transition-transform"
        style={{ left: value ? '20px' : '2px' }} />
    </button>
  );
}

function AutomStat({ label, value, color, testid }) {
  return (
    <div className="rounded-lg px-3 py-1.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted)' }}>{label}</div>
      <div className="text-sm font-bold leading-tight" style={{ color: color || 'var(--text)' }}>{value}</div>
    </div>
  );
}

function AutomStatPill({ label, on, testid }) {
  return (
    <div className="rounded-lg px-3 py-1.5 flex items-center gap-2" style={{ background: 'var(--surface)', border: `1px solid ${on ? 'rgba(16,185,129,0.3)' : 'var(--border)'}` }} data-testid={testid}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: on ? '#10B981' : '#6B7280' }} />
      <div>
        <div className="text-[9px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted)' }}>{label}</div>
        <div className="text-[10px] font-bold leading-tight" style={{ color: on ? '#10B981' : 'var(--muted-2)' }}>{on ? 'ON' : 'OFF'}</div>
      </div>
    </div>
  );
}

function AutomToggleRow({ label, value, onChange, testid, icon: Icon }) {
  return (
    <div className="flex items-center justify-between rounded-lg px-3 py-2 gap-2" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
      <span className="text-xs flex items-center gap-1.5 min-w-0 truncate" style={{ color: 'var(--muted-2)' }}>
        {Icon && <Icon size={12} style={{ color: 'var(--muted)' }} />}
        <span className="truncate">{label}</span>
      </span>
      <Toggle value={value} onChange={onChange} testid={testid} />
    </div>
  );
}

function AutomNumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] block mb-1 font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>{label}</label>
      <input type="number" min={0} value={value ?? 0}
        onChange={e => onChange(parseInt(e.target.value || '0', 10))}
        className="w-full text-sm px-3 py-2 rounded-lg focus:outline-none"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
        data-testid={testid} />
    </div>
  );
}

function OfferAutomationManager({ token }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/offers/automation-settings`, { headers });
      if (res.ok) {
        const d = await res.json();
        setSettings(d);
      } else {
        toast.error('Erro ao carregar configuracao');
      }
    } catch (e) { toast.error('Erro de conexao'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateTop = (patch) => setSettings(prev => ({ ...prev, ...patch }));
  const updateGlobalSchedule = (patch) => setSettings(prev => ({ ...prev, global_schedule: { ...(prev.global_schedule || {}), ...patch } }));
  const updateThrottling = (patch) => setSettings(prev => ({ ...prev, throttling: { ...(prev.throttling || {}), ...patch } }));
  const updateDefaultChannels = (patch) => setSettings(prev => ({ ...prev, default_channels: { ...(prev.default_channels || {}), ...patch } }));

  const updateRule = (provider_key, patch) => {
    setSettings(prev => ({
      ...prev,
      marketplace_rules: (prev.marketplace_rules || []).map(r => r.provider_key === provider_key ? { ...r, ...patch } : r),
    }));
  };
  const updateRuleChannels = (provider_key, patch) => {
    setSettings(prev => ({
      ...prev,
      marketplace_rules: (prev.marketplace_rules || []).map(r => r.provider_key === provider_key ? { ...r, channels: { ...(r.channels || {}), ...patch } } : r),
    }));
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/offers/automation-settings`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.settings) setSettings(d.settings);
        setLastSavedAt(Date.now());
        toast.success('Configuracao salva');
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Erro ao salvar');
      }
    } catch (e) { toast.error('Erro de conexao'); }
    setSaving(false);
  };

  if (loading || !settings) {
    return (
      <div className="flex justify-center py-10" data-testid="automation-loading">
        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} />
      </div>
    );
  }

  const rules = Array.isArray(settings.marketplace_rules) ? settings.marketplace_rules : [];
  const totalStores = rules.length;
  const connectedCount = rules.filter(r => r.connected).length;
  const enabledCount = rules.filter(r => r.enabled).length;

  const visibleRules = rules.filter(r => {
    const name = (r.provider_name || r.provider_key || '').toLowerCase();
    if (query && !name.includes(query.toLowerCase())) return false;
    if (filter === 'connected' && !r.connected) return false;
    if (filter === 'enabled' && !r.enabled) return false;
    return true;
  }).sort((a, b) => {
    if (a.connected !== b.connected) return a.connected ? -1 : 1;
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    return (a.provider_name || '').localeCompare(b.provider_name || '');
  });

  return (
    <div className="space-y-5" data-testid="offer-automation-panel">
      {/* HERO */}
      <div className="rounded-xl p-5" style={{ background: 'linear-gradient(135deg, var(--surface) 0%, var(--bg) 100%)', border: '1px solid var(--border)' }} data-testid="automation-hero">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg" style={{ background: 'var(--gold-bg)', border: '1px solid var(--border-hover)' }}>
              <Clock size={18} style={{ color: 'var(--sv-gold-primary)' }} />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Cerebro de Ofertas</h3>
              <p className="text-xs mt-0.5 max-w-xl" style={{ color: 'var(--muted)' }}>
                Regras operacionais por loja conectada em Marketplaces: agenda, limites, prioridade, canais e janelas horarias.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <AutomStat label="Lojas" value={totalStores} testid="automation-stat-total" />
            <AutomStat label="Conectadas" value={connectedCount} color="#10B981" testid="automation-stat-connected" />
            <AutomStat label="Habilitadas" value={enabledCount} color="var(--sv-gold-primary)" testid="automation-stat-enabled" />
            <AutomStatPill label="Global" on={!!settings.global_enabled} testid="automation-stat-global" />
            <AutomStatPill label="Coleta" on={!!settings.collection_enabled} testid="automation-stat-collection" />
            <AutomStatPill label="Distribuicao" on={!!settings.distribution_enabled} testid="automation-stat-distribution" />
          </div>
        </div>
      </div>

      {/* BLOCO GLOBAL */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="automation-global">
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
          <div className="flex items-center gap-2">
            <Settings size={14} style={{ color: 'var(--sv-gold-primary)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Configuracao global</span>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold tracking-wider" style={{
            background: settings.global_enabled ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
            color: settings.global_enabled ? '#10B981' : '#EF4444',
          }} data-testid="automation-global-badge">
            {settings.global_enabled ? 'HABILITADA' : 'DESABILITADA'}
          </span>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] block mb-1 font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Timezone</label>
              <input value={settings.timezone || ''} onChange={e => updateTop({ timezone: e.target.value })}
                className="w-full text-sm px-3 py-2 rounded-lg focus:outline-none" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                data-testid="automation-timezone" />
            </div>
            <AutomToggleRow label="Automacao global" value={!!settings.global_enabled} onChange={v => updateTop({ global_enabled: v })} testid="automation-toggle-global" />
            <AutomToggleRow label="Coleta habilitada" value={!!settings.collection_enabled} onChange={v => updateTop({ collection_enabled: v })} testid="automation-toggle-collection" />
            <AutomToggleRow label="Distribuicao habilitada" value={!!settings.distribution_enabled} onChange={v => updateTop({ distribution_enabled: v })} testid="automation-toggle-distribution" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
            <div>
              <label className="text-[10px] block mb-2 font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Dias ativos globais</label>
              <DaysPicker value={settings.global_schedule?.active_days} onChange={v => updateGlobalSchedule({ active_days: v })} testid="automation-global-days" />
            </div>
            <div>
              <label className="text-[10px] block mb-2 font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Janela horaria global</label>
              <WindowEditor windows={settings.global_schedule?.time_windows} onChange={v => updateGlobalSchedule({ time_windows: v })} testid="automation-global-windows" />
            </div>
          </div>

          <div className="pt-4" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="text-[10px] font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--muted)' }}>Throttling global</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <AutomNumField label="Min. minutos entre ofertas" value={settings.throttling?.min_minutes_between_offers ?? 10} onChange={v => updateThrottling({ min_minutes_between_offers: v })} testid="automation-min-interval" />
              <AutomNumField label="Max. ofertas por janela" value={settings.throttling?.max_offers_per_window ?? 20} onChange={v => updateThrottling({ max_offers_per_window: v })} testid="automation-max-window" />
              <AutomNumField label="Max. ofertas por dia" value={settings.throttling?.max_offers_per_day ?? 100} onChange={v => updateThrottling({ max_offers_per_day: v })} testid="automation-max-day" />
            </div>
          </div>

          <div className="pt-4" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="text-[10px] font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--muted)' }}>Canais globais padrao</div>
            <div className="grid grid-cols-2 gap-3">
              <AutomToggleRow label="Telegram" value={!!settings.default_channels?.telegram} onChange={v => updateDefaultChannels({ telegram: v })} testid="automation-channel-telegram" icon={Send} />
              <AutomToggleRow label="Site" value={!!settings.default_channels?.site} onChange={v => updateDefaultChannels({ site: v })} testid="automation-channel-site" icon={Globe} />
            </div>
          </div>
        </div>
      </div>

      {/* POR LOJA */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="automation-rules">
        <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
          <div className="flex items-center gap-2">
            <Store size={14} style={{ color: 'var(--sv-gold-primary)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Controle por loja</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--surface)', color: 'var(--muted)' }}>{visibleRules.length}/{totalStores}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 rounded-lg px-2 py-1" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <Search size={12} style={{ color: 'var(--muted)' }} />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar loja..." className="text-xs bg-transparent border-none outline-none" style={{ color: 'var(--text)', width: 140 }} data-testid="automation-search" />
            </div>
            <div className="flex gap-0.5 rounded-lg p-0.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              {[{k:'all',l:'Todas'},{k:'connected',l:'Conectadas'},{k:'enabled',l:'Habilitadas'}].map(f => (
                <button key={f.k} onClick={() => setFilter(f.k)} className="text-[10px] px-2 py-1 rounded-md font-medium transition-colors" style={{ background: filter === f.k ? 'var(--sv-gold-primary)' : 'transparent', color: filter === f.k ? '#0A0A0A' : 'var(--muted)' }} data-testid={`automation-filter-${f.k}`}>
                  {f.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          {visibleRules.length === 0 && (
            <p className="text-xs text-center py-6" style={{ color: 'var(--muted)' }}>Nenhuma loja corresponde ao filtro.</p>
          )}
          {visibleRules.map(r => {
            const isOpen = !!expanded[r.provider_key];
            const meta = PROVIDER_META[r.provider_key] || { color: '#888', initial: (r.provider_name || '?')[0] };
            return (
              <div key={r.provider_key} data-testid={`automation-rule-${r.provider_key}`} style={{ borderTop: '1px solid var(--border)' }}>
                <div className="flex items-center gap-3 px-5 py-3 hover:bg-[rgba(128,128,128,0.04)] transition-colors">
                  <button onClick={() => setExpanded(prev => ({ ...prev, [r.provider_key]: !isOpen }))} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`automation-rule-toggle-${r.provider_key}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: `${meta.color}15`, color: meta.color, border: `1px solid ${meta.color}30` }}>
                      {meta.initial || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{r.provider_name || r.provider_key}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider" style={{
                          background: r.connected ? 'rgba(16,185,129,0.12)' : 'rgba(107,114,128,0.15)',
                          color: r.connected ? '#10B981' : '#9CA3AF',
                        }} data-testid={`automation-rule-connected-${r.provider_key}`}>
                          {r.connected ? 'CONECTADA' : 'NAO CONECTADA'}
                        </span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider" style={{
                          background: r.enabled ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                          color: r.enabled ? '#10B981' : '#EF4444',
                        }} data-testid={`automation-rule-status-${r.provider_key}`}>
                          {r.enabled ? 'HABILITADA' : 'DESABILITADA'}
                        </span>
                      </div>
                      <div className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--muted)' }}>
                        Prioridade {r.priority ?? 100} · {r.max_offers_per_day ?? 40}/dia · {r.max_offers_per_window ?? 10}/janela · min {r.min_minutes_between_offers ?? 10}min
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <Toggle value={!!r.enabled} onChange={v => updateRule(r.provider_key, { enabled: v })} testid={`automation-rule-enabled-${r.provider_key}`} />
                    {isOpen ? <ChevronDown size={16} style={{ color: 'var(--muted)' }} /> : <ChevronRight size={16} style={{ color: 'var(--muted)' }} />}
                  </div>
                </div>

                {isOpen && (
                  <div className="px-5 pb-5 pt-1 space-y-4" style={{ background: 'var(--bg)', borderTop: '1px dashed var(--border)' }}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3">
                      <AutomToggleRow label="Coleta" value={!!r.collection_enabled} onChange={v => updateRule(r.provider_key, { collection_enabled: v })} testid={`automation-rule-collection-${r.provider_key}`} />
                      <AutomToggleRow label="Distribuicao" value={!!r.distribution_enabled} onChange={v => updateRule(r.provider_key, { distribution_enabled: v })} testid={`automation-rule-distribution-${r.provider_key}`} />
                      <AutomToggleRow label="Telegram" value={!!r.channels?.telegram} onChange={v => updateRuleChannels(r.provider_key, { telegram: v })} testid={`automation-rule-ch-tg-${r.provider_key}`} icon={Send} />
                      <AutomToggleRow label="Site" value={!!r.channels?.site} onChange={v => updateRuleChannels(r.provider_key, { site: v })} testid={`automation-rule-ch-site-${r.provider_key}`} icon={Globe} />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <AutomNumField label="Prioridade" value={r.priority ?? 100} onChange={v => updateRule(r.provider_key, { priority: v })} testid={`automation-rule-priority-${r.provider_key}`} />
                      <AutomNumField label="Min. minutos" value={r.min_minutes_between_offers ?? 10} onChange={v => updateRule(r.provider_key, { min_minutes_between_offers: v })} testid={`automation-rule-min-${r.provider_key}`} />
                      <AutomNumField label="Max. por janela" value={r.max_offers_per_window ?? 10} onChange={v => updateRule(r.provider_key, { max_offers_per_window: v })} testid={`automation-rule-win-${r.provider_key}`} />
                      <AutomNumField label="Max. por dia" value={r.max_offers_per_day ?? 40} onChange={v => updateRule(r.provider_key, { max_offers_per_day: v })} testid={`automation-rule-day-${r.provider_key}`} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="text-[10px] block mb-2 font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Dias ativos</label>
                        <DaysPicker value={r.active_days} onChange={v => updateRule(r.provider_key, { active_days: v })} testid={`automation-rule-days-${r.provider_key}`} />
                      </div>
                      <div>
                        <label className="text-[10px] block mb-2 font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>Janela horaria</label>
                        <WindowEditor windows={r.time_windows} onChange={v => updateRule(r.provider_key, { time_windows: v })} testid={`automation-rule-windows-${r.provider_key}`} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* SAVE BAR */}
      <div className="sticky bottom-3 flex items-center justify-end gap-3 px-4 py-2.5 rounded-xl z-10" style={{ background: 'var(--surface)', border: '1px solid var(--border-hover)', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
        {lastSavedAt && (
          <span className="text-[10px]" style={{ color: 'var(--muted)' }} data-testid="automation-last-saved">
            Ultimo save: {new Date(lastSavedAt).toLocaleTimeString('pt-BR')}
          </span>
        )}
        <button onClick={save} disabled={saving} className="text-xs px-5 py-2 rounded-lg font-bold flex items-center gap-2 transition-all hover:scale-[1.02] disabled:opacity-60"
          style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="automation-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar configuracao
        </button>
      </div>
    </div>
  );
}


function AdminMasterPageInner() {
  const [tab, setTab] = useState('integrations');
  const [integrations, setIntegrations] = useState(null);
  const [groups, setGroups] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [refreshingKw, setRefreshingKw] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [appConfig, setAppConfig] = useState({});
  const [savingApp, setSavingApp] = useState(false);
  const [socialLogin, setSocialLogin] = useState(null);
  const [savingSocial, setSavingSocial] = useState(false);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const loadIntegrations = useCallback(async () => {
    if (!token) { setGroups({}); return; }
    try {
      const res = await fetch(`${API}/admin/master/integrations`, { headers });
      if (res.status === 403) { setAuthError(true); return; }
      if (res.ok) {
        const data = await res.json();
        setIntegrations(data.integrations || {});
        const g = data.groups && typeof data.groups === 'object' ? data.groups : {};
        setGroups(g);
        const expanded = {};
        Object.keys(g).forEach(k => expanded[k] = true);
        setExpandedGroups(prev => Object.keys(prev).length ? prev : expanded);
      } else {
        setGroups({});
      }
    } catch(e) { console.error('[AdminMaster] loadIntegrations error:', e); setGroups({}); }
  }, [token]);

  const loadAudit = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/audit-log`, { headers });
      if (res.ok) { const data = await res.json(); setAuditLogs(Array.isArray(data.logs) ? data.logs : []); }
    } catch(e) { console.error('[AdminMaster] loadAudit error:', e); }
  }, [token]);

  const loadKeywords = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/keywords`, { headers });
      if (res.ok) { const data = await res.json(); setKeywords(Array.isArray(data.keywords) ? data.keywords : []); }
    } catch(e) { console.error('[AdminMaster] loadKeywords error:', e); }
  }, [token]);

  const loadFunnel = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/master/funnel-overview`, { headers });
      if (res.ok) { const data = await res.json(); setFunnelData(Array.isArray(data.deals) ? data.deals : []); }
    } catch(e) { console.error('[AdminMaster] loadFunnel error:', e); }
  }, [token]);

  const loadAppConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/app-download/config`);
      if (res.ok) { const data = await res.json(); setAppConfig(data && typeof data === 'object' ? data : {}); }
    } catch(e) { console.error('[AdminMaster] loadAppConfig error:', e); }
  }, []);

  const loadSocialLogin = useCallback(async () => {
    try {
      const res = await fetch(`${API}/settings/social-login/admin`, { headers });
      if (res.ok) { const data = await res.json(); setSocialLogin(data.providers || data || {}); }
    } catch(e) { console.error('[AdminMaster] loadSocialLogin error:', e); }
  }, [token]);

  const saveSocialLogin = async (providers) => {
    setSavingSocial(true);
    try {
      const res = await fetch(`${API}/settings/social-login`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ providers }),
      });
      if (res.ok) { toast.success('Login Social atualizado'); loadSocialLogin(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d?.detail || 'Erro ao salvar'); }
    } catch(e) { toast.error('Erro de conexao'); }
    setSavingSocial(false);
  };

  const toggleSocialProvider = (provider, field, value) => {
    if (!socialLogin) return;
    const updated = { ...socialLogin, [provider]: { ...(socialLogin[provider] || {}), [field]: value } };
    setSocialLogin(updated);
    saveSocialLogin(updated);
  };

  useEffect(() => {
    console.log('[AdminMaster] mount — token:', !!token);
    setLoading(true);
    Promise.all([loadIntegrations(), loadAudit(), loadKeywords(), loadFunnel(), loadAppConfig(), loadSocialLogin()])
      .catch(err => console.error('[AdminMaster] init error:', err))
      .finally(() => { console.log('[AdminMaster] loaded'); setLoading(false); });
  }, []);

  const refreshKeywords = async () => {
    setRefreshingKw(true);
    try {
      const res = await fetch(`${API}/admin/master/keywords/refresh`, { method: 'POST', headers });
      if (res.ok) {
        const data = await res.json();
        setKeywords(Array.isArray(data.keywords) ? data.keywords : []);        toast.success(`${data.count} keywords atualizadas`);
      }
    } catch(e) { toast.error('Erro'); }
    setRefreshingKw(false);
  };

  const toggleGroup = (g) => setExpandedGroups(prev => ({ ...prev, [g]: !prev[g] }));

  const TABS = [
    { key: 'integrations', label: 'Integracoes', icon: Key },
    { key: 'telegram', label: 'Telegram+', icon: Send },
    { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { key: 'external', label: 'Pub. Externa', icon: Globe },
    { key: 'social', label: 'Login Social', icon: Shield },
    { key: 'reports', label: 'Relatorios', icon: FileText },
    { key: 'funnel', label: 'Funil', icon: TrendingUp },
    { key: 'keywords', label: 'Keywords', icon: Zap },
    { key: 'app', label: 'App', icon: Download },
    { key: 'banners', label: 'Banners', icon: Image },
    { key: 'popup', label: 'Pop-up', icon: Radio },
    { key: 'features', label: 'Features', icon: Sparkles },
    { key: 'recovery', label: 'Recuperacao', icon: Download },
    { key: 'audit', label: 'Auditoria', icon: FileText },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="master-loading">
        <Loader2 size={24} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="master-forbidden">
        <div className="text-center">
          <Shield size={32} style={{ color: 'var(--status-red)', margin: '0 auto 8px' }} />
          <p className="text-sm" style={{ color: 'var(--muted-2)' }}>Acesso restrito ao Admin Master</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-master-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg" style={{ background: 'var(--gold-bg)', border: '1px solid var(--sv-gold-primary)' }}>
          <Shield size={20} style={{ color: 'var(--sv-gold-primary)' }} />
        </div>
        <div>
          <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Admin Master</h2>
          <p className="text-xs" style={{ color: 'var(--muted)' }}>Gestao de integracoes, funil e inteligencia do sistema</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-master-tabs" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => t.external ? window.open(t.external, '_blank', 'noopener,noreferrer') : setTab(t.key)}
            className={`admin-master-tab ${tab === t.key ? 'active' : ''}`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <MiniTour tabKey={tab} />
      {tab === 'integrations' && !groups && !authError && (
        <div className="p-6 rounded-xl text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="integrations-loading">
          <Loader2 size={18} className="animate-spin mx-auto mb-2" style={{ color: 'var(--sv-gold-primary)' }} />
          <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Carregando integrações...</p>
        </div>
      )}
      {tab === 'integrations' && !groups && authError && (
        <div className="p-6 rounded-xl text-center" style={{ background: 'var(--status-error-bg)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="integrations-auth-error">
          <AlertTriangle size={20} style={{ color: 'var(--status-red)', margin: '0 auto 8px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--status-red)' }}>Acesso restrito ao Admin Master</p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Apenas emails autorizados podem gerenciar integrações.</p>
        </div>
      )}
      {tab === 'integrations' && groups && Object.keys(groups).length === 0 && !authError && (
        <div className="p-6 rounded-xl text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="integrations-empty">
          <Key size={20} style={{ color: 'var(--muted)', margin: '0 auto 8px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--muted-2)' }}>Nenhuma integração configurada</p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Configure chaves de API nas variáveis de ambiente do servidor.</p>
          <button onClick={() => { setGroups(null); loadIntegrations(); }} className="mt-3 px-3 py-1.5 rounded text-xs" style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid="integrations-retry">
            <RefreshCw size={12} style={{ display: 'inline', marginRight: 4 }} />Tentar novamente
          </button>
        </div>
      )}
      {tab === 'integrations' && groups && Object.keys(groups).length > 0 && (
        <div className="space-y-3" data-testid="integrations-panel">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} style={{ color: 'var(--status-amber)' }} />
            <span className="text-xs" style={{ color: 'var(--muted-2)' }}>Valores mascarados por seguranca. Clique na chave para editar.</span>
          </div>
          {Object.entries(groups).map(([groupName, items]) => {
            const safeItems = Array.isArray(items) ? items : [];
            return (
            <div key={groupName} className="rounded-lg overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <button
                onClick={() => toggleGroup(groupName)}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-[rgba(128,128,128,0.08)] transition-colors"
                data-testid={`group-${groupName}`}
              >
                <span className="w-7 h-7 rounded flex items-center justify-center text-xs font-bold" style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }}>
                  {GROUP_ICONS[groupName] || '?'}
                </span>
                <span className="flex-1 text-sm font-medium" style={{ color: 'var(--text)' }}>{groupName}</span>
                <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--bg)', color: 'var(--muted-2)' }}>
                  {safeItems.filter(i => i.has_value).length}/{safeItems.length}
                </span>
                {expandedGroups[groupName] ? <ChevronDown size={14} style={{ color: 'var(--muted)' }} /> : <ChevronRight size={14} style={{ color: 'var(--muted)' }} />}
              </button>
              {expandedGroups[groupName] && (
                <div className="px-3 pb-3 space-y-1.5">
                  {safeItems.map(item => (
                    <MaskedField
                      key={item.key}
                      config={item}
                      configKey={item.key}
                      token={token}
                      onSaved={loadIntegrations}
                    />
                  ))}
                  {groupName.includes('Belvo') && (
                    <BelvoTestButton token={token} />
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {tab === 'telegram' && (
        <TelegramAdvancedManager token={token} />
      )}

      {tab === 'whatsapp' && (
        <WhatsAppManager token={token} />
      )}

      {tab === 'external' && (
        <ExternalPublishManager token={token} />
      )}

      {tab === 'social' && socialLogin && (
        <div className="space-y-4" data-testid="social-login-panel">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={14} style={{ color: 'var(--sv-gold-primary)' }} />
            <span className="text-xs" style={{ color: 'var(--muted-2)' }}>Ative ou desative provedores de login social na pagina de login.</span>
          </div>
          {[
            { key: 'google', label: 'Google', color: '#4285F4', desc: 'Login com conta Google (gerenciado via Emergent)' },
            { key: 'facebook', label: 'Facebook', color: '#1877F2', desc: 'Login com conta Facebook (requer App ID e Secret)' },
            { key: 'apple', label: 'Apple', color: '#A2AAAD', desc: 'Login com Apple ID (requer Service ID, Key ID e Team ID)' },
          ].map(p => {
            const cfg = socialLogin[p.key] || {};
            return (
              <div key={p.key} className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`social-${p.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: p.color + '20', color: p.color, border: `1px solid ${p.color}40` }}>
                      {p.label[0]}
                    </span>
                    <div>
                      <div className="text-sm font-medium" style={{ color: 'var(--text)' }}>{p.label}</div>
                      <div className="text-[10px]" style={{ color: 'var(--muted)' }}>{p.desc}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{
                      background: cfg.configured ? 'var(--status-success-bg)' : 'var(--status-error-bg)',
                      color: cfg.configured ? '#10B981' : '#EF4444',
                    }}>
                      {cfg.configured ? 'Configurado' : 'Nao configurado'}
                    </span>
                    <button
                      onClick={() => toggleSocialProvider(p.key, 'enabled', !cfg.enabled)}
                      disabled={savingSocial}
                      className="relative w-10 h-5 rounded-full transition-colors"
                      style={{ background: cfg.enabled ? 'var(--sv-gold-primary)' : 'var(--surface-3)' }}
                      data-testid={`toggle-social-${p.key}`}
                    >
                      <span className="absolute top-0.5 rounded-full w-4 h-4 bg-white transition-transform"
                        style={{ left: cfg.enabled ? '22px' : '2px' }} />
                    </button>
                  </div>
                </div>
                {cfg.enabled && p.key === 'facebook' && (
                  <div className="mt-3 pt-3 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
                    <div>
                      <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>App ID</label>
                      <input type="text" value={cfg.app_id || ''} onChange={e => setSocialLogin(prev => ({ ...prev, facebook: { ...prev.facebook, app_id: e.target.value } }))}
                        placeholder="Facebook App ID" className="w-full text-xs py-1.5 px-2 rounded"
                        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="social-fb-appid" />
                    </div>
                    <div>
                      <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>App Secret</label>
                      <input type="password" value={cfg.app_secret || ''} onChange={e => setSocialLogin(prev => ({ ...prev, facebook: { ...prev.facebook, app_secret: e.target.value } }))}
                        placeholder="Facebook App Secret" className="w-full text-xs py-1.5 px-2 rounded"
                        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="social-fb-secret" />
                    </div>
                    <button onClick={() => saveSocialLogin(socialLogin)} disabled={savingSocial}
                      className="px-4 py-1.5 rounded text-xs font-bold flex items-center gap-2"
                      style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="social-fb-save">
                      {savingSocial ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar Facebook
                    </button>
                  </div>
                )}
                {cfg.enabled && p.key === 'apple' && (
                  <div className="mt-3 pt-3 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>Service ID</label>
                        <input type="text" value={cfg.service_id || ''} onChange={e => setSocialLogin(prev => ({ ...prev, apple: { ...prev.apple, service_id: e.target.value } }))}
                          className="w-full text-xs py-1.5 px-2 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="social-apple-sid" />
                      </div>
                      <div>
                        <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>Key ID</label>
                        <input type="text" value={cfg.key_id || ''} onChange={e => setSocialLogin(prev => ({ ...prev, apple: { ...prev.apple, key_id: e.target.value } }))}
                          className="w-full text-xs py-1.5 px-2 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="social-apple-kid" />
                      </div>
                      <div>
                        <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>Team ID</label>
                        <input type="text" value={cfg.team_id || ''} onChange={e => setSocialLogin(prev => ({ ...prev, apple: { ...prev.apple, team_id: e.target.value } }))}
                          className="w-full text-xs py-1.5 px-2 rounded" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="social-apple-tid" />
                      </div>
                    </div>
                    <button onClick={() => saveSocialLogin(socialLogin)} disabled={savingSocial}
                      className="px-4 py-1.5 rounded text-xs font-bold flex items-center gap-2"
                      style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="social-apple-save">
                      {savingSocial ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar Apple
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'reports' && (
        <WeeklyReportManager token={token} />
      )}

      {tab === 'funnel' && (
        <div className="space-y-4" data-testid="funnel-panel">
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Top 20 deals por cliques (ultimos 30 dias)</p>
            <button onClick={loadFunnel} className="text-xs flex items-center gap-1 px-2 py-1 rounded" style={{ color: 'var(--sv-gold-primary)', background: 'var(--gold-bg)' }}>
              <RefreshCw size={12} /> Atualizar
            </button>
          </div>
          {funnelData.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--muted)' }}>
              <TrendingUp size={32} className="mx-auto mb-2" />
              <p className="text-sm">Sem dados de funil ainda</p>
            </div>
          ) : (
            <div className="rounded-lg overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="grid grid-cols-6 gap-2 px-3 py-2 text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
                <div className="col-span-2">Deal</div>
                <div className="text-right">Views</div>
                <div className="text-right">Cliques</div>
                <div className="text-right">Redirects</div>
                <div className="text-right">Conv.</div>
              </div>
              {funnelData.map((d, i) => (
                <div key={d.deal_id} className="grid grid-cols-6 gap-2 px-3 py-2 hover:bg-[rgba(128,128,128,0.08)] transition-colors" style={{ borderBottom: '1px solid var(--border)' }} data-testid={`funnel-row-${i}`}>
                  <div className="col-span-2 truncate">
                    <div className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{d.title}</div>
                    <div className="text-[10px]" style={{ color: 'var(--muted)' }}>{d.store_name || '-'}</div>
                  </div>
                  <div className="text-right text-xs" style={{ color: 'var(--muted-2)' }}>{d.views}</div>
                  <div className="text-right text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>{d.clicks}</div>
                  <div className="text-right text-xs" style={{ color: 'var(--muted-2)' }}>{d.redirect_completes}</div>
                  <div className="text-right text-xs font-medium" style={{ color: d.conversion_rate > 10 ? '#10B981' : 'var(--muted-2)' }}>{d.conversion_rate}%</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'keywords' && (
        <div className="space-y-4" data-testid="keywords-panel">
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Keywords automaticas detectadas + manuais ({keywords.length})</p>
            <button onClick={refreshKeywords} disabled={refreshingKw} className="text-xs flex items-center gap-1 px-2 py-1 rounded" style={{ color: 'var(--sv-gold-primary)', background: 'var(--gold-bg)' }} data-testid="refresh-keywords-btn">
              {refreshingKw ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Recalcular
            </button>
          </div>
          <div className="rounded-lg p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            {keywords.length === 0 ? (
              <p className="text-sm text-center py-4" style={{ color: 'var(--muted)' }}>Nenhuma keyword ainda. Clique em "Recalcular" para detectar automaticamente.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {keywords.map((kw, i) => (
                  <span key={i} className="px-2 py-1 rounded text-xs font-medium" style={{ background: 'var(--gold-bg)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border-hover)' }} data-testid={`keyword-${i}`}>
                    {kw}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'app' && (
        <AppDownloadManager appConfig={appConfig} token={token} onSaved={loadAppConfig} savingApp={savingApp} setSavingApp={setSavingApp} />
      )}

      {tab === 'features' && (
        <FeaturesManager token={token} />
      )}

      {tab === 'recovery' && (
        <RecoveryManager token={token} />
      )}

      {tab === 'banners' && (
        <BannersManager token={token} />
      )}

      {tab === 'popup' && (
        <PromoPopupManager token={token} />
      )}

      {tab === 'audit' && (
        <div className="space-y-3" data-testid="audit-panel">
          <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Historico de alteracoes em configuracoes</p>
          {auditLogs.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--muted)' }}>
              <FileText size={32} className="mx-auto mb-2" />
              <p className="text-sm">Nenhuma alteracao registrada</p>
            </div>
          ) : (
            <div className="space-y-2">
              {auditLogs.map((log, i) => (
                <div key={log.audit_id || i} className="rounded-lg p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`audit-row-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium" style={{ color: 'var(--sv-gold-primary)' }}>{log.label}</span>
                    <span className="text-[10px]" style={{ color: 'var(--muted)' }}>
                      {log.changed_at ? new Date(log.changed_at).toLocaleString('pt-BR') : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px]" style={{ color: 'var(--muted-2)' }}>
                    <span>Grupo: {log.group}</span>
                    <span>|</span>
                    <span>Por: {log.changed_by}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[10px]">
                    <code style={{ color: 'var(--status-red)' }}>{log.old_masked || '(vazio)'}</code>
                    <span style={{ color: 'var(--muted)' }}>→</span>
                    <code style={{ color: 'var(--status-green)' }}>{log.new_masked || '(vazio)'}</code>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminMasterPage() {
  return (
    <MasterErrorBoundary>
      <AdminMasterPageInner />
    </MasterErrorBoundary>
  );
}

export { MarketplacesHub, OfferAutomationManager };
